import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Link as LinkIcon,
  Lock as LockIcon,
  Download as DownloadIcon,
  FactCheck as GradeIcon,
  HelpOutline as HelpOutlineIcon,
  OpenInNew as OpenInNewIcon,
  Psychology as PsychologyIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  CalibrationPlot,
  CoverageBar,
  CoverageLegend,
  DeliveryQuadrant,
  GapBarChart,
  LensHeatmap,
  LensRankBar,
  PipelineBar,
  ProgressFunnel,
  RubricRadar,
} from '../components/cohort/analytics/CohortCharts';
import { ChartFrame } from '../components/cohort/analytics/ChartFrame';
import { ActionQueue, ReadThis, StatTile } from '../components/cohort/analytics/DecisionPanels';
import {
  biggestFunnelDrop,
  buildActionQueue,
  buildLearnerMetrics,
  buildLensMatrix,
  buildProgressFunnel,
  buildRubricAxes,
  cohortReference,
  deliveryQuadrant,
  learnerName,
  median,
  weakestLenses,
} from '../components/cohort/analytics/cohortAnalytics';
import {
  fetchAllCohortKeys,
  fetchCohortMembers,
  fetchCohortWeek,
  fetchCohortWeeks,
  fetchHackathonAttempts,
  fetchMockInterviews,
  fetchMyCohort,
  generateCohortWeek,
  gradeCohortRole,
  notifyCohortWeekRoles,
  saveCohortAvailability,
  saveCohortScheduleAvailability,
  saveCohortScheduleCoordinator,
  saveCohortWeek,
  selectCohortAvailabilitySlot,
  selectCohortScheduleSlot,
  startHackathonAttempt,
  submitHackathonAttempt,
  updateCohortRole,
} from '../api/cohortApi';
import { recordAuditEvent } from '../api/auditApi';
import { fetchLearnerSignals } from '../api/adminApi';
import { fetchJournal, pushJournalToGitHub, saveJournal, uploadArtifact } from '../api/repoSyncApi';
import { readJournalSection, upsertJournalSection } from '../utils/journalSections';
import { analyzeEvidence } from '../api/evidenceApi';
import { createClinicRecord } from '../api/clinicApi';
import { loadRemoteProgress, saveRemoteProgress } from '../api/progressApi';
import EvidenceIntelligenceDialog from '../components/evidence/EvidenceIntelligenceDialog';
import { formatPracticeJournalEntry } from '../engines/practice/utils/lessonTaskType';
import PracticeEntry from '../components/practice/PracticeEntry';
import PracticeGradeResult from '../engines/practice/PracticeGradeResult';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import GlossaryTerm from '../components/common/GlossaryTerm';
import CopyValueButton from '../components/common/CopyValueButton';
import SlideViewer, { SlideCard } from '../components/common/SlideViewer';
import ShowTellBoard from '../components/common/ShowTellBoard';
import MockInterviewBoard from '../components/common/MockInterviewBoard';
import SqlWindowFlashcards from '../components/cohort/flashcards/SqlWindowFlashcards';
import { DUPLICATE_ANSWER_GUIDE } from '../components/common/DuplicatePracticeBank';
import { MISSINGNESS_ANSWER_GUIDE } from '../components/common/MissingnessPracticeBank';
import { OUTLIER_ANSWER_GUIDE } from '../components/common/OutlierPracticeBank';
import { DQ_MATH_05_TRACKER_ID, DQ_MATH_05_LENSES, DQ_MATH_05_ITEMS } from '../data/practiceBankConfigs/dqMath05InvalidValues';
import { CT08_TABLE_DETECTIVE_TRACKER_ID, CT08_TABLE_DETECTIVE_LENSES, CT08_TABLE_DETECTIVE_ITEMS } from '../data/practiceBankConfigs/ct08TableDetective';
import { SQL02_DATA_MODELLING_TRACKER_ID, SQL02_DATA_MODELLING_LENSES, SQL02_DATA_MODELLING_ITEMS } from '../data/practiceBankConfigs/sql02DataModelling';
import { SQL03_DATABASE_FUNDAMENTALS_TRACKER_ID, SQL03_DATABASE_FUNDAMENTALS_LENSES, SQL03_DATABASE_FUNDAMENTALS_ITEMS } from '../data/practiceBankConfigs/databaseFundamentals';
import { SQL04_OLTP_OLAP_TRACKER_ID, SQL04_OLTP_OLAP_LENSES, SQL04_OLTP_OLAP_ITEMS } from '../data/practiceBankConfigs/oltpOlap';
import { CT10_REQUEST_TO_SPEC_TRACKER_ID, CT10_REQUEST_TO_SPEC_LENSES, CT10_REQUEST_TO_SPEC_ITEMS } from '../data/practiceBankConfigs/ct10RequestToSpec';
import { DM_TECHNIQUE_LENSES, DM05_CONFIG, DM08_CONFIG } from '../data/practiceBankConfigs/dataModellingTechniques';
import { STATS_CLINIC_01A_TRACKER_ID, STATS_CLINIC_01A_LENSES, STATS_CLINIC_01A_ITEMS } from '../data/practiceBankConfigs/statisticsClinic01aCentreSpread';
import { STATS_CLINIC_01B_TRACKER_ID, STATS_CLINIC_01B_LENSES, STATS_CLINIC_01B_ITEMS } from '../data/practiceBankConfigs/statisticsClinic01bPositionRelationship';
import { STATS_CLINIC_03_TRACKER_ID, STATS_CLINIC_03_LENSES, STATS_CLINIC_03_ITEMS } from '../data/practiceBankConfigs/statisticsClinic03OutliersSamplingBias';
import { STATS_CLINIC_03A_TRACKER_ID, STATS_CLINIC_03A_LENSES, STATS_CLINIC_03A_ITEMS } from '../data/practiceBankConfigs/statisticsClinic03aPosition';
import { STATS_CLINIC_04_TRACKER_ID, STATS_CLINIC_04_LENSES, STATS_CLINIC_04_ITEMS } from '../data/practiceBankConfigs/statisticsClinic04NormalDistributionZScores';
import { STATS_CLINIC_05_TRACKER_ID, STATS_CLINIC_05_LENSES, STATS_CLINIC_05_ITEMS } from '../data/practiceBankConfigs/statisticsClinic05ProbabilityEmpiricalRule';
import { STATS_CLINIC_06_TRACKER_ID, STATS_CLINIC_06_LENSES, STATS_CLINIC_06_ITEMS } from '../data/practiceBankConfigs/statisticsClinic06HypothesisABTesting';
import { STATS_CLINIC_07_TRACKER_ID, STATS_CLINIC_07_LENSES, STATS_CLINIC_07_ITEMS } from '../data/practiceBankConfigs/statisticsClinic07PValuesConfidenceIntervals';
import { STATS_CLINIC_08A_TRACKER_ID, STATS_CLINIC_08A_LENSES, STATS_CLINIC_08A_ITEMS } from '../data/practiceBankConfigs/statisticsClinic08aSkewness';
import { STATS_CLINIC_08B_TRACKER_ID, STATS_CLINIC_08B_LENSES, STATS_CLINIC_08B_ITEMS } from '../data/practiceBankConfigs/statisticsClinic08bKurtosis';
import { EXCEL_ANALYST_CHALLENGE_TRACKER_ID, EXCEL_ANALYST_CHALLENGE_LENSES, EXCEL_ANALYST_CHALLENGE_ITEMS } from '../data/practiceBankConfigs/excelAnalystChallengeScenarios';
import SESSION_SLIDES from '../data/session_slides';
import { fetchSlideSettings, patchSlideSettings } from '../api/settingsApi';
import { useAuth } from '../auth/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['open', 'claimed', 'pr-open', 'reviewed', 'merged'];
const STATUS_LABEL = {
  open: 'Open',
  claimed: 'Claimed',
  'pr-open': 'PR open',
  reviewed: 'Reviewed',
  merged: 'Merged',
};
const STATUS_COLOR = {
  open: 'default',
  claimed: 'info',
  'pr-open': 'warning',
  reviewed: 'secondary',
  merged: 'success',
};

const CopyableCommandBlock = ({ value, fontSize = '0.78rem', lineHeight = 1.55, padding = 1, marginTop = 0.75 }) => (
  <Box sx={{ position: 'relative', mt: marginTop }}>
    <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: 'rgba(16, 24, 40, 0.9)', borderRadius: 1 }}>
      <CopyValueButton value={value} label="snippet" />
    </Box>
    <Box
      component="pre"
      sx={{
        mt: 0,
        mb: 0,
        p: padding,
        pr: 5,
        borderRadius: 1,
        overflowX: 'auto',
        bgcolor: '#101828',
        color: '#f8fafc',
        border: '1px solid',
        borderColor: '#344054',
        fontSize,
        lineHeight,
      }}
    >
      <code>{value}</code>
    </Box>
  </Box>
);
const WEEK_STATUS_LABEL = {
  locked: 'Locked',
  open: 'Open',
  in_progress: 'In progress',
  closed: 'Closed',
};
const WEEK_STATUS_COLOR = {
  locked: 'default',
  open: 'info',
  in_progress: 'warning',
  closed: 'success',
};
const AVAILABILITY_DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const formatHourLabel = (hour) => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  if (normalizedHour === 0) return '12am';
  if (normalizedHour === 12) return '12pm';
  return normalizedHour > 12 ? `${normalizedHour - 12}pm` : `${normalizedHour}am`;
};

const buildHourlySlots = (days = AVAILABILITY_DAY_ORDER.slice(0, 6), startHour = 0, endHour = 24) => (
  days.flatMap((day) => (
    Array.from({ length: endHour - startHour }, (_, index) => {
      const hour = startHour + index;
      return `${day} ${formatHourLabel(hour)} - ${formatHourLabel(hour + 1)}`;
    })
  ))
);

const DEFAULT_AVAILABILITY_SLOTS = buildHourlySlots();

const DEFAULT_HACKATHON_SLOTS = buildHourlySlots();

const DEFAULT_SHOW_TELL_SLOTS = buildHourlySlots();

const DEFAULT_MOCK_INTERVIEW_SLOTS = buildHourlySlots();

const SCHEDULE_ACTIVITY_CONFIG = {
  clinic: {
    label: 'Concept Clinic',
    weekLabel: 'Week',
    cadence: 'Weekly availability poll',
    helper: 'Concept clinic timing for this week.',
    selectedLabel: 'Confirmed clinic time',
    slotsFieldLabel: 'Poll slots',
    maxSelections: 0,
    defaultSlots: DEFAULT_AVAILABILITY_SLOTS,
  },
  hackathon: {
    label: 'Hackathon',
    weekLabel: 'Week',
    cadence: '1 per week',
    helper: 'Hackathon timing for this week. Pick up to 2 options.',
    selectedLabel: 'Confirmed hackathon time',
    slotsFieldLabel: 'Hackathon time options',
    maxSelections: 2,
    defaultSlots: DEFAULT_HACKATHON_SLOTS,
  },
  show_tell: {
    label: 'Show & Tell',
    weekLabel: 'Week',
    cadence: '1 per week',
    helper: 'Show & Tell timing for this week. Pick up to 2 options.',
    selectedLabel: 'Confirmed Show & Tell time',
    slotsFieldLabel: 'Show & Tell time options',
    maxSelections: 2,
    defaultSlots: DEFAULT_SHOW_TELL_SLOTS,
  },
  mock_interviews: {
    label: 'Mock Interviews',
    weekLabel: 'Cycle',
    cadence: '2 per month',
    helper: 'Mock interview timing for this cycle. Pick up to 2 options.',
    selectedLabel: 'Confirmed mock interview time',
    slotsFieldLabel: 'Mock interview time options',
    maxSelections: 2,
    defaultSlots: DEFAULT_MOCK_INTERVIEW_SLOTS,
  },
};
const AVAILABILITY_DAY_INDEX = Object.fromEntries(AVAILABILITY_DAY_ORDER.map((day, index) => [day, index]));

const availabilityDayForSlot = (slot = '') => {
  const foundDay = AVAILABILITY_DAY_ORDER.find((day) => slot.toLowerCase().startsWith(day.toLowerCase()));
  return foundDay || 'Other';
};

const availabilityTimeForSlot = (slot = '') => {
  const day = availabilityDayForSlot(slot);
  return day === 'Other' ? slot : slot.slice(day.length).trim();
};

const availabilityHourForSlot = (slot = '') => {
  const match = availabilityTimeForSlot(slot).match(/^(\d{1,2})(am|pm)\b/i);
  if (!match) return 99;
  const hour = Number(match[1]);
  const period = match[2].toLowerCase();
  if (hour === 12) return period === 'am' ? 0 : 12;
  return period === 'pm' ? hour + 12 : hour;
};

const compareAvailabilityRows = (a, b) => (
  (AVAILABILITY_DAY_INDEX[a.day] ?? 99) - (AVAILABILITY_DAY_INDEX[b.day] ?? 99) ||
  availabilityHourForSlot(a.slot) - availabilityHourForSlot(b.slot) ||
  a.slot.localeCompare(b.slot)
);

const TRACK_OPTIONS = [
  { slug: 'python-job-ready', label: 'Python Job Ready' },
  { slug: 'sql-job-ready', label: 'SQL Job Ready' },
  { slug: 'data-analyst-job-ready', label: 'Data Analyst Job Ready' },
  { slug: 'azure-data-engineer', label: 'Azure Data Engineer' },
];

const trackLabelFor = (trackSlug) => (
  TRACK_OPTIONS.find((track) => track.slug === trackSlug)?.label || trackSlug || 'No study plan selected'
);

const TEAM_SIZE_OPTIONS = [4, 5];

const COHORT_ACTIVITY_TABS = [
  { key: 'daily_clinic', label: 'Daily Clinic' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'hackathon', label: 'Hackathon' },
  { key: 'journal_evidence', label: 'Journal & Evidence' },
  { key: 'show_and_tell', label: 'Show & Tell' },
  { key: 'mock_interview', label: 'Mock Interviews' },
  { key: 'progress', label: 'Cohort Progress' },
];

const SCHEDULE_ACTIVITY_TABS = [
  { key: 'clinic', label: SCHEDULE_ACTIVITY_CONFIG.clinic.label, cadence: SCHEDULE_ACTIVITY_CONFIG.clinic.cadence },
  { key: 'hackathon', label: SCHEDULE_ACTIVITY_CONFIG.hackathon.label, cadence: SCHEDULE_ACTIVITY_CONFIG.hackathon.cadence },
  { key: 'show_tell', label: SCHEDULE_ACTIVITY_CONFIG.show_tell.label, cadence: SCHEDULE_ACTIVITY_CONFIG.show_tell.cadence },
  { key: 'mock_interviews', label: SCHEDULE_ACTIVITY_CONFIG.mock_interviews.label, cadence: SCHEDULE_ACTIVITY_CONFIG.mock_interviews.cadence },
];

const DUPLICATE_PRACTICE_TRACKER_ID = 'dq-math-03-duplicates-practice-bank';
const MISSINGNESS_PRACTICE_TRACKER_ID = 'dq-math-02-missingness-practice-bank';
const OUTLIER_PRACTICE_TRACKER_ID = 'dq-math-04-outliers-practice-bank';

// Builds the {itemId: {lensKey: expectedText}} shape DCC_TAKE_HOME_CONFIGS
// expects, straight from each practice bank config's own items — so the
// admin panel's answer guide can never drift out of sync with the lens keys
// the practice bank component actually grades against (unlike duplicates/
// missingness/outliers above, which hand-typed a parallel lens list).
const answerGuideFromItems = (items) =>
  items.reduce((acc, item) => ({ ...acc, [item.id]: item.answerGuide }), {});
const DCC_TAKE_HOME_CONFIGS = {
  duplicates: {
    label: 'Duplicates',
    practiceKey: 'duplicate_practice',
    completedTarget: 8,
    lenses: [
      ['duplicate_bucket', 'Duplicate type / bucket'],
      ['grain', 'Grain'],
      ['uniqueness_rule', 'Uniqueness rule'],
      ['duplicate_type', 'Duplicate type'],
      ['evidence', 'Evidence'],
      ['wrong_move', 'Wrong move'],
      ['treatment', 'Treatment'],
      ['verify', 'Verify'],
      ['learner_sentence', 'Learner sentence'],
    ],
    answerGuide: DUPLICATE_ANSWER_GUIDE,
  },
  missingness: {
    label: 'Missingness',
    practiceKey: 'missingness_practice',
    completedTarget: 8,
    lenses: [
      ['missingness_bucket', 'Missingness type / bucket'],
      ['mechanism', 'Stat mechanism'],
      ['evidence', 'Evidence'],
      ['denominator', 'Denominator / scope'],
      ['treatment', 'Treatment'],
      ['verify', 'Verify'],
      ['caution', 'Caution'],
      ['learner_sentence', 'Learner sentence'],
    ],
    answerGuide: MISSINGNESS_ANSWER_GUIDE,
  },
  outliers: {
    label: 'Outliers',
    practiceKey: 'outlier_practice',
    completedTarget: 8,
    lenses: [
      ['outlier_bucket', 'Outlier type / bucket'],
      ['stat_rule', 'Stat rule'],
      ['evidence', 'Evidence'],
      ['business_check', 'Business check'],
      ['treatment', 'Treatment'],
      ['verify', 'Verify'],
      ['caution', 'Caution'],
      ['learner_sentence', 'Learner sentence'],
    ],
    answerGuide: OUTLIER_ANSWER_GUIDE,
  },
  dq_math_05: {
    label: 'Invalid Values',
    practiceKey: 'dq_math_05_practice',
    completedTarget: DQ_MATH_05_ITEMS.length,
    lenses: DQ_MATH_05_LENSES,
    answerGuide: answerGuideFromItems(DQ_MATH_05_ITEMS),
  },
  statistics_01a: {
    label: 'Statistics: Centre & Spread',
    practiceKey: 'statistics_01a_practice',
    completedTarget: STATS_CLINIC_01A_ITEMS.length,
    lenses: STATS_CLINIC_01A_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_01A_ITEMS),
  },
  statistics_01b: {
    label: 'Statistics: Position & Relationship',
    practiceKey: 'statistics_01b_practice',
    completedTarget: STATS_CLINIC_01B_ITEMS.length,
    lenses: STATS_CLINIC_01B_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_01B_ITEMS),
  },
  statistics_03: {
    label: 'Statistics 3: Outliers & Sampling Bias',
    practiceKey: 'statistics_03_practice',
    completedTarget: STATS_CLINIC_03_ITEMS.length,
    lenses: STATS_CLINIC_03_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_03_ITEMS),
  },
  statistics_03a: {
    label: 'Statistics 3a: Position',
    practiceKey: 'statistics_03a_practice',
    completedTarget: STATS_CLINIC_03A_ITEMS.length,
    lenses: STATS_CLINIC_03A_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_03A_ITEMS),
  },
  statistics_04: {
    label: 'Statistics 4: Normal Distribution & Z-Scores',
    practiceKey: 'statistics_04_practice',
    completedTarget: STATS_CLINIC_04_ITEMS.length,
    lenses: STATS_CLINIC_04_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_04_ITEMS),
  },
  statistics_05: {
    label: 'Statistics 5: Probability & the 68-95-99.7 Rule',
    practiceKey: 'statistics_05_practice',
    completedTarget: STATS_CLINIC_05_ITEMS.length,
    lenses: STATS_CLINIC_05_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_05_ITEMS),
  },
  statistics_06: {
    label: 'Statistics 6: Hypothesis Testing & A/B Testing',
    practiceKey: 'statistics_06_practice',
    completedTarget: STATS_CLINIC_06_ITEMS.length,
    lenses: STATS_CLINIC_06_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_06_ITEMS),
  },
  statistics_07: {
    label: 'Statistics 7: P-values & Confidence Intervals',
    practiceKey: 'statistics_07_practice',
    completedTarget: STATS_CLINIC_07_ITEMS.length,
    lenses: STATS_CLINIC_07_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_07_ITEMS),
  },
  statistics_08a: {
    label: 'Statistics 8a: Skewness',
    practiceKey: 'statistics_08a_practice',
    completedTarget: STATS_CLINIC_08A_ITEMS.length,
    lenses: STATS_CLINIC_08A_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_08A_ITEMS),
  },
  statistics_08b: {
    label: 'Statistics 8b: Kurtosis',
    practiceKey: 'statistics_08b_practice',
    completedTarget: STATS_CLINIC_08B_ITEMS.length,
    lenses: STATS_CLINIC_08B_LENSES,
    answerGuide: answerGuideFromItems(STATS_CLINIC_08B_ITEMS),
  },
  dm_05_kimball_facts: {
    label: 'DM 05: Kimball Fact Tables',
    practiceKey: 'dm_05_kimball_facts_practice',
    completedTarget: DM05_CONFIG.items.length,
    lenses: DM_TECHNIQUE_LENSES,
    answerGuide: answerGuideFromItems(DM05_CONFIG.items),
  },
  dm_08_data_vault: {
    label: 'DM 08: Data Vault Foundations',
    practiceKey: 'dm_08_data_vault_practice',
    completedTarget: DM08_CONFIG.items.length,
    lenses: DM_TECHNIQUE_LENSES,
    answerGuide: answerGuideFromItems(DM08_CONFIG.items),
  },
  ct_10_request_to_spec: {
    label: 'CT 10: From Request to Spec',
    practiceKey: 'ct_10_request_to_spec_practice',
    completedTarget: CT10_REQUEST_TO_SPEC_ITEMS.length,
    lenses: CT10_REQUEST_TO_SPEC_LENSES,
    answerGuide: answerGuideFromItems(CT10_REQUEST_TO_SPEC_ITEMS),
  },
  sql_02_data_modelling: {
    label: 'SQL 02: Data Modelling Masterclass',
    practiceKey: 'sql_02_data_modelling_practice',
    completedTarget: SQL02_DATA_MODELLING_ITEMS.length,
    lenses: SQL02_DATA_MODELLING_LENSES,
    answerGuide: answerGuideFromItems(SQL02_DATA_MODELLING_ITEMS),
  },
  sql_03_database_fundamentals: {
    label: 'DB 01: Database Fundamentals',
    practiceKey: 'sql_03_database_fundamentals_practice',
    completedTarget: SQL03_DATABASE_FUNDAMENTALS_ITEMS.length,
    lenses: SQL03_DATABASE_FUNDAMENTALS_LENSES,
    answerGuide: answerGuideFromItems(SQL03_DATABASE_FUNDAMENTALS_ITEMS),
  },
  sql_04_oltp_olap: {
    label: 'DB 02: OLTP vs OLAP',
    practiceKey: 'sql_04_oltp_olap_practice',
    completedTarget: SQL04_OLTP_OLAP_ITEMS.length,
    lenses: SQL04_OLTP_OLAP_LENSES,
    answerGuide: answerGuideFromItems(SQL04_OLTP_OLAP_ITEMS),
  },
  ct_08_table_detective: {
    label: 'CT 08: Tables, Fields & Relationships',
    practiceKey: 'ct_08_table_detective_practice',
    completedTarget: CT08_TABLE_DETECTIVE_ITEMS.length,
    lenses: CT08_TABLE_DETECTIVE_LENSES,
    answerGuide: answerGuideFromItems(CT08_TABLE_DETECTIVE_ITEMS),
  },
  excel_analyst_challenge: {
    label: 'Excel: Real-Life Analyst Scenarios',
    practiceKey: 'excel_analyst_challenge_practice',
    completedTarget: EXCEL_ANALYST_CHALLENGE_ITEMS.length,
    lenses: EXCEL_ANALYST_CHALLENGE_LENSES,
    answerGuide: answerGuideFromItems(EXCEL_ANALYST_CHALLENGE_ITEMS),
  },
};
const DCC_TAKE_HOME_TABS = Object.entries(DCC_TAKE_HOME_CONFIGS).map(([key, config]) => ({
  key,
  label: config.label,
}));
const DCC_TAKE_HOME_PRACTICE_KEYS = Object.values(DCC_TAKE_HOME_CONFIGS).map((config) => config.practiceKey);

// Every topic with an in-app gradable take-home (TrackerProgress-backed
// practice bank) — used to offer the learner's own graded results as
// evidence-field suggestions, instead of making them retype a score that's
// already computed and stored.
const PRACTICE_EVIDENCE_TRACKERS = [
  { trackerId: DUPLICATE_PRACTICE_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.duplicates.label, target: DCC_TAKE_HOME_CONFIGS.duplicates.completedTarget },
  { trackerId: MISSINGNESS_PRACTICE_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.missingness.label, target: DCC_TAKE_HOME_CONFIGS.missingness.completedTarget },
  { trackerId: OUTLIER_PRACTICE_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.outliers.label, target: DCC_TAKE_HOME_CONFIGS.outliers.completedTarget },
  { trackerId: DQ_MATH_05_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.dq_math_05.label, target: DCC_TAKE_HOME_CONFIGS.dq_math_05.completedTarget },
  { trackerId: STATS_CLINIC_01A_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_01a.label, target: DCC_TAKE_HOME_CONFIGS.statistics_01a.completedTarget },
  { trackerId: STATS_CLINIC_01B_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_01b.label, target: DCC_TAKE_HOME_CONFIGS.statistics_01b.completedTarget },
  { trackerId: STATS_CLINIC_03_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_03.label, target: DCC_TAKE_HOME_CONFIGS.statistics_03.completedTarget },
  { trackerId: STATS_CLINIC_03A_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_03a.label, target: DCC_TAKE_HOME_CONFIGS.statistics_03a.completedTarget },
  { trackerId: STATS_CLINIC_04_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_04.label, target: DCC_TAKE_HOME_CONFIGS.statistics_04.completedTarget },
  { trackerId: STATS_CLINIC_05_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_05.label, target: DCC_TAKE_HOME_CONFIGS.statistics_05.completedTarget },
  { trackerId: STATS_CLINIC_06_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_06.label, target: DCC_TAKE_HOME_CONFIGS.statistics_06.completedTarget },
  { trackerId: STATS_CLINIC_07_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_07.label, target: DCC_TAKE_HOME_CONFIGS.statistics_07.completedTarget },
  { trackerId: STATS_CLINIC_08A_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_08a.label, target: DCC_TAKE_HOME_CONFIGS.statistics_08a.completedTarget },
  { trackerId: STATS_CLINIC_08B_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.statistics_08b.label, target: DCC_TAKE_HOME_CONFIGS.statistics_08b.completedTarget },
  { trackerId: CT08_TABLE_DETECTIVE_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.ct_08_table_detective.label, target: DCC_TAKE_HOME_CONFIGS.ct_08_table_detective.completedTarget },
  { trackerId: SQL02_DATA_MODELLING_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.sql_02_data_modelling.label, target: DCC_TAKE_HOME_CONFIGS.sql_02_data_modelling.completedTarget },
  { trackerId: SQL03_DATABASE_FUNDAMENTALS_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.sql_03_database_fundamentals.label, target: DCC_TAKE_HOME_CONFIGS.sql_03_database_fundamentals.completedTarget },
  { trackerId: SQL04_OLTP_OLAP_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.sql_04_oltp_olap.label, target: DCC_TAKE_HOME_CONFIGS.sql_04_oltp_olap.completedTarget },
  { trackerId: CT10_REQUEST_TO_SPEC_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.ct_10_request_to_spec.label, target: DCC_TAKE_HOME_CONFIGS.ct_10_request_to_spec.completedTarget },
  { trackerId: DM05_CONFIG.trackerId, label: DCC_TAKE_HOME_CONFIGS.dm_05_kimball_facts.label, target: DCC_TAKE_HOME_CONFIGS.dm_05_kimball_facts.completedTarget },
  { trackerId: DM08_CONFIG.trackerId, label: DCC_TAKE_HOME_CONFIGS.dm_08_data_vault.label, target: DCC_TAKE_HOME_CONFIGS.dm_08_data_vault.completedTarget },
  { trackerId: EXCEL_ANALYST_CHALLENGE_TRACKER_ID, label: DCC_TAKE_HOME_CONFIGS.excel_analyst_challenge.label, target: DCC_TAKE_HOME_CONFIGS.excel_analyst_challenge.completedTarget },
];

const loadPracticeEvidenceOptions = async () => {
  const results = await Promise.all(
    PRACTICE_EVIDENCE_TRACKERS.map(({ trackerId }) => loadRemoteProgress(trackerId).catch(() => null))
  );
  return PRACTICE_EVIDENCE_TRACKERS.reduce((options, { label, target }, index) => {
    const state = results[index];
    const summary = state?.grade_summary;
    if (!summary) return options;
    const completed = state?.completed_tables || 0;
    options.push(
      `Graded take-home — ${label}: ${completed}/${target} completed, ${summary.percent}% (auto-graded in-app; see Cohort Progress -> Daily Concept Clinic for the breakdown)`
    );
    return options;
  }, []);
};

// Shared across every activity type's "what next" field — a starting point,
// not a constraint; the field stays freeSolo so a learner can always type
// their own answer instead.
const NEXT_FIELD_SUGGESTIONS = [
  'Redo the take-home practice with a fresh dataset',
  'More worked examples on this topic',
  '1:1 review with a mentor',
  'Revisit the core concept in a follow-up clinic',
  'No further support needed',
];

const CLINIC_LIBRARY_TABS = [
  { key: 'all', label: 'All' },
  { key: 'flashcards', label: 'Flashcards' },
  { key: 'dq_math', label: 'Data Quality Math' },
  { key: 'cross_track', label: 'Cross-Track' },
  { key: 'sql', label: 'SQL' },
  { key: 'python', label: 'Python' },
  { key: 'full_stack', label: 'Full-Stack / APIs' },
  { key: 'azure', label: 'Azure' },
  { key: 'data_analyst', label: 'Data Analyst' },
];

// A mock interview has more than one player at the table: the candidate who
// answered, the interviewer(s) who asked the questions and graded them, and
// any other cohort member sitting in on the panel. The capture form used to
// be written for the candidate only ("Your answer summary"), so an
// interviewer had nowhere honest to log the session. These variants re-label
// the same five journal keys (topic/covered/takeaway/evidence/next) per role,
// which keeps the rendered journal, the artifact folder, and the
// understanding review working unchanged while the wording matches whoever
// is actually writing.
const MOCK_INTERVIEW_ROLE_FORMS = [
  {
    key: 'candidate',
    label: 'Candidate (you were interviewed)',
    shortLabel: 'Candidate',
    helper: 'You sat the interview and answered the questions.',
    description: 'Capture the question you were actually asked, how you answered it, the feedback you were given, and your next practice target.',
    fields: [
      ['topic', 'Question or topic you were asked'],
      ['covered', 'Your answer summary'],
      ['takeaway', 'Feedback received'],
      ['evidence', 'Repo project, journal entry, or example you used'],
      ['next', 'What to practise next'],
    ],
  },
  {
    key: 'interviewer',
    label: 'Interviewer (you asked and graded)',
    shortLabel: 'Interviewer',
    helper: 'You ran the interview and scored it against the rubric.',
    description: 'Capture the question you asked, how the candidate answered, the feedback and score you gave, and what to probe next cycle. Scores themselves still go on the interview board.',
    fields: [
      ['topic', 'Question or topic you assessed'],
      ['covered', 'How the candidate answered'],
      ['takeaway', 'Feedback and score you gave'],
      ['evidence', 'Rubric line, project, or example the answer was judged against'],
      ['next', 'What to probe or run differently next cycle'],
    ],
  },
  {
    key: 'observer',
    label: 'Panel observer (you watched)',
    shortLabel: 'Panel observer',
    helper: 'You sat in on the panel without asking or answering.',
    description: 'Capture what was asked, what you observed in the exchange, what a stronger answer would have covered, and what you will practise before your own cycle.',
    fields: [
      ['topic', 'Question or topic you observed'],
      ['covered', 'What you observed in the exchange'],
      ['takeaway', 'What a stronger answer would have covered'],
      ['evidence', 'Model answer, project, or example referenced'],
      ['next', 'What to practise before your own cycle'],
    ],
  },
];

// The API already tells each viewer which side of the table they were on
// (repositories.py's _mock_interview_to_dict sets viewer_role), so the form
// can default the role instead of asking the learner to classify themselves.
// An admin watching a cycle they set up is treated as an interviewer, which
// they can override in the form.
const MOCK_INTERVIEW_ROLE_BY_VIEWER = {
  candidate: 'candidate',
  interviewer: 'interviewer',
  admin: 'interviewer',
  other: 'observer',
};

const mockInterviewRoleForm = (role) =>
  MOCK_INTERVIEW_ROLE_FORMS.find((entry) => entry.key === role) || MOCK_INTERVIEW_ROLE_FORMS[0];

// Swaps in the role variant's wording; forms without role variants (daily
// clinic, hackathon, show & tell) pass through untouched.
const resolveJournalFormConfig = (config, role) => {
  if (!config?.roles?.length) return config;
  const variant = config.roles.find((entry) => entry.key === role) || config.roles[0];
  return {
    ...config,
    description: variant.description,
    fields: variant.fields,
    roleKey: variant.key,
    roleLabel: variant.shortLabel,
  };
};

const describeInterviewSession = (session) => {
  if (!session) return '';
  const when = session.scheduled_at ? new Date(session.scheduled_at) : null;
  const parts = [
    `Cycle ${session.cycle_number || '?'}`,
    session.job_title || 'Mock interview',
  ];
  if (when && !Number.isNaN(when.getTime())) {
    parts.push(when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
  }
  return parts.join(' · ');
};

// Topic options for a mock interview note should be the questions from the
// cycle that was actually posted, not a generic list. `questions` is only
// present on sessions the server let this viewer see — interviewers and
// admins always, the candidate only once the cycle is marked complete — so
// reading it here inherits that masking rather than leaking the question set.
const mockInterviewTopicOptions = (sessions = [], fallback = []) => {
  const seen = new Set();
  const options = [];
  const add = (value, group) => {
    const clean = String(value || '').trim();
    const key = clean.toLowerCase();
    if (!clean || seen.has(key)) return;
    seen.add(key);
    options.push({ value: clean, label: clean, group });
  };
  sessions.forEach((session) => {
    const cycle = `Cycle ${session.cycle_number || '?'}`;
    (session.questions || []).forEach((question) => add(question?.question, `${cycle} — questions asked`));
    (session.topics_covered || []).forEach((topic) => add(topic, `${cycle} — advertised topics`));
    if (session.job_title) add(`${session.job_title} — overall interview`, `${cycle} — advertised topics`);
  });
  fallback.forEach((option) => add(typeof option === 'string' ? option : option?.value, 'General practice'));
  return options;
};

const COHORT_JOURNAL_FORMS = {
  journal_evidence: {
    title: 'Daily tasks evidence',
    description: 'Capture daily task proof, repo paths, screenshots, blockers, and learning summaries.',
    section: 'Daily Tasks',
    order: 1,
    fields: [
      ['topic', 'Task or topic'],
      ['covered', 'What you did'],
      ['takeaway', 'Decision, learning, or blocker'],
      ['evidence', 'Evidence, file, repo path, or screenshot'],
      ['next', 'What to review next'],
    ],
  },
  daily_clinic: {
    title: 'Daily clinic output',
    description: 'Capture the concept taught today, your understanding, your feedback, and the take-home evidence it connects to.',
    section: 'Daily Concept Clinic',
    order: 2,
    fields: [
      ['topic', 'Clinic topic'],
      ['covered', 'What was taught'],
      ['takeaway', 'What you now understand'],
      ['question', 'What is still unclear or your feedback'],
      ['evidence', 'Take-home, graded practice, file, repo path, or screenshot'],
      ['next', 'What support or practice you need next'],
    ],
  },
  hackathon: {
    title: 'Hackathon journal note',
    description: 'Log your role, contribution, PR status, and what the team learned.',
    section: 'Hackathon',
    order: 3,
    fields: [
      ['topic', 'Hackathon / week'],
      ['covered', 'Your role or task'],
      ['takeaway', 'What you contributed'],
      ['evidence', 'PR, branch, file, workbook, or screenshot'],
      ['next', 'Review or follow-up'],
    ],
  },
  show_and_tell: {
    title: 'Show & Tell note',
    description: 'Capture what you presented, feedback received, and how you explained your evidence.',
    section: 'Show & Tell',
    order: 4,
    fields: [
      ['topic', 'Demo topic'],
      ['covered', 'What you showed'],
      ['takeaway', 'Questions or feedback received'],
      ['evidence', 'Demo link, repo path, slide, or screenshot'],
      ['next', 'How you will improve the explanation'],
    ],
  },
  mock_interview: {
    title: 'Mock interview note',
    description: 'Capture the question, your answer quality, feedback, and next practice target.',
    section: 'Mock Interview',
    order: 5,
    // Default (candidate) wording; the live labels come from the role
    // variant chosen in the form — see MOCK_INTERVIEW_ROLE_FORMS.
    fields: MOCK_INTERVIEW_ROLE_FORMS[0].fields,
    roles: MOCK_INTERVIEW_ROLE_FORMS,
  },
};

const COHORT_ACTIVITY_SLUG = {
  'Daily Tasks': 'daily-tasks',
  'Daily Concept Clinic': 'daily-concept-clinic',
  Hackathon: 'hackathon',
  'Show & Tell': 'show-and-tell',
  'Mock Interview': 'mock-interview',
};

const appendCohortJournalEntry = async (trackerId, entry) => {
  const current = await fetchJournal(trackerId).catch(() => ({ markdown: '' }));
  const existingSection = readJournalSection(current?.markdown, 'cohort');
  const nextSection = existingSection ? `${existingSection}\n\n${entry.trim()}` : entry.trim();
  const merged = upsertJournalSection(current?.markdown, 'cohort', nextSection);
  return saveJournal(trackerId, merged);
};

const resolveCohortJournalTracker = (...trackSlugs) => {
  const match = trackSlugs.find((trackSlug) => TRACK_OPTIONS.some((track) => track.slug === trackSlug));
  return match || '';
};

const HACKATHON_WORKFLOW_SECTIONS = [
  {
    title: 'Part 1: setup, push, and PR',
    description: 'Do this before the hackathon starts. The goal is to prove everyone can clone, prepare their folder, push, and raise a small PR before real work begins.',
    steps: [
      {
        label: 'Open the hackathon workspace and copy the repo URL',
        detail: 'Use the shared workspace button for this week. If it opens GitHub, copy the repo URL. If it opens Excel Online, keep it open for the file work and use the repo URL your admin shared.',
      },
      {
        label: 'Clone the cohort repo once',
        detail: 'Do this only the first time you work on the cohort repo. It creates a local folder for the shared hackathon files.',
        command: 'git clone <repo-url> alpha_cohort_hackathon\ncd alpha_cohort_hackathon',
      },
      {
        label: 'Create your setup branch',
        detail: 'Use this branch only to prove your Git setup works.',
        command: 'git checkout -b setup-<your-first-name>',
      },
      {
        label: 'Create your learner folder and starter file',
        detail: 'Put the starter file inside your own learner folder. Do not edit another learner\'s folder.',
        command: 'mkdir -p hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>\ntouch hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>/tempfile.md',
      },
      {
        label: 'Prepare the dataset folder',
        detail: 'Download the hackathon dataset or starter workbook before the session. Put the raw/starter file inside the dataset folder within your own learner folder.',
        command: 'mkdir -p hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>/dataset\n# After downloading the file, move or copy it into:\n# hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>/dataset/',
      },
      {
        label: 'Commit, push, and raise your setup PR',
        detail: 'Open a small PR from your setup branch into main and assign at least two reviewers. This proves your folder and prep files are in the right place before the hackathon work starts.',
        command: 'git add hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>\ngit commit -m "Prepare learner hackathon folder"\ngit push -u origin setup-<your-first-name>',
      },
      {
        label: 'Add a setup reflection to your journal workspace',
        detail: 'After raising the setup PR, write a short journal note so you remember what you learned before the live hackathon starts.',
        command: 'Example journal note:\nSetup PR raised for Week 2. I cloned the cohort repo, created my learner folder, added the dataset folder, pushed my branch, and opened a PR for review. Lesson learned: pull from main before starting and keep shared data inside my own learner folder.',
      },
    ],
  },
  {
    title: 'Part 2: during the hackathon',
    description: 'Use this loop for your assigned case or role. It is designed to build collaboration habits while avoiding conflicts.',
    steps: [
      {
        label: 'Pull the latest main before working',
        detail: 'Start from the newest main branch so your branch includes reviewed setup files and teammate changes.',
        command: 'git checkout main\ngit pull',
      },
      {
        label: 'Create your hackathon work branch',
        detail: 'Use a fresh branch for the real case or role work. Keep setup work and hackathon output separate.',
        command: 'git checkout -b <your-first-name>-week-02',
      },
      {
        label: 'Work only inside your learner folder',
        detail: 'Delete the setup tempfile, then work only inside your learner folder. Open the shared workbook or dataset, complete only your assigned case or role, then save or download your output file into the same folder.',
        command: 'rm hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>/tempfile.md',
      },
      {
        label: 'Add your output and notes',
        detail: 'Your PR should contain your completed workbook/output file and a short notes.md explaining your role, changes, assumptions, and anything unfinished.',
        command: 'cd hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>\ntouch notes.md',
      },
      {
        label: 'Commit your work locally',
        detail: 'Commit your assigned output before pulling main again. This protects your changes before you sync with the team.',
        command: 'git add hackathon/week-02/learner-<your code, e.g. a>-<your-first-name>\ngit commit -m "Add week 02 <case-or-role> output"',
      },
      {
        label: 'Pull main again before pushing',
        detail: 'Sync with the team before your push. If Git reports a conflict, stop and ask for help before guessing.',
        command: 'git checkout main\ngit pull\ngit checkout <your-first-name>-week-02\ngit merge main',
      },
      {
        label: 'Push and open one PR for your case or role',
        detail: 'Do not wait for the whole team. Push your branch, open one PR for your assigned output, assign at least two reviewers, and respond to comments before merge.',
        command: 'git push -u origin <your-first-name>-week-02',
      },
      {
        label: 'Add a hackathon reflection to your journal workspace',
        detail: 'After opening your hackathon PR, capture what you did, what changed after cleaning, how collaboration worked, and what you would do differently next time.',
        command: 'Example journal note:\nWeek 2 hackathon completed. I worked on the Inspect/Clean/Check/Answer rotation, kept my files inside my learner folder, deleted the setup tempfile, committed my output, pulled main before pushing, and opened one PR. Lesson learned: documenting cleaning decisions matters because teammates need evidence before trusting the final answer.',
      },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const displayCohortName = (k) =>
  k ? k.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

// So the "Open shared workspace" button tells a learner what they're about to
// see before they click — a GitHub repo (code) and an Excel Online sheet
// (spreadsheet) are very different destinations for a beginner to land on cold.
const describeWorkspaceLink = (url) => {
  if (!url) return 'Open shared workspace';
  if (/github\.com/i.test(url)) return 'Open GitHub repo';
  if (/1drv\.ms|office\.com|live\.com|sharepoint\.com/i.test(url)) return 'Open Excel Online sheet';
  return 'Open shared workspace';
};

const formatScheduled = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) +
      ' · ' +
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    );
  } catch {
    return iso;
  }
};

const toDatetimeLocal = (iso) => (iso ? iso.slice(0, 16) : '');

const getInitials = (name) => {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const weekToSummary = (full) => ({
  week_number: full.week_number,
  problem_title: full.problem_title,
  status: full.status,
  total_roles: (full.roles || []).length,
  merged_count: (full.roles || []).filter((r) => r.status === 'merged').length,
  scheduled_at: full.scheduled_at || null,
  availability_response_count: full.availability_response_count || Object.keys(full.availability_responses || {}).length,
  clinic_selected_slot: full.clinic_selected_slot || '',
  clinic_coordinator_user_id: full.clinic_coordinator_user_id || '',
  clinic_coordinator_name: full.clinic_coordinator_name || '',
  role_owners: (full.roles || []).map((r) => r.owner || ''),
  updated_at: full.updated_at,
});

const describeRoleStatusChange = (role, previousStatus, nextStatus) => ({
  id: `${role.id || role.title}-${previousStatus || 'none'}-${nextStatus || 'none'}-${Date.now()}`,
  roleId: role.id,
  roleTitle: role.title || 'Role',
  owner: role.owner || role.owner_email || '',
  previousStatus,
  nextStatus,
  time: new Date(),
});

const formatActivityTime = (date) => {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return '';
  }
};

// ── Hackathon preview data (shown before weeks are unlocked) ─────────────────
//
// Week 1 is framed as a real ticket from a line manager rather than a generic
// "challenge" — the team is picking up DA-117, not playing a game. Team size
// is configurable (4 or 5): at 4, workbook setup folds into the Librarian
// role so all five underlying skills (setup, tables, sorting, filtering,
// named ranges) are still covered by fewer people.

const TICKET_ID = 'DA-117';

const TICKET = {
  id: TICKET_ID,
  subject: "Clean up the Q3 regional sales export before Thursday's review",
  raisedBy: 'Priya Shah',
  raisedByTitle: 'Line Manager, Commercial Analytics',
  priority: 'High',
  due: "Before Thursday's 10am review",
};

const ROLE_DEFS = {
  architect: {
    id: 'architect',
    title: 'The Architect',
    emoji: '🏗️',
    task: "Everyone is waiting on you before they can start — do this first.\n\n1. Click 'Open Excel Online sheet' above to open the blank workbook the admin shared.\n2. Rename Sheet1 → Raw_Export (right-click the tab → Rename).\n3. Add a second sheet → name it Clean_Data.\n4. Add a third sheet → name it Analysis.\n5. In cell A1 of Raw_Export, type: DO NOT EDIT — paste raw CSV here only.\n6. Post the workbook link in the team channel and confirm everyone can open it.\n\nOnce the team confirms access, your part is done. The Librarian takes over from here.",
    skills: 'Workbook setup · sheet structure · file hygiene',
    colour: '#1565c0',
  },
  librarian: {
    id: 'librarian',
    title: 'The Librarian',
    emoji: '📚',
    task: "Download the raw dataset using the link in the workspace section above. Paste it into the Raw_Export sheet the Architect created — do not edit it there. Then copy it into Clean_Data, fix all the messy values (mixed-case regions, inconsistent statuses, £-prefixed numbers, blank rows, leading spaces), convert it to an Excel Table named SalesData, and build named ranges for Status, Region, and Category. Subtasks 2 and 5 on the ticket.",
    skills: 'Excel Tables · structured references · named ranges · data cleaning',
    colour: '#2e7d32',
  },
  librarianCombined: {
    id: 'librarian',
    title: 'The Librarian',
    emoji: '📚',
    task: "Download the raw dataset using the link in the workspace section above. Set up the shared workbook with three sheets (Raw_Export, Clean_Data, Analysis), paste the raw file into Raw_Export untouched, then copy into Clean_Data and fix all the messy values (mixed-case regions, inconsistent statuses, £-prefixed numbers, blank rows, leading spaces). Convert to an Excel Table named SalesData and build named ranges for Status, Region, and Category. Subtasks 1, 2, and 5 — covering setup as well, since the team's running at 4.",
    skills: 'Workbook setup · Excel Tables · named ranges · data cleaning',
    colour: '#2e7d32',
  },
  sorter: {
    id: 'sorter',
    title: 'The Sorter',
    emoji: '🔢',
    task: 'Multi-level sort the table so Priya can see top performers at a glance — by deal value, then by region. Subtask 3 on the ticket.',
    skills: 'Multi-level sort · custom sort orders · rank logic',
    colour: '#6a1b9a',
  },
  investigator: {
    id: 'investigator',
    title: 'The Investigator',
    emoji: '🔍',
    task: 'Filter out the noise: deals over £10k still "In Progress", plus anything sitting in a declining category. Subtask 4 on the ticket.',
    skills: 'AutoFilter · custom filter · Advanced Filter',
    colour: '#e65100',
  },
  presenter: {
    id: 'presenter',
    title: 'The Presenter',
    emoji: '📊',
    task: "Write the reply to Priya — plain English, using SUMIF with the named ranges so it's actually checkable. Answer her three questions: who's doing well, what's stuck, what's worth raising.",
    skills: 'SUMIF · structured references · clear written comms',
    colour: '#00695c',
  },
};

const getHackathonRoles = (teamSize = 5) => {
  const order =
    teamSize <= 4
      ? ['librarianCombined', 'sorter', 'investigator', 'presenter']
      : ['architect', 'librarian', 'sorter', 'investigator', 'presenter'];

  return order.map((key, index) => {
    const def = ROLE_DEFS[key];
    return {
      id: def.id,
      title: def.title,
      emoji: def.emoji,
      task: def.task,
      skills: def.skills,
      colour: def.colour,
      branch: `${TICKET_ID}/${def.id}`,
      level: index === 0 ? 'Coordinator' : 'Team role',
    };
  });
};

const getHackathonTimeline = (teamSize = 5) => {
  const fivePerson = [
    { time: '0:00', action: 'Architect drops the shared workbook link. Everyone opens it.' },
    { time: '0:15', action: 'Librarian pastes the raw export into the workbook, converts it to a Table, and builds the named ranges.' },
    { time: '0:30', action: "Librarian signals done. Sorter and Investigator both start — they don't block each other." },
    { time: '0:50', action: 'Sorter delivers the ranked tab. Investigator delivers the filtered tab.' },
    { time: '1:30', action: '⚡ TWIST: Priya messages the channel — can you also break the top performers down by region? Sorter handles it.' },
    { time: '1:45', action: "Presenter pulls everyone's output into the reply Priya actually reads." },
    { time: '2:00', action: "Reply goes out. Team checks it against the ticket's 3 questions before calling it done." },
  ];

  const fourPerson = [
    { time: '0:00', action: 'Librarian sets up the shared workbook, pastes in the raw export, and converts it to a Table.' },
    { time: '0:25', action: 'Named ranges built for Status, Region, and Category. Librarian signals done.' },
    { time: '0:30', action: "Sorter and Investigator both start — they don't block each other." },
    { time: '0:50', action: 'Sorter delivers the ranked tab. Investigator delivers the filtered tab.' },
    { time: '1:30', action: '⚡ TWIST: Priya messages the channel — can you also break the top performers down by region? Sorter handles it.' },
    { time: '1:45', action: "Presenter pulls everyone's output into the reply Priya actually reads." },
    { time: '2:00', action: "Reply goes out. Team checks it against the ticket's 3 questions before calling it done." },
  ];

  return teamSize <= 4 ? fourPerson : fivePerson;
};

const PREP_ITEMS = [
  'Open Excel Online in your browser — go to office.com, sign in with any free Microsoft account (Outlook/Hotmail/Live), and confirm you can create a new workbook',
  'Know how to paste plain text data into a spreadsheet',
  'Read the ticket and resources below fully before the session starts',
  'Watch: "Excel Tables in 5 minutes" on YouTube',
  'Read: What is a Named Range? (Excel Help)',
];

const buildWeekOneHackathonDraft = (teamSize = 5) => ({
  problem_title: `Ticket ${TICKET_ID}: Clean up the Q3 sales export`,
  problem_brief: [
    `## Ticket ${TICKET_ID}`,
    '',
    `**Raised by:** ${TICKET.raisedBy}, ${TICKET.raisedByTitle}`,
    `**Priority:** ${TICKET.priority}`,
    `**Due:** ${TICKET.due}`,
    '**Status:** Open',
    '',
    '### Description',
    '',
    'Morning team,',
    '',
    'IT just dumped the Q3 regional sales export on me as one raw, unformatted sheet — no structure, inconsistent dates, columns named things like "Col1". I need this turned into something the team can actually use before Thursday, and ideally something we can reuse every quarter without rebuilding it from scratch.',
    '',
    "Here's what I need, in order:",
    '',
    "1. Set the workbook up properly — sensible sheet names, a structure that survives being opened on someone else's laptop.",
    '2. Turn the raw export into a proper Excel Table so it updates cleanly as rows are added or removed.',
    '3. Sort it so I can see our top performers at a glance — by deal value, then by region.',
    '4. Filter out the noise — deals over £10k still "In Progress", plus anything in a declining category.',
    "5. Set up named ranges for the Status, Region, and Category lookup lists so the formulas don't turn into a wall of cell references nobody else can read.",
    '',
    "I don't need a polished dashboard. I need to look at this Thursday and answer: who's doing well, what's stuck, and what's worth raising with the directors.",
    '',
    "Resources are linked below this brief — go through them before touching the file. Ping me if you're blocked.",
    '',
    '— Priya',
    '',
    '## Suggested working order',
    '',
    ...getHackathonTimeline(teamSize).map((step) => `- **${step.time}:** ${step.action}`),
    '',
    '## Before you pick this up',
    '',
    ...PREP_ITEMS.map((item) => `- ${item}`),
  ].join('\n'),
  scenario_context:
    "Priya's just had the Q3 regional sales export dumped on her by IT — one raw sheet, no structure, due for Thursday's review. " +
    `There's a real ticket, 5 underlying subtasks, and a ${teamSize}-person team to split them across. Each person's output feeds the next.`,
  shared_repo_url: '',
  dataset_url: '/data/hackathon-data/da-117-q3-sales-raw.csv',
  team_size: teamSize,
  roles: getHackathonRoles(teamSize).map((role) => ({
    id: role.id,
    title: role.title,
    level: role.level,
    solve_prompt: role.task,
    branch_hint: role.branch,
    owner: '',
    owner_email: '',
    status: 'open',
    notes: role.skills,
  })),
  reflection: '',
  status: 'locked',
  scheduled_at: null,
});

const NoActiveHackathon = () => (
  <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
    <LockIcon sx={{ fontSize: 46, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h5" fontWeight={800} gutterBottom>
      No active hackathon yet
    </Typography>
    <Typography color="text.secondary">
      Your cohort admin is preparing the next weekly hackathon. It will appear here after
      they unlock it, and assigned learners will receive the notification email.
    </Typography>
  </Container>
);

// ── HackathonCTA (admin-only draft preview) ──────────────────────────────────

const HackathonCTA = ({ teamSize = 5 }) => {
  const theme = useTheme();
  const [showTimeline, setShowTimeline] = useState(false);
  const roles = useMemo(() => getHackathonRoles(teamSize), [teamSize]);
  const timeline = useMemo(() => getHackathonTimeline(teamSize), [teamSize]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.92)} 0%, ${alpha(theme.palette.primary.main, 0.78)} 100%)`,
          color: '#fff',
          p: { xs: 3, md: 5 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: -40, right: -40, width: 200, height: 200,
          borderRadius: '50%', background: alpha('#fff', 0.04), pointerEvents: 'none',
        }} />
        <Stack direction="row" sx={{ mb: 1.5 }}>
          <Chip
            icon={<AssignmentIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
            label={`TICKET ${TICKET_ID} — WEEK 1 HACKATHON`}
            size="small"
            sx={{
              bgcolor: alpha('#fff', 0.15), color: '#fff', fontWeight: 700,
              fontSize: '0.7rem', letterSpacing: 0.8,
              border: '1px solid', borderColor: alpha('#fff', 0.3),
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
        </Stack>
        <Typography variant="h3" fontWeight={900} sx={{ mb: 1, lineHeight: 1.15, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, wordBreak: 'break-word' }}>
          {TICKET.subject}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.85, maxWidth: 640, fontWeight: 400, lineHeight: 1.5 }}>
          Raised by {TICKET.raisedBy} ({TICKET.raisedByTitle}). A raw, unstructured export, a real
          ticket, and a {teamSize}-person team to fix it before the review.
        </Typography>
        <Box sx={{
          mt: 3, p: 2.5, borderRadius: 2,
          bgcolor: alpha('#000', 0.25),
          borderLeft: '4px solid', borderColor: '#ffd54f',
        }}>
          <Typography variant="body2" sx={{ color: '#ffd54f', fontWeight: 700, mb: 0.5 }}>
            THE TICKET
          </Typography>
          <Typography variant="body1" sx={{ color: alpha('#fff', 0.9), lineHeight: 1.6 }}>
            IT exported Q3 regional sales as one raw sheet — no structure, inconsistent dates,
            "Col1"-style column names. Priya needs it set up, turned into a Table, sorted, filtered,
            and backed by named ranges, then a plain-English answer to who's doing well, what's
            stuck, and what's worth raising with the directors. Due{' '}
            <strong style={{ color: '#ffd54f' }}>{TICKET.due}</strong>.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
          {[
            { icon: <GroupsIcon sx={{ fontSize: 18, opacity: 0.8 }} />, label: `${teamSize}-person team` },
            { icon: <AccessTimeIcon sx={{ fontSize: 18, opacity: 0.8 }} />, label: '2 hours · 1 live follow-up at 90 min' },
            { icon: <AccountTreeIcon sx={{ fontSize: 18, opacity: 0.8 }} />, label: 'Forced-dependency workflow' },
          ].map(({ icon, label }) => (
            <Stack key={label} direction="row" spacing={0.75} alignItems="center">
              {icon}
              <Typography variant="body2" sx={{ opacity: 0.85 }}>{label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Role cards */}
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Pick your role</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Each role's output feeds the next. Claim yours when Week 1 unlocks.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {roles.map((role, idx) => (
          <Grid item xs={12} sm={6} md={4} key={role.id}>
            <Paper elevation={0} sx={{
              p: 2.5, height: '100%',
              border: '1px solid', borderColor: 'divider',
              borderTop: `3px solid ${role.colour}`,
              borderRadius: 2,
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: 4 },
            }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 22 }}>{role.emoji}</Typography>
                <Box>
                  <Typography variant="caption" sx={{
                    color: role.colour, fontWeight: 700, fontSize: '0.68rem',
                    letterSpacing: 0.6, textTransform: 'uppercase',
                  }}>
                    Role {idx + 1}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    {role.title}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.55 }}>
                {role.task}
              </Typography>
              <Box sx={{
                px: 1.25, py: 0.75, borderRadius: 1, mb: 1,
                bgcolor: alpha(role.colour, 0.07),
                border: `1px solid ${alpha(role.colour, 0.2)}`,
              }}>
                <Typography variant="caption" sx={{ color: role.colour, fontWeight: 600, fontSize: '0.7rem' }}>
                  Skills: {role.skills}
                </Typography>
              </Box>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.35, borderRadius: 1,
                bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
              }}>
                <LinkIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'text.secondary' }}>
                  {role.branch}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Timeline */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined" size="small" startIcon={<AccessTimeIcon />}
          onClick={() => setShowTimeline((v) => !v)} sx={{ mb: 2 }}
        >
          {showTimeline ? 'Hide' : 'See'} the 2-hour sequence
        </Button>
        {showTimeline && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {timeline.map((step, idx) => (
              <Stack key={idx} direction="row" spacing={2} alignItems="flex-start" sx={{
                px: 2.5, py: 1.5,
                borderBottom: idx < timeline.length - 1 ? '1px solid' : 0,
                borderColor: 'divider',
                bgcolor: step.action.startsWith('⚡') ? alpha(theme.palette.warning.main, 0.06) : 'transparent',
              }}>
                <Typography variant="caption" sx={{
                  fontFamily: 'monospace', fontWeight: 700, fontSize: '0.78rem',
                  color: step.action.startsWith('⚡') ? 'warning.dark' : 'text.disabled',
                  minWidth: 36, mt: 0.15,
                }}>
                  {step.time}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{step.action}</Typography>
              </Stack>
            ))}
          </Paper>
        )}
      </Box>

      {/* Prep checklist */}
      <Paper elevation={0} sx={{
        p: 3, borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
        bgcolor: alpha(theme.palette.success.main, 0.04),
      }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={800}>
            Prepare before your admin unlocks Week 1
          </Typography>
        </Stack>
        <Stack spacing={1}>
          {PREP_ITEMS.map((item, idx) => (
            <Stack key={idx} direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                border: '2px solid', borderColor: 'success.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.1,
              }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'success.main' }}>
                  {idx + 1}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Container>
  );
};

// ── RoleSetupRow (admin: compact assignment row inside setup panel) ───────────

const RoleSetupRow = ({ role, members, onUpdate }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 1, sm: 2 }}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    sx={{
      py: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': { borderBottom: 0, pb: 0 },
    }}
  >
    <Box sx={{ minWidth: { sm: 190 } }}>
      <Typography variant="body2" fontWeight={700}>{role.title}</Typography>
      {role.branch_hint && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {role.branch_hint}
        </Typography>
      )}
    </Box>
    <TextField
      select
      label="Assigned to"
      value={role.owner_email || ''}
      onChange={(e) => {
        const email = e.target.value;
        const member = (members || []).find((m) => m.email === email);
        if (member) {
          onUpdate('owner', member.label);
          onUpdate('owner_email', member.email);
        } else {
          onUpdate('owner', '');
          onUpdate('owner_email', '');
        }
      }}
      size="small"
      sx={{ flex: 1 }}
      helperText="Notification emailed to their registered address on unlock"
    >
      <MenuItem value="">— Unassigned —</MenuItem>
      {(members || []).map((m) => (
        <MenuItem key={m.email} value={m.email}>{m.label}</MenuItem>
      ))}
    </TextField>
  </Stack>
);

const formatAttemptTimeLeft = (deadlineAt, nowMs) => {
  if (!deadlineAt) return '';
  const diff = new Date(deadlineAt).getTime() - nowMs;
  if (Number.isNaN(diff) || diff <= 0) return '00:00';
  const totalMinutes = Math.floor(diff / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const attemptStatusLabel = (status) => ({
  in_progress: 'In progress',
  expired: 'Expired',
  submitted_on_time: 'Submitted on time',
  submitted_late: 'Submitted late',
  reviewed: 'Reviewed',
}[status] || 'Not started');

const attemptStatusColor = (status) => ({
  in_progress: 'warning',
  expired: 'error',
  submitted_on_time: 'success',
  submitted_late: 'error',
  reviewed: 'secondary',
}[status] || 'default');

const HackathonAttemptMonitor = ({ attempts = [], roles = [] }) => {
  const roleTitleById = Object.fromEntries((roles || []).map((role) => [role.id, role.title]));
  if (!attempts.length) {
    return (
      <Alert severity="info">
        No learner has started a timed attempt for this week yet.
      </Alert>
    );
  }
  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
        Timed attempt monitor
      </Typography>
      <Stack spacing={1}>
        {attempts.map((attempt) => (
          <Box key={attempt.id} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={800}>
                  {attempt.learner_name || attempt.learner_email || 'Learner'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {roleTitleById[attempt.role_id] || attempt.role_id}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.5}>
                <Chip size="small" label={attemptStatusLabel(attempt.status)} color={attemptStatusColor(attempt.status)} />
                {attempt.deadline_at && (
                  <Chip size="small" variant="outlined" icon={<AccessTimeIcon />} label={`Due ${formatScheduled(attempt.deadline_at)}`} />
                )}
              </Stack>
            </Stack>
            {(attempt.branch_name || attempt.commit_url || attempt.pr_url) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.5} sx={{ mt: 1 }}>
                {attempt.branch_name && <Chip size="small" variant="outlined" label={attempt.branch_name} />}
                {attempt.commit_url && (
                  <Button size="small" href={attempt.commit_url} target="_blank" rel="noreferrer" endIcon={<OpenInNewIcon />}>
                    Commit
                  </Button>
                )}
                {attempt.pr_url && (
                  <Button size="small" href={attempt.pr_url} target="_blank" rel="noreferrer" endIcon={<OpenInNewIcon />}>
                    PR
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

// ── LearnerRoleCard (learner: their role — informational + status update) ─────

const LearnerRoleCard = ({ role, cohortKey, weekNumber, onSavePatch, weekData }) => {
  const theme = useTheme();
  const [localStatus, setLocalStatus] = useState(role.status || 'open');
  const [patching, setPatching] = useState(false);
  const [patchStatus, setPatchStatus] = useState('');
  const [showSolve, setShowSolve] = useState(false);
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [aiReviewState, setAiReviewState] = useState('idle');
  const [aiReviewResult, setAiReviewResult] = useState(null);
  const [aiReviewError, setAiReviewError] = useState('');
  const [gradeOpen, setGradeOpen] = useState(false);
  const [gradeState, setGradeState] = useState('idle');
  const [gradeResult, setGradeResult] = useState(null);
  const [gradeError, setGradeError] = useState('');
  const [attempt, setAttempt] = useState(null);
  const [attemptLoading, setAttemptLoading] = useState(false);
  const [attemptError, setAttemptError] = useState('');
  const [attemptForm, setAttemptForm] = useState({
    branch_name: role.branch_hint || '',
    commit_url: '',
    pr_url: '',
    files_note: '',
    reflection: '',
  });
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => { setLocalStatus(role.status || 'open'); }, [role.status]);
  useEffect(() => {
    setAttemptForm((prev) => ({ ...prev, branch_name: prev.branch_name || role.branch_hint || '' }));
  }, [role.branch_hint]);
  useEffect(() => {
    let alive = true;
    fetchHackathonAttempts(cohortKey, weekNumber)
      .then((items) => {
        if (!alive) return;
        const ownAttempt = (items || []).find((item) => item.role_id === role.id);
        if (ownAttempt) {
          setAttempt(ownAttempt);
          setAttemptForm({
            branch_name: ownAttempt.branch_name || role.branch_hint || '',
            commit_url: ownAttempt.commit_url || '',
            pr_url: ownAttempt.pr_url || '',
            files_note: ownAttempt.files_note || '',
            reflection: ownAttempt.reflection || '',
          });
        }
      })
      .catch(() => { });
    return () => { alive = false; };
  }, [cohortKey, weekNumber, role.id, role.branch_hint]);
  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress') return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [attempt]);

  const timeLeft = formatAttemptTimeLeft(attempt?.deadline_at, nowMs);
  const computedAttemptStatus = attempt?.status === 'in_progress' && timeLeft === '00:00' ? 'expired' : attempt?.status;

  const practiceItem = useMemo(
    () => ({
      sourceType: 'cohort',
      sourceKey: `${cohortKey}:w${weekNumber}:${role.id}`,
      title: role.title,
      prompt: role.solve_prompt || '',
      availableFrameworks: ['solve'],
      context: {
        task_type: 'excel',
        is_cohort: true,
        workspace_url: weekData?.shared_repo_url || '',
        dataset_url: weekData?.dataset_url || '',
        problem_title: weekData?.problem_title || '',
      },
    }),
    // Only the three URL/title fields matter — ignore other localData changes (status, roles, etc.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cohortKey, weekNumber, role.id, role.title, role.solve_prompt,
     weekData?.shared_repo_url, weekData?.dataset_url, weekData?.problem_title],
  );

  const handlePracticeComplete = useCallback(async (responses, framework) => {
    try {
      const weekLabel = `Week ${weekNumber}: ${weekData?.problem_title || 'Cohort evidence'}`;
      const entry = [
        `## ${weekLabel}`,
        '',
        `### 3. Hackathon - ${new Date().toISOString().slice(0, 10)}`,
        '',
        formatPracticeJournalEntry(practiceItem, responses, framework),
      ].join('\n');
      await appendCohortJournalEntry(resolveCohortJournalTracker(weekData?.track_slug), entry);
    } catch { /* journal save is best-effort */ }
  }, [practiceItem, weekNumber, weekData?.problem_title, weekData?.track_slug]);

  const handleSaveStatus = async () => {
    if (!onSavePatch) return;
    setPatching(true);
    setPatchStatus('');
    try {
      await onSavePatch({ status: localStatus });
      setPatchStatus('Saved');
    } catch (err) {
      setPatchStatus(err.message || 'Save failed');
    } finally {
      setPatching(false);
    }
  };

  const runAiReview = async () => {
    setAiReviewState('loading');
    setAiReviewError('');
    try {
      const content = [
        `# ${role.title}`,
        role.branch_hint ? `Branch: ${role.branch_hint}` : '',
        'Task:',
        role.solve_prompt || '',
        `Status: ${role.status}`,
      ].filter(Boolean).join('\n\n');
      const result = await analyzeEvidence({
        content,
        analysisType: 'hackathon-review',
        context: { week_number: weekNumber, cohort_key: cohortKey, role_title: role.title },
      });
      setAiReviewResult(result);
      setAiReviewState('done');
      recordAuditEvent('hackathon_role_review_ran', {
        cohort_key: cohortKey,
        week_number: weekNumber,
        role_id: role.id,
        mode: result.mode,
      }).catch(() => { });
    } catch (error) {
      setAiReviewError(error.message || 'AI review failed.');
      setAiReviewState('error');
    }
  };

  const runGrade = async () => {
    setGradeState('loading');
    setGradeError('');
    try {
      const result = await gradeCohortRole(cohortKey, weekNumber, role.id);
      setGradeResult(result);
      setGradeState('done');
      recordAuditEvent('hackathon_role_graded_viewed', {
        cohort_key: cohortKey,
        week_number: weekNumber,
        role_id: role.id,
        score: result?.score,
      }).catch(() => { });
    } catch (error) {
      setGradeError(error.message || 'Grading failed.');
      setGradeState('error');
    }
  };

  const handleStartAttempt = async () => {
    setAttemptLoading(true);
    setAttemptError('');
    try {
      const started = await startHackathonAttempt(cohortKey, weekNumber, role.id);
      setAttempt(started);
      setNowMs(Date.now());
      setAttemptForm((prev) => ({
        ...prev,
        branch_name: started.branch_name || prev.branch_name || role.branch_hint || '',
      }));
    } catch (error) {
      setAttemptError(error.message || 'Could not start attempt.');
    } finally {
      setAttemptLoading(false);
    }
  };

  const handleSubmitAttempt = async () => {
    setAttemptLoading(true);
    setAttemptError('');
    try {
      const submitted = await submitHackathonAttempt(cohortKey, weekNumber, role.id, attemptForm);
      setAttempt(submitted);
      setLocalStatus('pr-open');
      if (onSavePatch) {
        await onSavePatch({ status: 'pr-open', pr_link: submitted.pr_url, notes: submitted.files_note });
      }
    } catch (error) {
      setAttemptError(error.message || 'Could not submit attempt.');
    } finally {
      setAttemptLoading(false);
    }
  };

  return (
    <>
    <Paper elevation={0} sx={{
      p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 2,
      borderTop: `3px solid ${theme.palette.primary.main}`, overflow: 'hidden',
    }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="primary.main" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Your role
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
              {role.title}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={STATUS_LABEL[role.status] || role.status}
            color={STATUS_COLOR[role.status] || 'default'}
          />
        </Stack>

        {/* Branch hint */}
        {role.branch_hint && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            px: 1.25, py: 0.6, borderRadius: 1, overflow: 'hidden', maxWidth: '100%',
            bgcolor: alpha(theme.palette.primary.main, 0.07),
            border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2),
          }}>
            <LinkIcon sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="caption" sx={{
              fontFamily: 'monospace', fontWeight: 700, color: 'primary.dark', fontSize: '0.72rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {role.branch_hint}
            </Typography>
          </Box>
        )}

        {/* Task */}
        {role.solve_prompt && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {role.solve_prompt}
          </Typography>
        )}

        {/* Skills */}
        {role.notes && (
          <Box sx={{
            px: 1.25, py: 0.75, borderRadius: 1,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2),
          }}>
            <Typography variant="caption" color="success.dark" fontWeight={600}>
              Skills: {role.notes}
            </Typography>
          </Box>
        )}

        {/* Status update */}
        {onSavePatch && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
            <TextField
              select
              label="My progress"
              value={localStatus}
              onChange={(e) => setLocalStatus(e.target.value)}
              size="small"
              sx={{ minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small" variant="outlined"
              startIcon={patching ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveStatus}
              disabled={patching}
            >
              Save
            </Button>
            {patchStatus && (
              <Typography variant="caption" color={patchStatus === 'Saved' ? 'success.main' : 'error.main'}>
                {patchStatus}
              </Typography>
            )}
            <Button
              size="small" variant="outlined" startIcon={<PsychologyIcon />}
              onClick={() => { setAiReviewOpen(true); runAiReview(); }}
            >
              AI review
            </Button>
            <Button
              size="small" variant="outlined" color="secondary" startIcon={<GradeIcon />}
              onClick={() => { setGradeOpen(true); runGrade(); }}
            >
              Grade my understanding
            </Button>
          </Stack>
        )}

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
              <Box>
                <Typography variant="subtitle2" fontWeight={800}>Timed hackathon attempt</Typography>
                <Typography variant="caption" color="text.secondary">
                  Start when ready. You have exactly 2 hours to commit and raise your PR.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {attempt && (
                  <Chip size="small" color={attemptStatusColor(computedAttemptStatus)} label={attemptStatusLabel(computedAttemptStatus)} />
                )}
                {attempt?.status === 'in_progress' && (
                  <Chip size="small" icon={<AccessTimeIcon />} label={timeLeft} color={timeLeft === '00:00' ? 'error' : 'warning'} />
                )}
              </Stack>
            </Stack>

            {!attempt ? (
              <Button
                variant="contained"
                startIcon={attemptLoading ? <CircularProgress size={14} color="inherit" /> : <AccessTimeIcon />}
                onClick={handleStartAttempt}
                disabled={attemptLoading}
                sx={{ alignSelf: 'flex-start' }}
              >
                Start my 2-hour attempt
              </Button>
            ) : (
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Branch name"
                    size="small"
                    value={attemptForm.branch_name}
                    onChange={(e) => setAttemptForm((prev) => ({ ...prev, branch_name: e.target.value }))}
                    disabled={attempt.status !== 'in_progress'}
                    fullWidth
                  />
                  <TextField
                    label="Commit URL or hash"
                    size="small"
                    value={attemptForm.commit_url}
                    onChange={(e) => setAttemptForm((prev) => ({ ...prev, commit_url: e.target.value }))}
                    disabled={attempt.status !== 'in_progress'}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="PR link"
                  size="small"
                  value={attemptForm.pr_url}
                  onChange={(e) => setAttemptForm((prev) => ({ ...prev, pr_url: e.target.value }))}
                  disabled={attempt.status !== 'in_progress'}
                  fullWidth
                />
                <TextField
                  label="Files submitted"
                  size="small"
                  value={attemptForm.files_note}
                  onChange={(e) => setAttemptForm((prev) => ({ ...prev, files_note: e.target.value }))}
                  disabled={attempt.status !== 'in_progress'}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Finish note"
                  size="small"
                  value={attemptForm.reflection}
                  onChange={(e) => setAttemptForm((prev) => ({ ...prev, reflection: e.target.value }))}
                  disabled={attempt.status !== 'in_progress'}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  startIcon={attemptLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                  onClick={handleSubmitAttempt}
                  disabled={attemptLoading || attempt.status !== 'in_progress'}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Submit attempt
                </Button>
              </Stack>
            )}
            {attemptError && <Alert severity="error">{attemptError}</Alert>}
          </Stack>
        </Box>

        {/* SOLVE workspace */}
        {role.solve_prompt && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
            {showSolve ? (
              <>
                <PracticeEntry item={practiceItem} compact onComplete={handlePracticeComplete} />
                <Button size="small" onClick={() => setShowSolve(false)} sx={{ mt: 1 }}>
                  Hide SOLVE workspace
                </Button>
              </>
            ) : (
              <Stack spacing={0.5} alignItems="flex-start">
                <Button
                  size="small" variant="outlined" startIcon={<AutoAwesomeIcon />}
                  onClick={() => setShowSolve(true)}
                >
                  Open <GlossaryTerm term="solve">SOLVE</GlossaryTerm> workspace
                </Button>
                <Typography variant="caption" color="text.secondary">
                  A guided worksheet to break this role's problem down before you start.
                </Typography>
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
    <EvidenceIntelligenceDialog
      open={aiReviewOpen}
      onClose={() => setAiReviewOpen(false)}
      title={`AI review — ${role.title}`}
      state={aiReviewState}
      result={aiReviewResult}
      error={aiReviewError}
      onRerun={runAiReview}
      sections={['scores', 'recommendations', 'gaps', 'next_actions']}
      loadingLabel="Reviewing your role evidence..."
      idleHint="Run the review to check whether this role's evidence would satisfy a reviewer."
    />
    <EvidenceIntelligenceDialog
      open={gradeOpen}
      onClose={() => setGradeOpen(false)}
      title={`Understanding check — ${role.title}`}
      state={gradeState}
      result={gradeResult}
      error={gradeError}
      onRerun={runGrade}
      sections={['hackathon_score', 'hackathon_evidence', 'lens_results', 'weekly_summary']}
      loadingLabel="Reading your submitted files from GitHub and checking your reasoning..."
      idleHint="Pulls your actual dq_issue_log/final_dq_note from your branch and checks whether you correctly diagnosed the known issues — not just whether the file exists."
    />
    </>
  );
};

const emptyCohortJournalForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  week: '',
  topic: '',
  covered: '',
  takeaway: '',
  question: '',
  evidence: '',
  next: '',
});

const journalCell = (value) => String(value || '').trim().replace(/\|/g, '\\|').replace(/\n/g, '<br />');

// One semantic lens per signal we actually want scored, per activity type.
// `field` is which captured value is graded; `expectation` is handed to the
// same semantic-rubric-grade engine that grades hackathons and SOLVE/5-HOW
// practice — the grader judges meaning against this, not exact wording, and
// there's no fixed "correct" text since these are open-ended reflections.
const UNDERSTANDING_REVIEW_LENSES = {
  daily_clinic: [
    { key: 'topic_alignment', label: 'Topic alignment', field: 'takeaway', expectation: 'Reflects the actual clinic topic and what was taught today, not a generic statement.' },
    { key: 'concept_clarity', label: 'Concept clarity', field: 'takeaway', expectation: "Explains the concept correctly and specifically, in the learner's own words." },
    { key: 'reflection_depth', label: 'Reflection depth', field: 'question', expectation: 'Asks a specific question or names a concrete gap or blocker, not "no questions" or left blank.' },
    { key: 'evidence_connection', label: 'Evidence connection', field: 'evidence', expectation: 'Links to real take-home evidence such as a file, repo path, or graded practice, not blank or vague.' },
    { key: 'next_action', label: 'Next action', field: 'next', expectation: 'States a concrete next step or practice target for this topic, not filler like "keep practising".' },
  ],
  show_and_tell: [
    { key: 'delivery_clarity', label: 'Delivery clarity', field: 'covered', expectation: 'Clearly describes what was actually demoed or built, with specific detail rather than a vague summary.' },
    { key: 'evidence_link', label: 'Evidence link', field: 'evidence', expectation: 'Points to a real demo link, repo path, slide, or screenshot, not blank or a placeholder.' },
    { key: 'feedback_capture', label: 'Feedback capture', field: 'takeaway', expectation: 'Captures specific questions or feedback received from the audience, not "none" or generic praise.' },
    { key: 'improvement_plan', label: 'Improvement plan', field: 'next', expectation: 'States a concrete way to improve the explanation next time, not filler.' },
  ],
  // Keyed `${activityType}_${role}` for mock interviews, since an
  // interviewer's note should not be graded on "did you answer well".
  mock_interview: [
    { key: 'answer_completeness', label: 'Answer completeness', field: 'covered', expectation: 'Summarises an actual answer to the stated interview question, with real reasoning or structure, not a one-line placeholder.' },
    { key: 'feedback_capture', label: 'Feedback capture', field: 'takeaway', expectation: 'Captures specific feedback received — what was strong, what was weak — not "went fine" or blank.' },
    { key: 'evidence_link', label: 'Evidence link', field: 'evidence', expectation: 'References a real project, journal entry, or example actually used to answer, not blank or vague.' },
    { key: 'practice_target', label: 'Practice target', field: 'next', expectation: 'Names a specific thing to practise next for interview readiness, not generic filler.' },
  ],
  mock_interview_interviewer: [
    { key: 'assessment_detail', label: 'Assessment detail', field: 'covered', expectation: "Describes what the candidate actually said and how far it answered the question, with specifics rather than 'did well' or 'struggled'." },
    { key: 'feedback_quality', label: 'Feedback quality', field: 'takeaway', expectation: 'Records the feedback and score actually given, naming both a strength and a concrete gap, not just a verdict.' },
    { key: 'rubric_link', label: 'Rubric link', field: 'evidence', expectation: 'References the rubric line, project, or example the judgement was anchored to, not blank or vague.' },
    { key: 'follow_up_plan', label: 'Follow-up plan', field: 'next', expectation: 'Names what to probe or run differently in the next cycle, not generic filler.' },
  ],
  mock_interview_observer: [
    { key: 'observation_detail', label: 'Observation detail', field: 'covered', expectation: 'Describes what was actually asked and answered in the exchange, with specifics rather than a vague impression.' },
    { key: 'answer_analysis', label: 'Answer analysis', field: 'takeaway', expectation: 'Explains what a stronger answer would have covered, not just whether the interview went well.' },
    { key: 'evidence_link', label: 'Evidence link', field: 'evidence', expectation: 'References the model answer, project, or example the analysis draws on, not blank or vague.' },
    { key: 'practice_target', label: 'Practice target', field: 'next', expectation: 'Names a specific thing to practise before their own cycle, not generic filler.' },
  ],
};

const UNDERSTANDING_TRACKER_ID = {
  daily_clinic: 'dcc-understanding-log',
  show_and_tell: 'show-tell-understanding-log',
  mock_interview: 'mock-interview-understanding-log',
};

const gradeUnderstandingReview = async (activityType, values = {}) => {
  const templates =
    UNDERSTANDING_REVIEW_LENSES[`${activityType}_${values.role || ''}`] ||
    UNDERSTANDING_REVIEW_LENSES[activityType] ||
    [];
  const lenses = templates.map((t) => ({
    key: t.key,
    label: t.label,
    user_answer: String(values[t.field] || '').trim(),
    expected_answer: t.expectation,
    critical_terms: [],
  }));
  const topic = String(values.topic || '').trim();
  const content = [
    topic ? `Topic: ${topic}` : '',
    ...templates.map((t) => `${t.label}: ${String(values[t.field] || '').trim()}`),
  ].filter(Boolean).join('\n');

  // This used to swallow the failure and return a fabricated score of 0, which
  // appendUnderstandingReview then stored as the learner's real understanding
  // review. A learner could write a strong reflection, have the grader time
  // out, and be left with a permanent 0% they never earned. An ungraded
  // attempt must stay ungraded: throw, and let the caller record the entry
  // without a score rather than with a false one.
  const result = await analyzeEvidence({
    content,
    analysisType: 'semantic-rubric-grade',
    context: { rubric_key: `understanding_${activityType}`, lenses },
  });
  const lensResults = result.lensResults || {};
  const strengths = Object.values(lensResults).filter((r) => Number(r.score) >= 1).map((r) => r.label);
  const weaknesses = Object.values(lensResults).filter((r) => Number(r.score) < 1).map((r) => r.label);
  return {
    score: result.score ?? 0,
    max: result.max ?? lenses.length,
    percent: result.percent ?? 0,
    lenses: lensResults,
    strengths,
    weaknesses,
    feedback: result.feedback || '',
    mode: result.mode || 'rules',
  };
};

const appendUnderstandingReview = async ({ activityType, values, cohortKey, recordId }) => {
  const graded = await gradeUnderstandingReview(activityType, values);
  const review = {
    id: recordId || `${values.date || ''}-${Date.now()}`,
    cohort_key: cohortKey || '',
    clinic_date: values.date || new Date().toISOString().slice(0, 10),
    topic: values.topic || '',
    role: values.role || '',
    session: values.session_label || '',
    score: graded.score,
    max: graded.max,
    percent: graded.percent,
    lenses: graded.lenses,
    strengths: graded.strengths,
    weaknesses: graded.weaknesses,
    ai_summary: graded.feedback,
    mode: graded.mode,
    created_at: new Date().toISOString(),
  };
  const trackerId = UNDERSTANDING_TRACKER_ID[activityType];
  const previous = await loadRemoteProgress(trackerId).catch(() => null);
  const reviews = [review, ...((previous?.reviews || []).filter((item) => item.id !== review.id))].slice(0, 50);
  await saveRemoteProgress(trackerId, {
    version: 1,
    reviews,
    latest: review,
    updated_at: review.created_at,
  });
  return review;
};

const weekFolderFromLabel = (label = '') => {
  const match = String(label).match(/week\s*(\d+)/i);
  if (!match) return 'current-week';
  return `week-${String(match[1]).padStart(2, '0')}`;
};

const repoPathSegment = (value = '', fallback = 'item') => (
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || fallback
);

const artifactFolderFor = (config, weekLabel, cohortKey) => [
  'evidence',
  'cohorts',
  repoPathSegment(cohortKey, 'cohort'),
  weekFolderFromLabel(weekLabel),
  COHORT_ACTIVITY_SLUG[config.section] || 'general',
].join('/');

const clinicTopicOptionsFromSlides = (slides = []) => {
  const seen = new Set();
  const options = [];
  const addOption = (value, label = value) => {
    const cleanValue = String(value || '').trim();
    const cleanLabel = String(label || cleanValue).trim();
    const key = cleanValue.toLowerCase();
    if (!cleanValue || seen.has(key)) return;
    seen.add(key);
    options.push({ value: cleanValue, label: cleanLabel });
  };
  slides.forEach((slide) => {
    if (slide.id === 'stats-01-centre-spread') {
      [
        'Statistics 1: Centre - mean, median, mode',
        'Statistics 1: Spread - range and standard deviation',
        'Statistics 1: Choosing mean vs median',
      ].forEach((topic) => addOption(topic));
      return;
    }
    if (slide.id === 'stats-02-relationship') {
      [
        'Statistics 2: Position - rank, quartiles, percentile, IQR',
        'Statistics 2: Relationship - scatter plot and correlation',
        'Statistics 2: Correlation caution - association is not causation',
        'Statistics 2: Mixed statistic selection and business interpretation',
      ].forEach((topic) => addOption(topic));
      return;
    }
    addOption(slide.title);
  });
  return options;
};

const optionList = (values = []) => {
  const seen = new Set();
  return values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((value) => ({ value, label: value }));
};

const weekTopicOptions = (weeks = []) => optionList(
  weeks.map((week) => `Week ${week.week_number}: ${week.problem_title || week.title || 'Cohort evidence'}`),
);

const captureTopicOptionsFor = (type, { weeks = [], clinicTopics = [] } = {}) => {
  if (type === 'daily_clinic') return clinicTopics;
  if (type === 'hackathon') return weekTopicOptions(weeks);
  if (type === 'journal_evidence') {
    return optionList([
      ...weeks.map((week) => `Week ${week.week_number}: daily task evidence`),
      'Repo proof',
      'Screenshot or workbook evidence',
      'Blocker or review note',
    ]);
  }
  if (type === 'show_and_tell') {
    return optionList([
      ...weeks.map((week) => `Week ${week.week_number}: show and tell`),
      'Project demo',
      'Workbook walkthrough',
      'Dashboard explanation',
      'Peer feedback',
    ]);
  }
  if (type === 'mock_interview') {
    return optionList([
      ...weeks.map((week) => `Week ${week.week_number}: mock interview`),
      'Technical explanation',
      'Project walkthrough',
      'SQL interview practice',
      'Data quality interview practice',
      'Behavioural evidence story',
    ]);
  }
  return [];
};

const formatCohortJournalEntry = (config, values, cohortKey, fallbackWeekLabel) => {
  const weekLabel = String(values.week || fallbackWeekLabel || 'Week 1: Cohort evidence').trim();
  const savedDate = values.date || new Date().toISOString().slice(0, 10);
  const lines = [
    `## ${weekLabel}`,
    '',
    `### ${config.order}. ${config.section} - ${savedDate}`,
    '',
    '| Field | Note |',
    '| --- | --- |',
    `| Activity type | ${config.section} |`,
    `| Cohort | ${cohortKey || 'Cohort workspace'} |`,
  ];

  // A mock interview entry is only readable later if it says which seat the
  // writer was in and which posted cycle it belongs to.
  if (values.role_label) lines.push(`| Role | ${journalCell(values.role_label)} |`);
  if (values.session_label) lines.push(`| Interview | ${journalCell(values.session_label)} |`);

  config.fields.forEach(([key, label]) => {
    const value = journalCell(values[key]);
    if (value) lines.push(`| ${label} | ${value} |`);
  });

  return lines.join('\n');
};

const CohortJournalCapture = ({
  type,
  cohortKey,
  trackerId,
  trackerLabel,
  defaultWeekLabel,
  weekOptions = [],
  topicOptions = [],
  interviewSessions = [],
}) => {
  const baseConfig = COHORT_JOURNAL_FORMS[type];
  const [values, setValues] = useState(() => emptyCohortJournalForm());
  const [artifactFile, setArtifactFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [lastReview, setLastReview] = useState(null);
  const [open, setOpen] = useState(false);
  const [evidenceOptions, setEvidenceOptions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [role, setRole] = useState('candidate');

  const isMockInterview = type === 'mock_interview';

  // A learner types a full clinic reflection into React state and nothing else.
  // Before this, any failed save plus a refresh (or a closed laptop) lost the
  // lot, which is the worst possible outcome of the save bug — they did the
  // work twice or not at all. Mirror the form into localStorage so a failure
  // is an inconvenience rather than lost work.
  const draftKey = `qc-journal-draft:${type}:${cohortKey || 'no-cohort'}`;

  useEffect(() => {
    if (!open) return;
    try {
      const saved = window.localStorage.getItem(draftKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object' && Object.values(parsed).some((v) => String(v || '').trim())) {
        setValues((prev) => ({ ...prev, ...parsed }));
        setStatus('Restored your unsaved draft.');
      }
    } catch { /* a corrupt or blocked store must never stop the form opening */ }
  }, [open, draftKey]);

  useEffect(() => {
    if (!open) return;
    try {
      const hasContent = Object.values(values).some((v) => String(v || '').trim());
      if (hasContent) window.localStorage.setItem(draftKey, JSON.stringify(values));
    } catch { /* storage full or blocked - the form still works */ }
  }, [open, values, draftKey]);

  const clearDraft = () => {
    try { window.localStorage.removeItem(draftKey); } catch { /* nothing to do */ }
  };

  useEffect(() => {
    if (!open || type !== 'daily_clinic') return;
    let cancelled = false;
    loadPracticeEvidenceOptions().then((options) => { if (!cancelled) setEvidenceOptions(options); });
    return () => { cancelled = true; };
  }, [open, type]);

  const selectedSession = useMemo(
    () => interviewSessions.find((session) => String(session.id) === String(sessionId)) || null,
    [interviewSessions, sessionId],
  );

  // Open the form already pointed at the cycle this learner was actually part
  // of — most recent first, preferring one they played a part in over one they
  // only watched, so the common case needs no picking at all.
  useEffect(() => {
    if (!open || !isMockInterview || sessionId || interviewSessions.length === 0) return;
    const mine = interviewSessions.filter((session) => session.viewer_role && session.viewer_role !== 'other');
    const preferred = (mine.length ? mine : interviewSessions)
      .slice()
      .sort((a, b) => new Date(b.scheduled_at || 0) - new Date(a.scheduled_at || 0))[0];
    if (preferred) setSessionId(String(preferred.id));
  }, [open, isMockInterview, sessionId, interviewSessions]);

  // The server already decided which seat this viewer was in; only override
  // the learner's own choice when the selected cycle changes.
  useEffect(() => {
    if (!selectedSession) return;
    setRole(MOCK_INTERVIEW_ROLE_BY_VIEWER[selectedSession.viewer_role] || 'observer');
  }, [selectedSession]);

  // The week used to be a free-text box that defaulted to "Week 1", so most
  // learners never changed it: the same calendar date ended up labelled Week 1
  // by one learner and Week 5 by another, and journal_render bucketed entries
  // by whatever number happened to be in the string. A heading with no digits
  // at all became "week 9999". Restricting this to the cohort's own weeks is
  // what makes the journal index trustworthy.
  //
  // A restored draft (or an entry logged before this change) can still hold a
  // value that is not one of the options; MUI would render that as an empty
  // select, so fall back to the default rather than showing a blank.
  const resolvedWeekValue = useMemo(() => {
    const current = values.week || defaultWeekLabel || '';
    if (weekOptions.some((option) => option.value === current)) return current;
    if (weekOptions.some((option) => option.value === defaultWeekLabel)) return defaultWeekLabel;
    return weekOptions[0]?.value || '';
  }, [values.week, defaultWeekLabel, weekOptions]);

  const config = useMemo(() => resolveJournalFormConfig(baseConfig, role), [baseConfig, role]);

  const resolvedTopicOptions = useMemo(() => {
    if (!isMockInterview) return topicOptions;
    return mockInterviewTopicOptions(
      selectedSession ? [selectedSession] : interviewSessions,
      topicOptions,
    );
  }, [isMockInterview, selectedSession, interviewSessions, topicOptions]);

  if (!config) return null;

  const updateValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Order matters here, and it used to be wrong.
  //
  // The chain ran: upload -> clinic record -> LLM understanding review ->
  // JOURNAL ENTRY -> GitHub push, all-or-nothing. The learner's actual written
  // work was written fourth, so any failure in the three steps before it threw
  // away the thing they had just spent ten minutes typing — and the journal
  // index showed an empty week even though they had done the work. That is the
  // "Failed to fetch" report and the sparse journal: one defect, two symptoms.
  //
  // Now the journal entry is written FIRST and everything after it is
  // best-effort. Grading and the GitHub push are valuable, but neither is worth
  // losing a learner's reflection over, and both can be retried later.
  const handleSave = async () => {
    if (!trackerId) {
      setStatus('Choose a cohort with a study plan before saving to a repo journal.');
      return;
    }
    const hasContent = config.fields.some(([key]) => String(values[key] || '').trim());
    if (!hasContent && !artifactFile) {
      setStatus('Add a note or attach a file before saving.');
      return;
    }
    setSaving(true);
    setStatus('');
    const weekLabel = String(values.week || defaultWeekLabel || 'Week 1: Cohort evidence').trim();
    let nextValues = isMockInterview
      ? {
        ...values,
        role,
        role_label: config.roleLabel,
        session_label: selectedSession ? describeInterviewSession(selectedSession) : '',
      }
      : values;

    try {
      // Step 1 - the artifact link has to exist before the entry is composed,
      // so this genuinely must come first. If it fails, say so and stop:
      // saving an entry that references a file which was never uploaded would
      // be worse than not saving.
      if (artifactFile) {
        const uploaded = await uploadArtifact(trackerId, {
          file: artifactFile,
          path: artifactFolderFor(config, weekLabel, cohortKey),
        });
        const artifactLine = `[${uploaded.path}](${uploaded.html_url})`;
        nextValues = {
          ...nextValues,
          evidence: [nextValues.evidence, artifactLine].filter(Boolean).join('\n'),
        };
      }

      // Step 2 - the learner's work. Everything else is optional; this is not.
      await appendCohortJournalEntry(
        trackerId,
        formatCohortJournalEntry(config, nextValues, cohortKey, defaultWeekLabel),
      );
    } catch (err) {
      setStatus(err.message || 'Could not save this journal note. Your draft has been kept — try again.');
      setSaving(false);
      return;
    }

    // From here the entry is safely stored server-side. Clear the form and the
    // draft, then report anything that failed afterwards as a warning rather
    // than as a failed save, because the work is already safe.
    setValues(emptyCohortJournalForm());
    setArtifactFile(null);
    clearDraft();

    const warnings = [];
    let understandingReview = null;

    if (type === 'daily_clinic' || type === 'show_and_tell' || type === 'mock_interview') {
      try {
        let recordId;
        if (type === 'daily_clinic') {
          const clinicRecord = await createClinicRecord({
            cohort_key: cohortKey,
            clinic_key: repoPathSegment(`${nextValues.date || values.date}-${nextValues.topic || 'daily-clinic'}`, 'daily-clinic'),
            clinic_date: nextValues.date || values.date,
            topic: nextValues.topic,
            concept: nextValues.covered,
            takeaway: nextValues.takeaway,
            question: nextValues.question,
            action: nextValues.next,
          });
          recordId = clinicRecord?.id;
        }
        understandingReview = await appendUnderstandingReview({
          activityType: type,
          values: nextValues,
          cohortKey,
          recordId,
        });
      } catch (err) {
        warnings.push('understanding review could not be scored');
        if (process.env.NODE_ENV !== 'production') console.warn('understanding review failed', err);
      }
    }

    let pushed = null;
    try {
      pushed = await pushJournalToGitHub(trackerId);
    } catch (err) {
      warnings.push('the GitHub push did not complete — an admin can re-push, your note is saved either way');
      if (process.env.NODE_ENV !== 'production') console.warn('github push failed', err);
    }

    setLastReview(understandingReview);
    const scored = understandingReview ? ` Understanding scored ${understandingReview.percent}%.` : '';
    const commit = pushed?.commit_sha ? ` (${pushed.commit_sha})` : '';
    setStatus(
      warnings.length
        ? `Saved to your journal.${scored} Note: ${warnings.join('; ')}.`
        : `Saved, pushed to GitHub${commit}.${scored}`
    );
    setSaving(false);
  };

  // The learner-typed topic (Clinic topic / Task or topic / Hackathon or
  // week / Demo topic / Interview topic) is what actually titles this entry
  // in the rendered journal (journal_render.py's render_week_file uses it as
  // each entry's ### heading) — "Week heading" only groups entries under a
  // week bucket and is never shown as a title. Rendering topic first, and
  // "Cohort week" last and de-emphasized, keeps that distinction visible in
  // the form instead of implying the week field is the entry's name.
  const journalTopicField = config.fields.find(([key]) => key === 'topic');
  const journalOtherFields = config.fields.filter(([key]) => key !== 'topic');

  // Says where the options actually came from, including the one case a
  // learner will otherwise read as a bug: a candidate cannot see the cycle's
  // question list until the interview is marked complete, by design.
  const topicHelperText = (() => {
    if (isMockInterview) {
      const hasQuestions = resolvedTopicOptions.some((option) => /questions asked/.test(option.group || ''));
      if (hasQuestions) {
        return "Pick the exact question from the posted cycle, or type the one you were asked. This titles today's journal entry.";
      }
      if (selectedSession && !selectedSession.revealed && role === 'candidate') {
        return "The cycle's question list stays hidden until the interview is marked complete — type the question you were asked. This titles today's journal entry.";
      }
      return "Type the exact question or topic covered. This titles today's journal entry.";
    }
    if (resolvedTopicOptions.length > 0) {
      return "Choose from available cohort topics. This titles today's entry in your journal.";
    }
    return "This titles today's entry in your journal.";
  })();

  const renderJournalField = ([key, label]) => (
    <Box key={key}>
      {key === 'evidence' && evidenceOptions.length > 0 ? (
        <Autocomplete
          freeSolo
          options={evidenceOptions}
          inputValue={values[key]}
          onInputChange={(_, next) => updateValue(key, next)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              fullWidth
              multiline
              minRows={2}
              size="small"
              helperText="Pick a graded take-home to auto-fill the score, or type your own evidence."
            />
          )}
        />
      ) : key === 'next' ? (
        <Autocomplete
          freeSolo
          options={NEXT_FIELD_SUGGESTIONS}
          inputValue={values[key]}
          onInputChange={(_, next) => updateValue(key, next)}
          renderInput={(params) => (
            <TextField {...params} label={label} fullWidth multiline minRows={2} size="small" />
          )}
        />
      ) : key === 'topic' && resolvedTopicOptions.length > 0 ? (
        <Autocomplete
          freeSolo
          options={resolvedTopicOptions}
          groupBy={isMockInterview ? (option) => option.group || 'General practice' : undefined}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value || '')}
          inputValue={values[key]}
          onInputChange={(_, next) => updateValue(key, next)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              fullWidth
              size="small"
              helperText={topicHelperText}
            />
          )}
        />
      ) : (
        <TextField
          label={label}
          value={values[key]}
          onChange={(e) => updateValue(key, e.target.value)}
          fullWidth
          multiline={key !== 'topic'}
          minRows={key === 'topic' ? 1 : 2}
          size="small"
          helperText={key === 'topic' ? topicHelperText : undefined}
        />
      )}
      {key === 'evidence' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" rowGap={1}>
          <Button variant="outlined" component="label" size="small">
            Upload evidence file
            <input
              hidden
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.xlsx,.xls,.csv,.txt,.md,.sql,.py,.ipynb,.pbix"
              onChange={(e) => setArtifactFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            {artifactFile
              ? `${artifactFile.name} -> ${artifactFolderFor(config, values.week || defaultWeekLabel, cohortKey)}`
              : 'Uploads to the right repo evidence folder, then adds the link here.'}
          </Typography>
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, position: { md: 'sticky' }, top: { md: 88 } }}
      >
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>{config.title}</Typography>
            <Typography variant="caption" color="text.secondary">{config.description}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
            <Button variant="contained" size="small" onClick={() => setOpen(true)}>
              Log {config.section}
            </Button>
            {lastReview && (
              <Chip
                size="small"
                color={lastReview.percent >= 70 ? 'success' : 'warning'}
                label={`Last score: ${lastReview.percent}%`}
              />
            )}
          </Stack>
          {status && (
            <Typography variant="caption" color={status.startsWith('Saved') ? 'success.main' : 'text.secondary'}>
              {status}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {config.title}
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">{config.description}</Typography>
            <TextField
              label="Date"
              type="date"
              value={values.date}
              onChange={(e) => updateValue('date', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 190 }}
            />
            {isMockInterview && (
              <>
                <TextField
                  select
                  label="Interview session"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  size="small"
                  fullWidth
                  helperText={
                    interviewSessions.length > 0
                      ? 'Which posted cycle this note belongs to. Picking it pulls in that interview’s real topics and questions.'
                      : 'No mock interview cycle has been posted for this cohort yet — log a practice session below.'
                  }
                >
                  <MenuItem value="">Not linked to a posted cycle</MenuItem>
                  {interviewSessions.map((session) => (
                    <MenuItem key={session.id} value={String(session.id)}>
                      {describeInterviewSession(session)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Your role in this interview"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  size="small"
                  fullWidth
                  helperText={`${mockInterviewRoleForm(role).helper} The fields below change to match.`}
                >
                  {MOCK_INTERVIEW_ROLE_FORMS.map((entry) => (
                    <MenuItem key={entry.key} value={entry.key}>{entry.label}</MenuItem>
                  ))}
                </TextField>
              </>
            )}
            {journalTopicField && renderJournalField(journalTopicField)}
            {journalOtherFields.map((field) => renderJournalField(field))}
            {lastReview && (
              <PracticeGradeResult
                grade={{
                  score: lastReview.score,
                  max_score: lastReview.max,
                  grade_result: {
                    percent: lastReview.percent,
                    lensResults: lastReview.lenses,
                    feedback: lastReview.ai_summary,
                    mode: lastReview.mode,
                  },
                }}
              />
            )}
            <TextField
              select
              label="Cohort week"
              value={resolvedWeekValue}
              onChange={(e) => updateValue('week', e.target.value)}
              size="small"
              fullWidth
              sx={{ opacity: 0.75 }}
              helperText={`Groups this entry under its week in your journal — the topic above is what actually titles it. Defaults to ${defaultWeekLabel || 'the current week'}.`}
            >
              {weekOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {status && (
            <Typography
              variant="body2"
              color={
                status.includes('Note:') ? 'warning.main'
                  : status.startsWith('Saved') ? 'success.main'
                    : 'error.main'
              }
              sx={{ mr: 'auto' }}
            >
              {status}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !trackerId}
          >
            {saving ? 'Saving...' : 'Save to repo journal'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── What each learner is actually doing ──────────────────────────────────────
//
// The rest of this page reports bands, medians and a funnel. Useful for
// steering a cohort, useless for the only question asked on a Monday morning:
// what is this person doing, and do they need me this week? These are plain
// statements computed server-side (repositories.build_learner_insights) from
// every clinic reflection a learner has written — each one carries a number or
// a quotation from their own words, so nothing here needs interpreting.

const INSIGHT_TONES = {
  good: { color: 'success.main', mark: '\u25CF' },
  watch: { color: 'warning.main', mark: '\u25CF' },
  risk: { color: 'error.main', mark: '\u25CF' },
  neutral: { color: 'text.disabled', mark: '\u25CF' },
};

const LearnerReadout = ({ rows = [] }) => {
  const theme = useTheme();
  const withInsight = rows.filter((row) => (row.insights || []).length);
  if (!withInsight.length) return null;

  // Anyone carrying a risk note first — that is the queue for the week.
  const ordered = [...withInsight].sort((a, b) => {
    const risk = (row) => ((row.insights || []).some((i) => i.tone === 'risk') ? 0 : 1);
    return risk(a) - risk(b) || String(a.user?.first_name || '').localeCompare(String(b.user?.first_name || ''));
  });

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={700}>What each learner is actually doing</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Written from every clinic reflection on file. Each line is a fact with a number or a quote behind it.
        </Typography>
        <Stack spacing={2}>
          {ordered.map((row) => {
            const name = [row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') || row.user?.email || 'Learner';
            const needsAttention = (row.insights || []).some((i) => i.tone === 'risk');
            return (
              <Box
                key={row.user?.id || name}
                sx={{
                  pl: 1.5,
                  borderLeft: `3px solid ${needsAttention ? theme.palette.error.main : theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>{name}</Typography>
                <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }} spacing={0.4}>
                  {(row.insights || []).map((insight, index) => {
                    const tone = INSIGHT_TONES[insight.tone] || INSIGHT_TONES.neutral;
                    return (
                      <Box component="li" key={index} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                        <Box component="span" sx={{ color: tone.color, fontSize: '0.6rem', lineHeight: 1.9 }}>
                          {tone.mark}
                        </Box>
                        <Typography variant="body2" color="text.primary">{insight.text}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

// ── WeekCard (grid tile) ──────────────────────────────────────────────────────

const WeekCard = ({ week, onClick }) => {
  const theme = useTheme();
  const [setupOpen, setSetupOpen] = useState(false);
  const scheduled = formatScheduled(week.scheduled_at);
  const total = week.total_roles || 0;
  const merged = week.merged_count || 0;
  const progress = total > 0 ? Math.round((merged / total) * 100) : 0;
  const owners = (week.role_owners || []).filter(Boolean);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5, border: 1, borderColor: 'divider', borderRadius: 2,
        cursor: 'pointer', height: '100%',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="caption" sx={{
            fontWeight: 700, fontSize: '0.68rem', letterSpacing: 0.8,
            textTransform: 'uppercase', color: 'text.disabled',
          }}>
            Week {week.week_number}
          </Typography>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.3, mt: 0.25 }}>
            {week.problem_title || `Week ${week.week_number} Hackathon`}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={WEEK_STATUS_LABEL[week.status] || week.status}
          color={WEEK_STATUS_COLOR[week.status] || 'default'}
          sx={{ ml: 1, flexShrink: 0 }}
        />
      </Stack>

      {/* Scheduled date */}
      {scheduled ? (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
          <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">{scheduled}</Typography>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5, display: 'block' }}>
          No date set
        </Typography>
      )}

      {/* Progress */}
      {total > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress
            variant="determinate" value={progress} color="success"
            sx={{ height: 5, borderRadius: 2, mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            {merged}/{total} roles merged
          </Typography>
        </Box>
      )}

      {/* Role owner initials */}
      {owners.length > 0 && (
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <AvatarGroup
            max={5}
            sx={{
              justifyContent: 'flex-start',
              '& .MuiAvatar-root': {
                width: 28, height: 28, fontSize: '0.65rem', fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.dark',
                border: `2px solid ${theme.palette.background.paper}`,
              },
            }}
          >
            {owners.map((name, i) => (
              <Tooltip key={i} title={name}>
                <Avatar>{getInitials(name)}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
      )}

      {week.status === 'locked' && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: owners.length ? 1 : 'auto', pt: 0.5 }}>
          <LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">Locked for learners</Typography>
        </Stack>
      )}

      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          mt: owners.length || week.status === 'locked' ? 1.5 : 'auto',
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Before this hackathon
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Setup PR first. During the hackathon: pull, work, commit, pull, push, PR.
            </Typography>
          </Box>
          <Tooltip title={setupOpen ? 'Hide workflow guard' : 'Show workflow guard'}>
            <IconButton
              size="small"
              aria-label={setupOpen ? 'Hide hackathon workflow guard' : 'Show hackathon workflow guard'}
              onClick={() => setSetupOpen((v) => !v)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {setupOpen && (
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
              Quick vocabulary before the commands below: a{' '}
              <GlossaryTerm term="branch">branch</GlossaryTerm> is a safe copy of the project to work in,{' '}
              <GlossaryTerm term="clone">cloning</GlossaryTerm> downloads the repo to your computer, and a{' '}
              <GlossaryTerm term="pull request">pull request (PR)</GlossaryTerm> asks for your work to be reviewed
              before it joins the shared project.
            </Typography>
            <Stack spacing={1.5}>
              {HACKATHON_WORKFLOW_SECTIONS.map((section) => (
                <Box key={section.title}>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', display: 'block' }}>
                    {section.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block', mb: 0.75 }}>
                    {section.description}
                  </Typography>
                  <Stack component="ol" spacing={0.85} sx={{ pl: 2.5, m: 0 }}>
                    {section.steps.map((step) => (
                      <Box component="li" key={step.label}>
                        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                          {step.detail}
                        </Typography>
                        {step.command && (
                          <CopyableCommandBlock value={step.command} fontSize="0.68rem" lineHeight={1.45} padding={0.75} marginTop={0.5} />
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ── GenerateDialog ────────────────────────────────────────────────────────────

const GenerateDialog = ({ open, onClose, cohortKey, weekNumber, onGenerated }) => {
  const [trackSlug, setTrackSlug] = useState('data-analyst-job-ready');
  const [curriculumWeek, setCurriculumWeek] = useState(weekNumber || 1);
  const [teamSize, setTeamSize] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setCurriculumWeek(weekNumber || 1);
  }, [open, weekNumber]);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await generateCohortWeek(cohortKey, weekNumber, trackSlug, curriculumWeek, teamSize);
      onGenerated(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Generation failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Week {weekNumber} from curriculum</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Auto-generate the hackathon from a curriculum track. Creates a real-world ticket,
            problem brief, and SOLVE-grounded roles split across the team. The week starts
            locked — review and unlock when ready.
          </Typography>
          <TextField
            select label="Learning track" value={trackSlug}
            onChange={(e) => setTrackSlug(e.target.value)} fullWidth
          >
            {TRACK_OPTIONS.map((t) => (
              <MenuItem key={t.slug} value={t.slug}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Curriculum week" type="number" value={curriculumWeek}
            onChange={(e) => setCurriculumWeek(Number(e.target.value))}
            inputProps={{ min: 1, max: 30 }}
            helperText="Which week in the track to use as the source"
            fullWidth
          />
          <TextField
            select label="Team size" value={teamSize}
            onChange={(e) => setTeamSize(Number(e.target.value))} fullWidth
            helperText="How many learners the roles get split across"
          >
            {TEAM_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n} members</MenuItem>
            ))}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          onClick={handleGenerate} disabled={loading}
        >
          {loading ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WeeklyAvailabilityPanel = ({
  week,
  cohortKey,
  weekNumber,
  activityKey = 'clinic',
  isAdmin,
  currentUser,
  members = [],
  onWeekUpdated,
  onUpdateWeekField,
  onUpdateActivityPoll,
}) => {
  const config = SCHEDULE_ACTIVITY_CONFIG[activityKey] || SCHEDULE_ACTIVITY_CONFIG.clinic;
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [slotsText, setSlotsText] = useState('');
  const [activeDay, setActiveDay] = useState('Monday');
  const [showCustomSlots, setShowCustomSlots] = useState(false);
  const [statusText, setStatusText] = useState('');
  const activityPoll = activityKey === 'clinic'
    ? {
      slots: week.availability_slots,
      responses: week.availability_responses,
      selected_slot: week.clinic_selected_slot,
    }
    : (week.schedule_polls || {})[activityKey] || {};
  const slots = (activityPoll.slots || []).length ? activityPoll.slots : config.defaultSlots;
  const responses = useMemo(() => activityPoll.responses || {}, [activityPoll.responses]);
  const currentResponse = currentUser?.id ? responses[currentUser.id] : null;
  const selectedSlot = activityPoll.selected_slot || '';
  const coordinatorId = week.schedule_coordinator_user_id || week.clinic_coordinator_user_id || '';
  const coordinatorName = week.schedule_coordinator_name || week.clinic_coordinator_name || members.find((member) => member.id === coordinatorId)?.label || '';
  const canConfirm = isAdmin || (coordinatorId && coordinatorId === currentUser?.id);

  useEffect(() => {
    setSelectedSlots(currentResponse?.slots || []);
  }, [currentResponse]);

  useEffect(() => {
    setSlotsText(slots.join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week.id, activityKey]);

  const dayGroups = useMemo(() => {
    const rows = slots.map((slot) => {
      const voters = Object.values(responses).filter((response) => (response.slots || []).includes(slot));
      return {
        slot,
        day: availabilityDayForSlot(slot),
        time: availabilityTimeForSlot(slot),
        count: voters.length,
        voters: voters.map((response) => response.name || response.email).filter(Boolean),
      };
    }).sort(compareAvailabilityRows);
    const grouped = rows.reduce((acc, row) => {
      const existing = acc.find((group) => group.day === row.day);
      if (existing) {
        existing.rows.push(row);
      } else {
        acc.push({ day: row.day, rows: [row] });
      }
      return acc;
    }, []);
    return grouped.map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => b.count - a.count || compareAvailabilityRows(a, b)),
    }));
  }, [responses, slots]);
  const activeDayRows = useMemo(() => {
    const activeGroup = dayGroups.find((group) => group.day === activeDay) || dayGroups[0];
    return activeGroup ? [...activeGroup.rows].sort(compareAvailabilityRows) : [];
  }, [activeDay, dayGroups]);
  const missingCount = Math.max((members || []).length - Object.keys(responses).length, 0);

  const toggleSlot = (slot) => {
    setSelectedSlots((current) => (
      current.includes(slot)
        ? current.filter((item) => item !== slot)
        : config.maxSelections && current.length >= config.maxSelections
          ? [...current.slice(1), slot]
          : [...current, slot]
    ));
    setStatusText('');
  };

  const saveAvailability = async () => {
    setStatusText('Saving...');
    try {
      const updated = activityKey === 'clinic'
        ? await saveCohortAvailability(cohortKey, weekNumber, selectedSlots)
        : await saveCohortScheduleAvailability(cohortKey, weekNumber, activityKey, selectedSlots);
      onWeekUpdated(updated);
      setStatusText('Saved');
    } catch (error) {
      setStatusText(error.message || 'Could not save availability');
    }
  };

  const applySlotsText = () => {
    const nextSlots = slotsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (activityKey === 'clinic') {
      onUpdateWeekField('availability_slots', nextSlots);
    } else {
      onUpdateActivityPoll(activityKey, { slots: nextSlots });
    }
    setStatusText('Save week to publish slots');
  };

  const confirmSlot = async (slot) => {
    if (!slot) return;
    setStatusText('Confirming...');
    try {
      const updated = activityKey === 'clinic'
        ? await selectCohortAvailabilitySlot(cohortKey, weekNumber, slot)
        : await selectCohortScheduleSlot(cohortKey, weekNumber, activityKey, slot);
      onWeekUpdated(updated);
      setStatusText(`${config.label} slot confirmed`);
    } catch (error) {
      setStatusText(error.message || 'Could not confirm slot');
    }
  };

  return (
    <Paper elevation={0} sx={{ mb: 2.5, p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>Weekly availability</Typography>
            <Typography variant="body2" color="text.secondary">
              {config.helper}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
            <Chip size="small" label={`${Object.keys(responses).length} responded`} color={Object.keys(responses).length ? 'success' : 'default'} variant="outlined" />
            {missingCount > 0 && <Chip size="small" label={`${missingCount} pending`} color="warning" variant="outlined" />}
            {coordinatorName && <Chip size="small" label={`Coordinator: ${coordinatorName}`} color={coordinatorId === currentUser?.id ? 'secondary' : 'default'} variant="outlined" />}
            {selectedSlot && <Chip size="small" label={`Final: ${selectedSlot}`} color="primary" />}
          </Stack>
        </Stack>

        {isAdmin && (
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <TextField
                select
                label="Global schedule coordinator"
                value={coordinatorId}
                onChange={(event) => onUpdateWeekField('schedule_coordinator_user_id', event.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Not assigned</MenuItem>
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>{member.label || member.email}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={7}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSlotsText(config.defaultSlots.join('\n'));
                  if (activityKey === 'clinic') {
                    onUpdateWeekField('availability_slots', config.defaultSlots);
                  } else {
                    onUpdateActivityPoll(activityKey, { slots: config.defaultSlots });
                  }
                  setStatusText('Save week to publish hourly slots');
                }}
                sx={{ mt: 1, ml: { xs: 0, sm: 1 } }}
              >
                Use hourly slots
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowCustomSlots((current) => !current)}
                sx={{ mt: 1, ml: 1 }}
              >
                Custom slots
              </Button>
              {showCustomSlots && (
                <Box sx={{ mt: 1.25 }}>
                  <TextField
                    label={config.slotsFieldLabel}
                    value={slotsText}
                    onChange={(event) => setSlotsText(event.target.value)}
                    multiline
                    minRows={4}
                    fullWidth
                    size="small"
                  />
                  <Button size="small" variant="outlined" onClick={applySlotsText} sx={{ mt: 1 }}>
                    Apply custom slots
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>My availability</Typography>
            {config.maxSelections > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Choose up to {config.maxSelections} options.
              </Typography>
            )}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75} sx={{ mb: 1 }}>
              {dayGroups.map((group) => (
                <Button
                  key={group.day}
                  size="small"
                  variant={group.day === activeDay ? 'contained' : 'outlined'}
                  onClick={() => setActiveDay(group.day)}
                  sx={{ minWidth: 0 }}
                >
                  {group.day.slice(0, 3)}
                </Button>
              ))}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 0.75,
              }}
            >
              {activeDayRows.map((row) => {
                const checked = selectedSlots.includes(row.slot);
                return (
                  <Button
                    key={row.slot}
                    size="small"
                    variant={checked ? 'contained' : 'outlined'}
                    color={checked ? 'primary' : 'inherit'}
                    onClick={() => toggleSlot(row.slot)}
                    sx={{ justifyContent: 'space-between', minHeight: 36, px: 1, textTransform: 'none' }}
                  >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.time || row.slot}
                    </Box>
                    {row.count > 0 && (
                      <Chip
                        size="small"
                        label={row.count}
                        color={checked ? 'default' : 'success'}
                        sx={{ ml: 0.75, height: 20, minWidth: 20 }}
                      />
                    )}
                  </Button>
                );
              })}
            </Box>
            <Button variant="contained" size="small" onClick={saveAvailability} sx={{ mt: 1.25 }}>
              Save availability
            </Button>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Best slots by day</Typography>
            <Stack spacing={1.25}>
              {dayGroups.map((group) => {
                const bestForDay = group.rows.find((row) => row.count > 0);
                return (
                  <Paper key={group.day} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography variant="subtitle2" fontWeight={800}>{group.day}</Typography>
                      {bestForDay && canConfirm && (
                        <Button size="small" variant="outlined" onClick={() => confirmSlot(bestForDay.slot)}>
                          Confirm {group.day}
                        </Button>
                      )}
                    </Stack>
                    <Stack spacing={0.75}>
                      {group.rows.map((row, index) => (
                        <Box key={row.slot} sx={{ p: 1, border: 1, borderColor: row.slot === selectedSlot ? 'primary.main' : 'divider', borderRadius: 1 }}>
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" fontWeight={700}>{row.time || row.slot}</Typography>
                              {index === 0 && row.count > 0 && <Chip size="small" label="Best" color="primary" variant="outlined" />}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={row.count} color={row.count ? 'success' : 'default'} variant="outlined" />
                              {canConfirm && (
                                <Button size="small" onClick={() => confirmSlot(row.slot)} disabled={!row.count}>
                                  Select
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                          {row.voters.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {row.voters.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
        {statusText && <Typography variant="body2" color={statusText === 'Saved' || statusText.includes('confirmed') ? 'success.main' : 'text.secondary'}>{statusText}</Typography>}
      </Stack>
    </Paper>
  );
};

const ScheduleAvailabilityView = ({
  cohortKey,
  weeks,
  isAdmin,
  currentUser,
  nextWeekNumber,
  onWeekSaved,
  onAddWeek,
}) => {
  const [scheduleTab, setScheduleTab] = useState('clinic');
  const scheduleConfig = SCHEDULE_ACTIVITY_CONFIG[scheduleTab] || SCHEDULE_ACTIVITY_CONFIG.clinic;
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    if (!selectedWeek && weeks.length > 0) setSelectedWeek(weeks[0].week_number);
  }, [selectedWeek, weeks]);

  useEffect(() => {
    if (!cohortKey || !selectedWeek) return;
    setLoading(true);
    setError('');
    setSaveStatus('');
    Promise.all([
      fetchCohortWeek(cohortKey, selectedWeek),
      isAdmin ? fetchCohortMembers(cohortKey).catch(() => []) : Promise.resolve([]),
    ])
      .then(([week, memberList]) => {
        setWeekData(week);
        setMembers(memberList || []);
      })
      .catch((err) => setError(err.message || 'Could not load schedule.'))
      .finally(() => setLoading(false));
  }, [cohortKey, selectedWeek, isAdmin]);

  const handleWeekUpdated = (updatedWeek) => {
    setWeekData(updatedWeek);
    onWeekSaved(weekToSummary(updatedWeek));
  };

  const updateWeekField = (field, value) => {
    setWeekData((prev) => ({ ...prev, [field]: value }));
    setSaveStatus('');
  };

  const updateActivityPoll = (activityKey, patch) => {
    setWeekData((prev) => {
      const polls = { ...(prev.schedule_polls || {}) };
      polls[activityKey] = { ...(polls[activityKey] || {}), ...patch };
      return { ...prev, schedule_polls: polls };
    });
    setSaveStatus('');
  };

  const saveScheduleSettings = async () => {
    if (!weekData || !selectedWeek) return;
    setSaving(true);
    setSaveStatus('');
    try {
      let coordinatorPatch = {};
      if (weekData.schedule_coordinator_user_id !== undefined) {
        coordinatorPatch = await saveCohortScheduleCoordinator(cohortKey, weekData.schedule_coordinator_user_id);
      }
      const saved = await saveCohortWeek(cohortKey, selectedWeek, {
        ...weekData,
        availability_slots: (weekData.availability_slots || []).length
          ? weekData.availability_slots
          : DEFAULT_AVAILABILITY_SLOTS,
      });
      handleWeekUpdated({ ...saved, ...coordinatorPatch });
      setSaveStatus('Schedule settings saved');
    } catch (err) {
      setSaveStatus(err.message || 'Could not save schedule settings');
    } finally {
      setSaving(false);
    }
  };

  const addScheduleWeek = async () => {
    if (!onAddWeek) return;
    setSaving(true);
    setSaveStatus('');
    setError('');
    try {
      const saved = await onAddWeek();
      if (saved?.week_number) {
        setSelectedWeek(saved.week_number);
        setWeekData(saved);
      }
      setSaveStatus('Schedule week added');
    } catch (err) {
      setError(err.message || 'Could not add schedule week.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight={800}>Cohort schedule</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Plan the four recurring cohort rhythms: clinic, hackathon, Show & Tell, and mock interviews.
            </Typography>
          </Box>
          {isAdmin && (
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addScheduleWeek} disabled={saving}>
              Add week {nextWeekNumber}
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Tabs
          value={scheduleTab}
          onChange={(_, value) => setScheduleTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Schedule activity tabs"
          sx={{
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 52,
            '& .MuiTab-root': { minHeight: 52, textTransform: 'none', fontWeight: 800 },
          }}
        >
          {SCHEDULE_ACTIVITY_TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={(
                <Stack alignItems="flex-start" spacing={0.1}>
                  <Typography variant="body2" fontWeight={800}>{tab.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{tab.cadence}</Typography>
                </Stack>
              )}
            />
          ))}
        </Tabs>
      </Paper>

      {weeks.length === 0 ? (
        <Alert severity="info">No cohort week is available yet.</Alert>
      ) : (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Stack spacing={1}>
                {weeks.map((week) => {
                  const isSelected = selectedWeek === week.week_number;
                  return (
                    <Button
                      key={week.week_number}
                      fullWidth
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => setSelectedWeek(week.week_number)}
                      sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1 }}
                    >
                      <Stack spacing={0.25} alignItems="flex-start">
                        <Typography variant="body2" fontWeight={800}>Week {week.week_number}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {scheduleTab === 'clinic'
                            ? (week.clinic_selected_slot || `${week.availability_response_count || 0} responses`)
                            : (
                              ((week.schedule_polls || {})[scheduleTab]?.selected_slot)
                              || `${Object.keys(((week.schedule_polls || {})[scheduleTab]?.responses) || {}).length} responses`
                            )}
                        </Typography>
                        {(week.schedule_coordinator_name || week.clinic_coordinator_name) && (
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Coordinator: {week.schedule_coordinator_name || week.clinic_coordinator_name}
                          </Typography>
                        )}
                      </Stack>
                    </Button>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={9}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : weekData ? (
              <Stack spacing={1.5}>
                {(weekData.schedule_coordinator_name || weekData.clinic_coordinator_name) && (
                  <Alert severity={(weekData.schedule_coordinator_user_id || weekData.clinic_coordinator_user_id) === currentUser?.id ? 'info' : 'success'}>
                    Global schedule coordinator: {weekData.schedule_coordinator_name || weekData.clinic_coordinator_name}
                    {(weekData.schedule_coordinator_user_id || weekData.clinic_coordinator_user_id) === currentUser?.id ? ' (you)' : ''}
                  </Alert>
                )}
                {(scheduleTab === 'clinic'
                  ? weekData.clinic_selected_slot
                  : (weekData.schedule_polls || {})[scheduleTab]?.selected_slot) && (
                  <Alert icon={<AccessTimeIcon />} severity="success">
                    {scheduleConfig.selectedLabel}: {scheduleTab === 'clinic'
                      ? weekData.clinic_selected_slot
                      : (weekData.schedule_polls || {})[scheduleTab]?.selected_slot}
                  </Alert>
                )}
                <WeeklyAvailabilityPanel
                  week={weekData}
                  cohortKey={cohortKey}
                  weekNumber={selectedWeek}
                  activityKey={scheduleTab}
                  isAdmin={isAdmin}
                  currentUser={currentUser}
                  members={members}
                  onWeekUpdated={handleWeekUpdated}
                  onUpdateWeekField={updateWeekField}
                  onUpdateActivityPoll={updateActivityPoll}
                />
                {isAdmin && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={saveScheduleSettings}
                      disabled={saving}
                    >
                      Save schedule settings
                    </Button>
                    {saveStatus && (
                      <Typography variant="body2" color={saveStatus.includes('saved') ? 'success.main' : 'text.secondary'}>
                        {saveStatus}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            ) : null}
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

// ── WeekModal ─────────────────────────────────────────────────────────────────

const WeekModal = ({ open, cohortKey, weekNumber, isAdmin, onClose, onSaved }) => {
  const theme = useTheme();
  const [localData, setLocalData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [briefOpen, setBriefOpen] = useState(false);
  const [setupHelpOpen, setSetupHelpOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [statusActivity, setStatusActivity] = useState([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const roleStatusSnapshotRef = useRef({});

  const captureRoleStatusChanges = useCallback((nextWeek, options = {}) => {
    const nextRoles = nextWeek?.roles || [];
    const nextSnapshot = Object.fromEntries(
      nextRoles.map((role) => [role.id, role.status || 'open']),
    );
    const previousSnapshot = roleStatusSnapshotRef.current || {};

    if (!options.silent) {
      const changes = nextRoles
        .map((role) => {
          const previousStatus = previousSnapshot[role.id];
          const nextStatus = role.status || 'open';
          if (!previousStatus || previousStatus === nextStatus) return null;
          return describeRoleStatusChange(role, previousStatus, nextStatus);
        })
        .filter(Boolean);

      if (changes.length > 0) {
        setStatusActivity((prev) => [...changes.reverse(), ...prev].slice(0, 6));
      }
    }

    roleStatusSnapshotRef.current = nextSnapshot;
  }, []);

  useEffect(() => {
    if (!open || !cohortKey || !weekNumber) return;
    setLoading(true);
    setSaveStatus('');
    setBriefOpen(false);
    setSetupHelpOpen(false);
    setStatusActivity([]);
    setLastRefreshedAt(null);
    roleStatusSnapshotRef.current = {};
    Promise.all([
      fetchCohortWeek(cohortKey, weekNumber),
      isAdmin ? fetchCohortMembers(cohortKey).catch(() => []) : Promise.resolve([]),
      isAdmin ? fetchHackathonAttempts(cohortKey, weekNumber).catch(() => []) : Promise.resolve([]),
    ])
      .then(([data, memberList, attemptList]) => {
        setLocalData(data);
        captureRoleStatusChanges(data, { silent: true });
        setLastRefreshedAt(new Date());
        setMembers(memberList || []);
        setAttempts(attemptList || []);
      })
      .catch((err) => setSaveStatus(`Failed to load: ${err.message}`))
      .finally(() => setLoading(false));
  }, [open, cohortKey, weekNumber, isAdmin, captureRoleStatusChanges]);

  useEffect(() => {
    if (!open || !cohortKey || !weekNumber || !localData) return undefined;
    const interval = window.setInterval(async () => {
      try {
        const fresh = await fetchCohortWeek(cohortKey, weekNumber);
        const freshAttempts = isAdmin ? await fetchHackathonAttempts(cohortKey, weekNumber).catch(() => []) : [];
        captureRoleStatusChanges(fresh);
        setLocalData(fresh);
        setAttempts(freshAttempts || []);
        onSaved(weekToSummary(fresh));
        setLastRefreshedAt(new Date());
      } catch {
        // Keep the modal quiet; failed background refresh should not interrupt learners.
      }
    }, 20000);

    return () => window.clearInterval(interval);
  }, [open, cohortKey, weekNumber, localData, isAdmin, captureRoleStatusChanges, onSaved]);

  const handleClose = () => {
    setLocalData(null);
    setMembers([]);
    setAttempts([]);
    setSaveStatus('');
    setStatusActivity([]);
    roleStatusSnapshotRef.current = {};
    onClose();
  };

  const updateField = (field, value) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRole = (roleId, field, value) => {
    setLocalData((prev) => ({
      ...prev,
      roles: (prev.roles || []).map((r) =>
        r.id === roleId ? { ...r, [field]: value } : r,
      ),
    }));
  };

  const handleRolePatch = async (roleId, patch) => {
    const updated = await updateCohortRole(cohortKey, weekNumber, roleId, patch);
    captureRoleStatusChanges(updated);
    setLocalData(updated);
    onSaved(weekToSummary(updated));
    setLastRefreshedAt(new Date());
  };

  const handleSave = async () => {
    if (!localData) return;
    setSaving(true);
    setSaveStatus('');
    try {
      const wasLocked = localData._prevStatus !== undefined
        ? localData._prevStatus === 'locked'
        : false;
      const saved = await saveCohortWeek(cohortKey, weekNumber, localData);
      setLocalData(saved);
      onSaved(weekToSummary(saved));
      const unlocked = saved.status === 'open' && wasLocked;
      const notificationResult = saved._notification_result;
      if (unlocked && notificationResult) {
        const { attempted = 0, sent = 0, failed = 0, skipped = 0 } = notificationResult;
        if (sent > 0 && failed === 0) {
          setSaveStatus(`Week unlocked — notifications sent to ${sent} member${sent > 1 ? 's' : ''}.`);
        } else if (attempted > 0 && failed > 0) {
          console.warn('Cohort notification email delivery failed. Check RESEND_API_KEY, sender domain, and backend logs.');
          setSaveStatus(`Week unlocked, but ${failed} of ${attempted} notification email${attempted > 1 ? 's' : ''} could not be sent — contact your platform admin.`);
        } else if (attempted === 0 && skipped > 0) {
          setSaveStatus('Week unlocked, but no notifications were sent because assigned roles do not have email addresses.');
        } else {
          setSaveStatus('Week unlocked, but no notification emails were sent. Check email configuration.');
        }
      } else {
        setSaveStatus('Saved.');
      }
    } catch (err) {
      setSaveStatus(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = () => {
    if (!localData) return;
    const newStatus = localData.status === 'locked' ? 'open' : 'locked';
    setLocalData((prev) => ({ ...prev, status: newStatus, _prevStatus: prev.status }));
  };

  const describeNotificationResult = (result) => {
    const { attempted = 0, sent = 0, failed = 0, skipped = 0 } = result || {};
    if (sent > 0 && failed === 0) {
      return `Notifications sent to ${sent} member${sent > 1 ? 's' : ''}.`;
    }
    if (attempted > 0 && failed > 0) {
      console.warn('Cohort notification email delivery failed. Check RESEND_API_KEY, sender domain, and backend logs.');
      return `${failed} of ${attempted} notification email${attempted > 1 ? 's' : ''} could not be sent — contact your platform admin.`;
    }
    if (attempted === 0 && skipped > 0) {
      return 'No notifications were sent because assigned roles do not have email addresses.';
    }
    return 'No notification emails were sent. Check email configuration.';
  };

  const handleNotifyRoles = async () => {
    if (!localData) return;
    setSaving(true);
    setSaveStatus('');
    try {
      const result = await notifyCohortWeekRoles(cohortKey, weekNumber);
      setSaveStatus(describeNotificationResult(result));
    } catch (err) {
      setSaveStatus(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '92vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" rowGap={0.5}>
                <Typography variant="caption" sx={{
                  fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                  letterSpacing: 0.8, color: 'text.disabled',
                }}>
                  Week {weekNumber}
                </Typography>
                {cohortKey && (
                  <Chip
                    size="small"
                    label={displayCohortName(cohortKey)}
                    variant="outlined"
                    sx={{ fontSize: '0.68rem', height: 20 }}
                  />
                )}
                {localData && (
                  <Chip
                    size="small"
                    label={WEEK_STATUS_LABEL[localData.status] || localData.status}
                    color={WEEK_STATUS_COLOR[localData.status] || 'default'}
                  />
                )}
                {localData?.track_slug && (
                  <Chip size="small" label={localData.track_slug.replace(/-/g, ' ')} variant="outlined" />
                )}
                {localData?.team_size && (
                  <Chip size="small" icon={<GroupsIcon sx={{ fontSize: 14 }} />} label={`${localData.team_size} members`} variant="outlined" />
                )}
              </Stack>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                {localData?.problem_title || `Week ${weekNumber} Hackathon`}
              </Typography>
              {localData?.scheduled_at && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                  <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatScheduled(localData.scheduled_at)}
                  </Typography>
                </Stack>
              )}
            </Box>
            <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: { xs: 2, md: 3 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : localData ? (
            <>
            {(statusActivity.length > 0 || lastRefreshedAt) && (
              <Paper elevation={0} sx={{
                mb: 2.5,
                p: { xs: 1.5, sm: 2 },
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.18),
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.035),
              }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: statusActivity.length > 0 ? 1.25 : 0 }}
                >
                  <Typography variant="caption" sx={{
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'primary.main',
                  }}>
                    Live status
                  </Typography>
                  {lastRefreshedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Auto-refreshing every 20s · last checked {formatActivityTime(lastRefreshedAt)}
                    </Typography>
                  )}
                </Stack>
                {statusActivity.length > 0 ? (
                  <Stack spacing={0.75}>
                    {statusActivity.map((item) => (
                      <Stack key={item.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.owner || item.roleTitle}
                        </Typography>
                        {item.owner && (
                          <Typography variant="body2" color="text.secondary">
                            on {item.roleTitle}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={STATUS_LABEL[item.previousStatus] || item.previousStatus}
                          color={STATUS_COLOR[item.previousStatus] || 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.68rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">→</Typography>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[item.nextStatus] || item.nextStatus}
                          color={STATUS_COLOR[item.nextStatus] || 'default'}
                          sx={{ height: 20, fontSize: '0.68rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatActivityTime(item.time)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    You will see teammate status changes here while this hackathon is open.
                  </Typography>
                )}
              </Paper>
            )}
            {isAdmin ? (
              /* ── Admin: setup config ──────────────────────────────────── */
              <Stack spacing={3}>

                {/* 1. Setup config */}
                <Paper elevation={0} sx={{
                  p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
                    Setup config
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Shared workspace URL"
                        value={localData.shared_repo_url || ''}
                        onChange={(e) => updateField('shared_repo_url', e.target.value)}
                        size="small" fullWidth
                        placeholder="https://1drv.ms/x/... or GitHub repo URL"
                        helperText="Excel Online shared link (anyone with link can edit) or GitHub repo — shown as a button to all learners"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Dataset URL"
                        value={localData.dataset_url || ''}
                        onChange={(e) => updateField('dataset_url', e.target.value)}
                        size="small" fullWidth
                        placeholder="Leave blank to use the platform-hosted dataset"
                        helperText="Pre-filled with the hosted DA-117 dataset. Override with any public URL for future hackathons."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="datetime-local"
                        label="Scheduled date & time"
                        value={toDatetimeLocal(localData.scheduled_at)}
                        onChange={(e) => updateField('scheduled_at', e.target.value || null)}
                        size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Shown on the week card and in notification emails"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Problem title"
                        value={localData.problem_title || ''}
                        onChange={(e) => updateField('problem_title', e.target.value)}
                        size="small" fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="password"
                        label="GitHub token (optional, this repo only)"
                        value={localData.shared_repo_token || ''}
                        onChange={(e) => updateField('shared_repo_token', e.target.value)}
                        size="small" fullWidth
                        placeholder={localData.has_repo_token ? 'Token set — leave blank to keep it' : 'e.g. github_pat_...'}
                        helperText={
                          localData.has_repo_token
                            ? "A token is already saved for this week's repo. Type a new one to replace it, or leave blank to keep it."
                            : 'Lets feedback pull real PR/commit facts from the GitHub repo above. Falls back to the admin default if left blank.'
                        }
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* 2. Role assignments */}
                {(localData.roles || []).length > 0 && (
                  <Paper elevation={0} sx={{
                    p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
                      Role assignments
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Assign cohort members to roles. Each will receive a notification email when you unlock the week.
                    </Typography>
                    <Box>
                      {localData.roles.map((role) => (
                        <RoleSetupRow
                          key={role.id}
                          role={role}
                          members={members}
                          onUpdate={(field, value) => updateRole(role.id, field, value)}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                <HackathonAttemptMonitor attempts={attempts} roles={localData.roles || []} />

                {/* 3. Ticket content (what learners see) */}
                <Paper elevation={0} sx={{
                  p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.02),
                }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
                    Ticket content
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Scenario context"
                      value={localData.scenario_context || ''}
                      onChange={(e) => updateField('scenario_context', e.target.value)}
                      size="small" fullWidth multiline minRows={2}
                      helperText="Brief context shown above the ticket — visible to learners"
                    />
                    <Box>
                      <Button
                        size="small" variant="text"
                        onClick={() => setBriefOpen((v) => !v)}
                        sx={{ mb: 1, px: 0, fontWeight: 700 }}
                      >
                        {briefOpen ? '▲ Hide' : '▼ Edit'} problem brief
                      </Button>
                      {briefOpen && (
                        <TextField
                          label="Problem brief (Markdown)"
                          value={localData.problem_brief || ''}
                          onChange={(e) => updateField('problem_brief', e.target.value)}
                          fullWidth multiline minRows={8}
                          helperText="Supports Markdown — the full ticket text"
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>

                {/* 4. Reflection */}
                <TextField
                  label="Reflection / closeout notes"
                  value={localData.reflection || ''}
                  onChange={(e) => updateField('reflection', e.target.value)}
                  size="small" fullWidth multiline minRows={3}
                  placeholder="What went well? What would the team do differently? (Post-session — not shown to learners during the hackathon)"
                />

                {saveStatus && (
                  <Alert severity={saveStatus.startsWith('Error') ? 'error' : 'success'}>
                    {saveStatus}
                  </Alert>
                )}

              </Stack>
            ) : (
              /* ── Learner: ticket + their role ─────────────────────────── */
              <Stack spacing={3}>

                {/* Scenario */}
                {localData.scenario_context && (
                  <Box sx={{
                    p: 2.5, borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.15),
                    borderLeft: '4px solid', borderLeftColor: 'primary.main',
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                      letterSpacing: 0.8, color: 'primary.main', display: 'block', mb: 1,
                    }}>
                      Scenario
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                      {localData.scenario_context}
                    </Typography>
                  </Box>
                )}

                {/* Ticket brief (collapsible) */}
                {localData.problem_brief && (
                  <Box>
                    <Button
                      size="small" variant="text"
                      onClick={() => setBriefOpen((v) => !v)}
                      sx={{ mb: 1, px: 0, fontWeight: 700 }}
                    >
                      {briefOpen ? '▲ Hide' : '▼ Show'} the ticket
                    </Button>
                    {briefOpen && (
                      <Paper elevation={0} sx={{
                        p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                        '& h2,& h3': { fontSize: '0.9rem', fontWeight: 700, mt: 2, mb: 0.75 },
                        '& p': { fontSize: '0.85rem', mb: 0.75, lineHeight: 1.65 },
                        '& li': { fontSize: '0.85rem', mb: 0.5, lineHeight: 1.55 },
                        '& strong': { color: 'text.primary' },
                        '& code': { fontSize: '0.78rem', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
                      }}>
                        <MarkdownRenderer content={localData.problem_brief} />
                      </Paper>
                    )}
                  </Box>
                )}

                {/* Workspace + dataset links */}
                {(localData.shared_repo_url || localData.dataset_url) && (
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
                      {localData.shared_repo_url && (
                        <Button
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={localData.shared_repo_url}
                          target="_blank"
                          rel="noreferrer"
                          component="a"
                          sx={{ textTransform: 'none' }}
                        >
                          {describeWorkspaceLink(localData.shared_repo_url)}
                        </Button>
                      )}
                      {localData.dataset_url && (
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          href={localData.dataset_url}
                          download="da-117-q3-sales-raw.csv"
                          component="a"
                          sx={{ textTransform: 'none' }}
                          color="secondary"
                        >
                          Download raw dataset
                        </Button>
                      )}
                      {localData.shared_repo_url && (
                        <Button
                          variant="text"
                          startIcon={<HelpOutlineIcon />}
                          onClick={() => setSetupHelpOpen((v) => !v)}
                          sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
                        >
                          {setupHelpOpen ? 'Hide workflow guard' : 'How do I submit my work?'}
                        </Button>
                      )}
                    </Stack>
                    {localData.shared_repo_url && setupHelpOpen && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          border: '1px solid',
                          borderColor: alpha(theme.palette.info.main, 0.25),
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.info.main, 0.05),
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                          Two-part collaboration workflow
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          First prove your setup with a small <GlossaryTerm term="pull request">PR</GlossaryTerm>.
                          During the hackathon, use the pull-work-commit-pull-push-PR loop to avoid conflicts.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                          Quick vocabulary: a <GlossaryTerm term="branch">branch</GlossaryTerm> is a safe copy of the
                          project to work in, <GlossaryTerm term="clone">cloning</GlossaryTerm> downloads the repo to
                          your computer, and a <GlossaryTerm term="pull request">pull request</GlossaryTerm> asks for
                          your work to be reviewed before it joins the shared project.
                        </Typography>
                        <Stack spacing={2}>
                          {HACKATHON_WORKFLOW_SECTIONS.map((section) => (
                            <Box key={section.title}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'primary.main', mb: 0.5 }}>
                                {section.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                {section.description}
                              </Typography>
                              <Stack component="ol" spacing={1.25} sx={{ pl: 3, mt: 1, mb: 0 }}>
                                {section.steps.map((step) => (
                                  <Box component="li" key={step.label}>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                      {step.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                      {step.detail}
                                    </Typography>
                                    {step.label === 'Open the hackathon workspace and copy the repo URL' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<OpenInNewIcon />}
                                        href={localData.shared_repo_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        component="a"
                                        sx={{ mt: 0.75, textTransform: 'none' }}
                                      >
                                        {describeWorkspaceLink(localData.shared_repo_url)}
                                      </Button>
                                    )}
                                    {step.command && (
                                      <CopyableCommandBlock value={step.command} />
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                )}

                {/* Learner's assigned role(s) */}
                {(localData.roles || []).map((role) => (
                  <LearnerRoleCard
                    key={role.id}
                    role={role}
                    cohortKey={cohortKey}
                    weekNumber={weekNumber}
                    onSavePatch={(patch) => handleRolePatch(role.id, patch)}
                    weekData={localData}
                  />
                ))}

                {/* Reflection (post-session, read-only for learners) */}
                {localData.reflection && (
                  <Box sx={{
                    p: 2.5, borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2),
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                      letterSpacing: 0.8, color: 'success.dark', display: 'block', mb: 1,
                    }}>
                      Closeout reflection
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                      {localData.reflection}
                    </Typography>
                  </Box>
                )}

              </Stack>
            )}
            </>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <WarningIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">Could not load week data.</Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer actions */}
        <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && localData && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={localData.status !== 'locked'}
                    onChange={handleToggleLock}
                    color="success"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600} color={localData.status !== 'locked' ? 'success.main' : 'text.secondary'}>
                    {localData.status !== 'locked' ? 'Open' : 'Locked'}
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              <Button
                size="small" variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setGenerateOpen(true)}
              >
                Re-generate
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
                onClick={handleNotifyRoles}
                disabled={saving}
              >
                Resend role emails
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save week'}
              </Button>
            </>
          )}
          <Button onClick={handleClose} variant={isAdmin ? 'text' : 'outlined'}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Nested generate dialog */}
      {isAdmin && (
        <GenerateDialog
          open={generateOpen}
          onClose={() => setGenerateOpen(false)}
          cohortKey={cohortKey}
          weekNumber={weekNumber}
          onGenerated={(data) => {
            setLocalData(data);
            onSaved(weekToSummary(data));
            setGenerateOpen(false);
          }}
        />
      )}
    </>
  );
};

// ── CohortPilotPage ───────────────────────────────────────────────────────────

const dccTakeHomeStatesForRow = (row) => (
  DCC_TAKE_HOME_PRACTICE_KEYS
    .map((practiceKey) => row?.[practiceKey])
    .filter((state) => state?.grade_summary)
);

const duplicateLensStatus = (grade, lensKey, lensLabel) => {
  const result = grade?.lensResults?.[lensKey];
  if (result) return result;
  if ((grade?.strengths || []).includes(lensLabel)) return { label: lensLabel, status: 'correct', score: 1 };
  if ((grade?.missing || []).includes(lensLabel)) return { label: lensLabel, status: 'missed', score: 0 };
  return { label: lensLabel, status: 'ungraded', score: 0 };
};

const compactFeedbackText = (value, fallback = 'No answer captured') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.length > 150 ? `${text.slice(0, 147)}...` : text;
};

function LearnerProgressSignals({ isAdmin, cohortKey, weeks = [] }) {
  const [state, setState] = useState(null);
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progressTab, setProgressTab] = useState('overview');
  const [selectedDccLearnerId, setSelectedDccLearnerId] = useState('');
  const [selectedDccTakeHome, setSelectedDccTakeHome] = useState('duplicates');
  const [dccLineDetail, setDccLineDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const loader = isAdmin
      ? fetchLearnerSignals({ cohort_key: cohortKey }).then((payload) => {
        if (!cancelled) {
          setSignals(payload || null);
          setState(null);
        }
      })
      : Promise.all([
        loadRemoteProgress(DUPLICATE_PRACTICE_TRACKER_ID).catch(() => null),
        loadRemoteProgress(MISSINGNESS_PRACTICE_TRACKER_ID).catch(() => null),
        loadRemoteProgress(OUTLIER_PRACTICE_TRACKER_ID).catch(() => null),
        loadRemoteProgress(DQ_MATH_05_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_01A_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_01B_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_03_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_03A_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_04_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_05_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_06_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_07_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_08A_TRACKER_ID).catch(() => null),
        loadRemoteProgress(STATS_CLINIC_08B_TRACKER_ID).catch(() => null),
        loadRemoteProgress(EXCEL_ANALYST_CHALLENGE_TRACKER_ID).catch(() => null),
        loadRemoteProgress(CT08_TABLE_DETECTIVE_TRACKER_ID).catch(() => null),
        loadRemoteProgress(SQL02_DATA_MODELLING_TRACKER_ID).catch(() => null),
        loadRemoteProgress(SQL03_DATABASE_FUNDAMENTALS_TRACKER_ID).catch(() => null),
        loadRemoteProgress(SQL04_OLTP_OLAP_TRACKER_ID).catch(() => null),
        loadRemoteProgress(CT10_REQUEST_TO_SPEC_TRACKER_ID).catch(() => null),
        loadRemoteProgress(DM05_CONFIG.trackerId).catch(() => null),
        loadRemoteProgress(DM08_CONFIG.trackerId).catch(() => null),
      ]).then(([duplicateState, missingnessState, outlierState, dqMath05State, stats01aState, stats01bState, stats03State, stats03aState, stats04State, stats05State, stats06State, stats07State, stats08aState, stats08bState, excelAnalystChallengeState, ct08TableDetectiveState, sql02DataModellingState, sql03DatabaseFundamentalsState, sql04OltpOlapState, ct10RequestToSpecState, dm05State, dm08State]) => {
        if (!cancelled) {
          setState({
            duplicates: duplicateState || null,
            missingness: missingnessState || null,
            outliers: outlierState || null,
            dq_math_05: dqMath05State || null,
            statistics_01a: stats01aState || null,
            statistics_01b: stats01bState || null,
            statistics_03: stats03State || null,
            statistics_03a: stats03aState || null,
            statistics_04: stats04State || null,
            statistics_05: stats05State || null,
            statistics_06: stats06State || null,
            statistics_07: stats07State || null,
            statistics_08a: stats08aState || null,
            statistics_08b: stats08bState || null,
            excel_analyst_challenge: excelAnalystChallengeState || null,
            ct_08_table_detective: ct08TableDetectiveState || null,
            sql_02_data_modelling: sql02DataModellingState || null,
            sql_03_database_fundamentals: sql03DatabaseFundamentalsState || null,
            sql_04_oltp_olap: sql04OltpOlapState || null,
            ct_10_request_to_spec: ct10RequestToSpecState || null,
            dm_05_kimball_facts: dm05State || null,
            dm_08_data_vault: dm08State || null,
          });
          setSignals(null);
        }
      });

    loader
      .catch((err) => {
        if (cancelled) return;
        const message = String(err?.message || '');
        if (isAdmin || !message.includes('Unable to load')) {
          setError(err.message || 'Could not load learner progress signals.');
        }
        setState(null);
        setSignals(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAdmin, cohortKey]);

  const selectedLearnerTakeHomeConfig = DCC_TAKE_HOME_CONFIGS[selectedDccTakeHome] || DCC_TAKE_HOME_CONFIGS.duplicates;
  const selectedLearnerState = state?.[selectedDccTakeHome] || null;
  const learnerTakeHomeStates = DCC_TAKE_HOME_TABS
    .map((tab) => ({
      ...tab,
      config: DCC_TAKE_HOME_CONFIGS[tab.key],
      state: state?.[tab.key] || null,
    }))
    .filter((item) => item.state);
  const summary = selectedLearnerState?.grade_summary || null;
  const grades = selectedLearnerState?.grades || {};
  const gradeRows = Object.entries(grades).filter(([, grade]) => grade);
  const cohortRows = useMemo(() => {
    const rows = signals?.learners || [];
    if (!isAdmin) return [];
    return rows;
  }, [signals, isAdmin]);
  useEffect(() => {
    if (!isAdmin || !cohortRows.length) return;
    if (!selectedDccLearnerId || !cohortRows.some((row) => row.user?.id === selectedDccLearnerId)) {
      setSelectedDccLearnerId(cohortRows[0].user?.id || '');
    }
  }, [isAdmin, cohortRows, selectedDccLearnerId]);

  // Derived once, read by every chart on every tab. Two charts disagreeing
  // about the same learner is a class of bug this removes by construction.
  const learnerMetrics = useMemo(
    () => buildLearnerMetrics(cohortRows, DCC_TAKE_HOME_PRACTICE_KEYS),
    [cohortRows]
  );
  const reference = useMemo(() => cohortReference(learnerMetrics), [learnerMetrics]);
  const actionQueue = useMemo(() => buildActionQueue(learnerMetrics), [learnerMetrics]);
  const funnelStages = useMemo(() => buildProgressFunnel(learnerMetrics), [learnerMetrics]);
  const lensMatrix = useMemo(
    () => buildLensMatrix(cohortRows, DCC_TAKE_HOME_CONFIGS[selectedDccTakeHome]),
    [cohortRows, selectedDccTakeHome]
  );

  const cohortMetrics = useMemo(() => {
    const total = cohortRows.length;
    const completedLessons = cohortRows.reduce((sum, row) => sum + Number(row.lessons_completed || 0), 0);
    const startedLessons = cohortRows.reduce((sum, row) => sum + Number(row.lessons_started || 0), 0);
    const journals = cohortRows.reduce((sum, row) => sum + Number(row.journal_count || 0), 0);
    const avgConfidence = total
      ? Math.round(cohortRows.reduce((sum, row) => sum + Number(row.avg_confidence || 0), 0) / total)
      : 0;
    const active = cohortRows.filter((row) => ['active', 'high_signal'].includes(row.status)).length;
    const atRisk = cohortRows.filter((row) => row.status === 'at_risk').length;
    const strong = cohortRows.filter((row) => row.engagement_band === 'strong').length;
    return { total, completedLessons, startedLessons, journals, avgConfidence, active, atRisk, strong };
  }, [cohortRows]);
  const clinicMetrics = useMemo(() => {
    const totalRecords = cohortRows.reduce((sum, row) => sum + (row.concept_clinic_records || []).length, 0);
    const learnersWithClinic = cohortRows.filter((row) => (row.concept_clinic_records || []).length > 0).length;
    const understandingRows = cohortRows.filter((row) => row.dcc_understanding?.latest);
    const takeHomeStates = cohortRows.flatMap((row) => dccTakeHomeStatesForRow(row));
    const avgUnderstanding = understandingRows.length
      ? Math.round(understandingRows.reduce((sum, row) => sum + Number(row.dcc_understanding.latest.percent || 0), 0) / understandingRows.length)
      : 0;
    const avgTakeHome = takeHomeStates.length
      ? Math.round(takeHomeStates.reduce((sum, state) => sum + Number(state.grade_summary?.percent || 0), 0) / takeHomeStates.length)
      : 0;
    const openQuestions = cohortRows.reduce(
      (sum, row) => sum + (row.concept_clinic_records || []).filter((record) => String(record.question || '').trim()).length,
      0
    );
    const takeawayCount = cohortRows.reduce(
      (sum, row) => sum + (row.concept_clinic_records || []).filter((record) => String(record.takeaway || '').trim()).length,
      0
    );
    return {
      totalRecords,
      learnersWithClinic,
      openQuestions,
      takeawayCount,
      understandingReviews: understandingRows.length,
      gradedTakeHomes: takeHomeStates.length,
      avgUnderstanding,
      avgTakeHome,
    };
  }, [cohortRows]);
  const hackathonRoles = weeks.flatMap((week) =>
    (week.roles || []).map((role) => ({ ...role, week_number: week.week_number, week_title: week.problem_title || week.title || `Week ${week.week_number}` }))
  );
  const hackathonStatusCounts = hackathonRoles.reduce((acc, role) => {
    const status = role.status || 'open';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const strengths = gradeRows
    .flatMap(([, grade]) => grade.strengths || [])
    .reduce((acc, lens) => ({ ...acc, [lens]: (acc[lens] || 0) + 1 }), {});
  const topStrengths = Object.entries(strengths)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([lens]) => lens);
  const tabs = [
    ['overview', 'Overview'],
    ['daily_tasks', 'Daily Tasks'],
    ['concept_clinic', 'Daily Concept Clinic'],
    ['hackathon', 'Hackathon'],
    ['show_tell', 'Show & Tell'],
    ['mock_interview', 'Mock Interview'],
  ];
  const learnerDuplicateTabActive = progressTab === 'overview' || progressTab === 'concept_clinic';
  const selectedLearnerTabLabel = tabs.find(([key]) => key === progressTab)?.[1] || 'Progress';

  const renderAdminTab = () => {
    if (loading) {
      return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading cohort analytics...</Typography>
          </Stack>
        </Paper>
      );
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!cohortRows.length) return <Alert severity="info">No learner progress records found for this cohort yet.</Alert>;

    if (progressTab === 'daily_tasks') {
      const withRate = learnerMetrics.filter((m) => m.completionRate != null);
      const quadrants = learnerMetrics.map((m) => ({ ...m, quadrant: deliveryQuadrant(m, reference) }));
      const notStarted = quadrants.filter((m) => m.quadrant.key === 'not_started');
      const coasting = quadrants.filter((m) => m.quadrant.key === 'coasting');
      const stalled = quadrants.filter((m) => m.quadrant.key === 'stalled');
      const topFinisher = [...withRate].sort((a, b) => b.completionRate - a.completionRate)[0];
      const lowest = [...withRate].sort((a, b) => a.completionRate - b.completionRate)[0];

      const rateRows = learnerMetrics.map((m) => ({
        name: m.name,
        value: m.completionRate,
        label: m.completionRate == null
          ? 'not started'
          : `${m.completionRate}%  (${m.lessonsCompleted}/${m.lessonsStarted})`,
        detail: m.completionRate == null
          ? 'No lessons opened yet'
          : `${m.lessonsCompleted} of ${m.lessonsStarted} opened lessons finished`,
      }));

      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Cohort completion rate"
              value={`${reference.medianCompletion}%`}
              caption="Median share of opened lessons a learner finishes. Counts rates, not totals, so a learner who opens fewer lessons is not penalised."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Finishing what they start"
              value={withRate.filter((m) => m.completionRate >= 80).length}
              denominator={`of ${learnerMetrics.length}`}
              status={withRate.filter((m) => m.completionRate >= 80).length >= Math.ceil(learnerMetrics.length / 2) ? 'good' : 'warning'}
              statusLabel="at 80% or better"
              caption="Learners converting opened lessons into completed ones."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Confident but not finishing"
              value={coasting.length}
              status={coasting.length ? 'serious' : 'good'}
              statusLabel={coasting.length ? 'Check the work quality' : 'None'}
              caption="Above-median confidence, below-median completion. Self-rating is running ahead of delivery."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Not started"
              value={notStarted.length}
              status={notStarted.length ? 'critical' : 'good'}
              statusLabel={notStarted.length ? 'No lessons opened' : 'Everyone has started'}
              caption="Shown separately from a 0% completion rate — never opening a lesson is a different problem from stalling in one."
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <ChartFrame
              question="Who is converting opened lessons into finished ones?"
              insight={
                topFinisher && lowest && topFinisher.id !== lowest.id
                  ? `${topFinisher.name} finishes ${topFinisher.completionRate}% of what they open; ${lowest.name} finishes ${lowest.completionRate}%`
                  : 'Completion rate by learner, against the cohort median'
              }
              footnote="Bar length is completion rate, not the raw number of lessons — a learner who opened 15 and finished 14 outranks one who opened 36 and finished 30. Blue is at or above the cohort median, red below."
              tableHead={['Learner', 'Completed', 'Opened', 'Rate']}
              tableRows={learnerMetrics.map((m) => [
                m.name,
                m.lessonsCompleted,
                m.lessonsStarted,
                m.completionRate == null ? 'not started' : `${m.completionRate}%`,
              ])}
              emptyMessage={learnerMetrics.length ? '' : 'No learner records in this cohort yet.'}
            >
              <GapBarChart
                rows={rateRows}
                referenceValue={reference.medianCompletion}
                referenceLabel={`median ${reference.medianCompletion}%`}
              />
            </ChartFrame>
          </Grid>

          <Grid item xs={12} md={5}>
            <ChartFrame
              question="Is self-rated confidence matching delivery?"
              insight={
                coasting.length
                  ? `${coasting.map((m) => m.name).join(', ')} rate themselves above the cohort but finish below it`
                  : 'Confidence and completion are moving together across the cohort'
              }
              footnote="Split at the cohort medians. Dot size is saved evidence. The top-left corner is the one to act on: high confidence, low delivery."
              tableHead={['Learner', 'Rate', 'Confidence', 'Read']}
              tableRows={quadrants.map((m) => [
                m.name,
                m.completionRate == null ? '—' : `${m.completionRate}%`,
                `${m.confidence}%`,
                m.quadrant.label,
              ])}
              emptyMessage={withRate.length ? '' : 'No learner has opened a lesson yet, so there is no completion rate to plot.'}
            >
              <DeliveryQuadrant points={quadrants} reference={reference} />
            </ChartFrame>
          </Grid>

          <Grid item xs={12}>
            <ReadThis
              status={stalled.length || notStarted.length ? 'critical' : coasting.length ? 'serious' : 'good'}
              headline={
                stalled.length || notStarted.length
                  ? `${stalled.length + notStarted.length} learner${stalled.length + notStarted.length === 1 ? '' : 's'} are not converting effort into finished work`
                  : 'The cohort is finishing what it starts'
              }
              points={[
                `Cohort median completion rate is ${reference.medianCompletion}%, median confidence ${reference.medianConfidence}%.`,
                notStarted.length
                  ? `${notStarted.map((m) => m.name).join(', ')} ${notStarted.length === 1 ? 'has' : 'have'} not opened a lesson. That is an access or onboarding problem, not a learning one — check they can get in before sending more content.`
                  : 'Every learner has opened at least one lesson.',
                coasting.length
                  ? `${coasting.map((m) => m.name).join(', ')} rate their confidence above the cohort median while finishing below it. Ask them to walk through a completed piece of work rather than assigning more.`
                  : 'No learner is rating themselves well above what they are delivering.',
                stalled.length
                  ? `${stalled.map((m) => m.name).join(', ')} are below median on both. Start with what is blocking them, not with new tasks.`
                  : 'Nobody is below median on both confidence and completion.',
              ]}
            />
          </Grid>
        </Grid>
      );
    }

    if (progressTab === 'concept_clinic') {
      const takeHomeConfig = DCC_TAKE_HOME_CONFIGS[selectedDccTakeHome] || DCC_TAKE_HOME_CONFIGS.duplicates;
      const calibrated = learnerMetrics.filter((m) => m.calibrationGap != null);
      const overconfident = calibrated.filter((m) => m.calibration.key === 'overconfident');
      const underconfident = calibrated.filter((m) => m.calibration.key === 'underconfident');
      const widestGap = [...overconfident].sort((a, b) => b.calibrationGap - a.calibrationGap)[0];
      const reviewed = learnerMetrics.filter((m) => m.understanding != null);
      const loggedOnly = learnerMetrics.filter((m) => m.understanding == null && m.clinicRecords > 0);
      const noClinic = learnerMetrics.filter((m) => m.understanding == null && m.clinicRecords === 0);
      const weakest = weakestLenses(lensMatrix.cohort, 3);
      const gradedLearners = lensMatrix.learners.filter((learner) => learner.tables > 0);

      const coverageSegments = [
        { key: 'reviewed', label: 'Understanding reviewed', value: reviewed.length },
        { key: 'loggedOnly', label: 'Logged, not reviewed', value: loggedOnly.length },
        { key: 'none', label: 'No clinic evidence', value: noClinic.length },
      ];

      return (
        <Grid container spacing={2}>
          {/* One filter row, above everything it scopes. The take-home choice
              drives the heatmap, the lens ranking and the line-by-line results
              below, so it cannot live inside one of the cards it controls. */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ borderRadius: 2, px: 1, py: 0.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ pl: 1, pt: { xs: 1, md: 0 }, fontWeight: 800, color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  Take-home topic
                </Typography>
                <Tabs
                  value={selectedDccTakeHome}
                  onChange={(_, value) => setSelectedDccTakeHome(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 40,
                    flexGrow: 1,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 40 },
                  }}
                >
                  {DCC_TAKE_HOME_TABS.map((tab) => (
                    <Tab key={`dcc-takehome-tab-${tab.key}`} value={tab.key} label={tab.label} />
                  ))}
                </Tabs>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Evidence coverage"
              value={reviewed.length}
              denominator={`of ${learnerMetrics.length} learners`}
              status={reviewed.length === learnerMetrics.length ? 'good' : reviewed.length >= learnerMetrics.length / 2 ? 'warning' : 'critical'}
              statusLabel={`${learnerMetrics.length - reviewed.length} without a reviewed score`}
              caption="A cohort-level average means little until most of the cohort is actually in it."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Avg stated understanding"
              value={`${clinicMetrics.avgUnderstanding}%`}
              caption={`Self-reported in the DCC form. Averaged over the ${reviewed.length} learner${reviewed.length === 1 ? '' : 's'} with a review, not the whole cohort.`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Avg graded take-home"
              value={`${clinicMetrics.avgTakeHome}%`}
              comparison={
                clinicMetrics.avgUnderstanding && clinicMetrics.avgTakeHome
                  ? {
                    direction: clinicMetrics.avgTakeHome >= clinicMetrics.avgUnderstanding ? 'up' : 'down',
                    label: `${Math.abs(clinicMetrics.avgUnderstanding - clinicMetrics.avgTakeHome)}pts ${clinicMetrics.avgTakeHome >= clinicMetrics.avgUnderstanding ? 'above' : 'below'} stated understanding`,
                  }
                  : null
              }
              caption="What the marked work actually shows."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Overconfident"
              value={overconfident.length}
              status={overconfident.length ? 'critical' : 'good'}
              statusLabel={overconfident.length ? 'Will not ask for help' : 'None flagged'}
              caption="Rate their understanding more than 15 points above what their graded work demonstrates."
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <ChartFrame
              question="Does stated understanding match graded performance?"
              insight={
                widestGap
                  ? `${widestGap.name} rates understanding at ${widestGap.understanding}% but scores ${widestGap.takeHome}% on the marked take-home`
                  : calibrated.length
                    ? 'Stated understanding and graded performance are tracking together'
                    : 'Not enough paired scores to compare understanding against performance'
              }
              footnote="Each learner needs both a DCC review and a graded take-home to appear. The diagonal is perfect agreement; the shaded corridor is ±15 points, close enough to treat as noise. Below the line means the learner believes they understand more than the marking shows."
              tableHead={['Learner', 'Understanding', 'Take-home', 'Gap', 'Read']}
              tableRows={calibrated.map((m) => [
                m.name,
                `${m.understanding}%`,
                `${m.takeHome}%`,
                `${m.calibrationGap > 0 ? '+' : ''}${m.calibrationGap}`,
                m.calibration.label,
              ])}
              emptyMessage={
                calibrated.length
                  ? ''
                  : `No learner yet has both a scored DCC review and a graded take-home. ${learnerMetrics.length - reviewed.length} of ${learnerMetrics.length} have no reviewed understanding score.`
              }
            >
              <CalibrationPlot points={calibrated} />
            </ChartFrame>
          </Grid>

          <Grid item xs={12} md={5}>
            <ReadThis
              status={overconfident.length ? 'critical' : underconfident.length ? 'warning' : calibrated.length ? 'good' : 'unknown'}
              headline={
                overconfident.length
                  ? `${overconfident.length} learner${overconfident.length === 1 ? '' : 's'} rate themselves well above their marked work`
                  : calibrated.length
                    ? 'Self-assessment is broadly honest across the cohort'
                    : 'Not enough paired evidence to judge calibration'
              }
              points={[
                overconfident.length
                  ? `${overconfident.map((m) => `${m.name} (+${m.calibrationGap}pts)`).join(', ')}. Overconfidence is the harder failure to catch: these learners will not raise a hand, so the gap only shows up under assessment. Ask them to talk through the questions they got wrong.`
                  : 'No learner is rating themselves more than 15 points above their graded work.',
                underconfident.length
                  ? `${underconfident.map((m) => `${m.name} (${m.calibrationGap}pts)`).join(', ')} score better than they think. They are ready for harder work and may be holding back — say so explicitly.`
                  : 'No learner is significantly underrating themselves.',
                learnerMetrics.length - reviewed.length > 0
                  ? `${learnerMetrics.length - reviewed.length} learner${learnerMetrics.length - reviewed.length === 1 ? '' : 's'} have no reviewed understanding score, so they are absent from this chart rather than sitting at zero. Chase the form before reading the cohort average as representative.`
                  : 'Every learner has a reviewed understanding score.',
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <ChartFrame
              question={`Which parts of the ${takeHomeConfig.label.toLowerCase()} rubric is the cohort failing?`}
              insight={
                weakest.length
                  ? `Weakest across the cohort: ${weakest.map((lens) => `${lens.label} (${Math.round(lens.ratio * 100)}%)`).join(', ')}`
                  : `No graded ${takeHomeConfig.label.toLowerCase()} tables yet`
              }
              footnote="Each cell is the share of that learner's graded tables where the lens was marked correct, with partial credit counted as a half. Darker is stronger. This is the same rubric data as the chips below, rolled up so a pattern is visible without reading every table."
              tableHead={['Learner', ...lensMatrix.lenses.map((lens) => lens.label)]}
              tableRows={gradedLearners.map((learner) => [
                learner.name,
                ...learner.cells.map((cell) => (cell.ratio == null ? '—' : `${Math.round(cell.ratio * 100)}%`)),
              ])}
              emptyMessage={
                gradedLearners.length
                  ? ''
                  : `Nobody has submitted a graded ${takeHomeConfig.label.toLowerCase()} take-home yet. Pick another take-home from the tabs below, or set this one as the current clinic task.`
              }
            >
              <LensHeatmap matrix={{ ...lensMatrix, learners: gradedLearners }} />
            </ChartFrame>
          </Grid>

          <Grid item xs={12} md={7}>
            <ChartFrame
              question="What should the next clinic reteach?"
              insight={
                weakest.length
                  ? `${weakest[0].label} is the cohort's weakest lens at ${Math.round(weakest[0].ratio * 100)}%`
                  : 'No graded rubric data for this take-home yet'
              }
              footnote="Sorted weakest first. The three worst are highlighted — those are the lenses worth building the next session around; the rest are holding."
              tableHead={['Rubric lens', 'Cohort correct', 'Learners graded']}
              tableRows={[...lensMatrix.cohort]
                .filter((lens) => lens.ratio != null)
                .sort((a, b) => a.ratio - b.ratio)
                .map((lens) => [lens.label, `${Math.round(lens.ratio * 100)}%`, lens.covered])}
              emptyMessage={gradedLearners.length ? '' : 'No graded tables to roll up yet.'}
            >
              <LensRankBar lenses={lensMatrix.cohort} />
            </ChartFrame>
          </Grid>

          <Grid item xs={12} md={5}>
            <ChartFrame
              question="How much of the cohort do I actually have evidence for?"
              insight={
                noClinic.length
                  ? `${noClinic.length} of ${learnerMetrics.length} learners have logged nothing`
                  : loggedOnly.length
                    ? `${loggedOnly.length} learner${loggedOnly.length === 1 ? '' : 's'} logged a form that has not been reviewed`
                    : 'Every learner has a reviewed clinic record'
              }
              footnote={`${clinicMetrics.totalRecords} clinic records in total — but records concentrate in a few learners, so the count on its own hides who is missing.`}
              tableHead={['State', 'Learners', 'Who']}
              tableRows={[
                ['Understanding reviewed', reviewed.length, reviewed.map((m) => m.name).join(', ') || '—'],
                ['Logged, not reviewed', loggedOnly.length, loggedOnly.map((m) => m.name).join(', ') || '—'],
                ['No clinic evidence', noClinic.length, noClinic.map((m) => m.name).join(', ') || '—'],
              ]}
              emptyMessage={learnerMetrics.length ? '' : 'No learners in this cohort yet.'}
            >
              <CoverageBar segments={coverageSegments} total={learnerMetrics.length} />
              <CoverageLegend segments={coverageSegments} />
            </ChartFrame>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                {takeHomeConfig.label} — line-by-line results
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The individual answers behind the heatmap above. Pick a learner, then tap a lens chip to compare their answer against the expected one.
              </Typography>
              <Tabs
                value={selectedDccLearnerId || cohortRows[0]?.user?.id || ''}
                onChange={(_, value) => setSelectedDccLearnerId(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 38 },
                }}
              >
                {cohortRows.map((row) => (
                  <Tab key={`dcc-learner-tab-${row.user.id}`} value={row.user.id} label={learnerName(row)} />
                ))}
              </Tabs>
              <Stack spacing={2}>
                {cohortRows
                  .filter((row) => row.user?.id === (selectedDccLearnerId || cohortRows[0]?.user?.id))
                  .map((row) => {
                  const practiceState = row[takeHomeConfig.practiceKey] || {};
                  const gradesByTable = practiceState.grades || {};
                  const responsesByTable = practiceState.responses || {};
                  const tableEntries = Object.entries(gradesByTable).filter(([, grade]) => grade);
                  const summaryPercent = practiceState.grade_summary?.percent ?? 0;
                  return (
                    <Box
                      key={`dcc-takehome-detail-${selectedDccTakeHome}-${row.user.id}`}
                      sx={{
                        p: 1.5,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={900}>{learnerName(row)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {practiceState.completed_tables || 0}/{takeHomeConfig.completedTarget} {takeHomeConfig.label.toLowerCase()} tables completed
                            {practiceState.updated_at ? `, updated ${new Date(practiceState.updated_at).toLocaleString('en-GB')}` : ''}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${summaryPercent}% ${takeHomeConfig.label.toLowerCase()}`}
                          color={summaryPercent >= 75 ? 'success' : summaryPercent >= 50 ? 'warning' : 'default'}
                          sx={{ fontWeight: 900, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                        />
                      </Stack>
                      {!tableEntries.length ? (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                          No graded {takeHomeConfig.label.toLowerCase()} take-home submitted yet.
                        </Alert>
                      ) : (
                        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                          {tableEntries.map(([tableId, grade]) => (
                            <Box key={`${row.user.id}-${tableId}`} sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1 }}>
                                <Typography variant="body2" fontWeight={900}>Table {tableId}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {grade.score}/{grade.max} ({grade.percent}%)
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {takeHomeConfig.lenses.map(([lensKey, lensLabel]) => {
                                  const status = duplicateLensStatus(grade, lensKey, lensLabel);
                                  const color = status.status === 'correct' ? 'success' : status.status === 'partial' ? 'warning' : status.status === 'missed' ? 'error' : 'default';
                                  return (
                                    <Chip
                                      key={`${tableId}-${lensKey}`}
                                      size="small"
                                      variant={status.status === 'correct' ? 'filled' : 'outlined'}
                                      color={color}
                                      label={`${status.status === 'correct' ? 'OK' : status.status === 'partial' ? 'Partial' : status.status === 'missed' ? 'Missed' : 'Ungraded'}: ${lensLabel}`}
                                      onClick={() => {
                                        const userAnswer = compactFeedbackText(responsesByTable?.[tableId]?.[lensKey]);
                                        const expectedAnswer = compactFeedbackText(status.expected || takeHomeConfig.answerGuide[tableId]?.[lensKey], 'No expected answer saved');
                                        setDccLineDetail({
                                          learner: learnerName(row),
                                          takeHome: takeHomeConfig.label,
                                          tableId,
                                          lensLabel,
                                          status: status.status,
                                          userAnswer,
                                          expectedAnswer,
                                        });
                                      }}
                                      clickable
                                      sx={{ fontWeight: 800 }}
                                    />
                                  );
                                })}
                              </Stack>
                              {grade.feedback && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                  {grade.feedback}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    if (progressTab === 'hackathon') {
      const stages = STATUS_OPTIONS.map((status) => ({
        key: status,
        label: STATUS_LABEL[status],
        value: hackathonStatusCounts[status] || 0,
      }));
      const totalRoles = hackathonRoles.length;
      const merged = hackathonStatusCounts.merged || 0;
      const stuck = (hackathonStatusCounts.open || 0) + (hackathonStatusCounts.claimed || 0);
      // Where the most roles are piled up short of merged — the bottleneck is
      // the stage to unblock, and it is not always the largest bar overall.
      const bottleneck = stages
        .filter((stage) => stage.key !== 'merged')
        .sort((a, b) => b.value - a.value)[0];

      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Roles in flight"
              value={totalRoles}
              caption="Assigned hackathon work items across every week in view."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Reached merged"
              value={merged}
              denominator={totalRoles ? `of ${totalRoles}` : ''}
              status={totalRoles && merged / totalRoles >= 0.5 ? 'good' : merged ? 'warning' : 'critical'}
              statusLabel={totalRoles ? `${Math.round((merged / totalRoles) * 100)}% completed` : 'No roles yet'}
              caption="The only stage that represents finished, reviewed work."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Not yet started"
              value={stuck}
              status={stuck ? 'warning' : 'good'}
              statusLabel={stuck ? 'Open or claimed only' : 'All roles in progress'}
              caption="Roles with no PR evidence attached yet."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Bottleneck stage"
              value={bottleneck?.value || 0}
              status={bottleneck?.value ? 'serious' : 'good'}
              statusLabel={bottleneck?.value ? bottleneck.label : 'No pile-up'}
              caption="Largest group of roles held short of merged."
            />
          </Grid>
          <Grid item xs={12}>
            <ChartFrame
              question="Where in the workflow are roles getting stuck?"
              insight={
                bottleneck?.value
                  ? `${bottleneck.value} role${bottleneck.value === 1 ? '' : 's'} sitting at "${bottleneck.label}"`
                  : 'No hackathon roles assigned yet'
              }
              footnote="Stages stay in workflow order rather than sorting by size — the sequence is the meaning here. Shading deepens along the pipeline, so height and position both read left-to-right toward merged."
              tableHead={['Stage', 'Roles', 'Share']}
              tableRows={stages.map((stage) => [
                stage.label,
                stage.value,
                totalRoles ? `${Math.round((stage.value / totalRoles) * 100)}%` : '—',
              ])}
              emptyMessage={totalRoles ? '' : 'No hackathon roles have been assigned for the weeks in view yet.'}
            >
              <PipelineBar stages={stages} />
            </ChartFrame>
          </Grid>
        </Grid>
      );
    }

    if (progressTab === 'show_tell' || progressTab === 'mock_interview') {
      const isMockInterview = progressTab === 'mock_interview';
      const title = progressTab === 'show_tell' ? 'Show & Tell' : 'Mock Interview';
      const understandingField = progressTab === 'show_tell' ? 'show_tell_understanding' : 'mock_interview_understanding';
      const mockScoreFor = (row) => Number(row.mock_interview_assignments?.latest_panel_score);
      const hasMockPanelScore = (row) => Number.isFinite(mockScoreFor(row));
      const latestScoreFor = (row) => {
        const latest = row[understandingField]?.latest;
        if (isMockInterview && hasMockPanelScore(row)) {
          return {
            percent: mockScoreFor(row),
            topic: row.mock_interview_assignments?.latest_title || 'Mock interview panel score',
            lenses: row.mock_interview_assignments?.latest_panel_lenses || {},
          };
        }
        return latest || null;
      };
      const reviewedRows = cohortRows.filter((row) => (isMockInterview ? hasMockPanelScore(row) : row[understandingField]?.latest));
      const completedCycleRows = isMockInterview
        ? cohortRows.filter((row) => Number(row.mock_interview_assignments?.completed_count || 0) > 0)
        : reviewedRows;
      const needsFollowUp = reviewedRows.filter((row) => Number(latestScoreFor(row)?.percent || 0) < 60).length;
      const notReviewed = cohortRows.filter((row) => (isMockInterview ? !hasMockPanelScore(row) : !row[understandingField]?.latest));
      const notCompleted = isMockInterview
        ? cohortRows.filter((row) => Number(row.mock_interview_assignments?.completed_count || 0) === 0)
        : notReviewed;
      const scoreRows = cohortRows.map((row) => {
        const latest = latestScoreFor(row);
        const mockAssignment = row.mock_interview_assignments || {};
        const score = latest ? Math.round(Number(latest.percent || 0)) : null;
        return {
          name: learnerName(row),
          value: score,
          label: score == null ? 'not reviewed' : `${score}%`,
          detail: latest
            ? latest.topic || 'Untitled topic'
            : isMockInterview && mockAssignment.latest_title
              ? `${mockAssignment.latest_status || 'scheduled'} - ${mockAssignment.latest_title}`
              : 'No graded note yet',
        };
      });
      const medianScore = median(reviewedRows.map((row) => Math.round(Number(latestScoreFor(row)?.percent || 0))));
      const weakest = [...reviewedRows]
        .sort((a, b) => Number(latestScoreFor(a)?.percent || 0) - Number(latestScoreFor(b)?.percent || 0))[0];
      const mockLensLabel = (key) => String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
      const rubricAxes = isMockInterview
        ? Object.keys(latestScoreFor(weakest)?.lenses || {}).map((key) => {
          const cohortScores = reviewedRows
            .map((row) => Number(latestScoreFor(row)?.lenses?.[key]?.score))
            .filter((score) => Number.isFinite(score));
          const learnerScore = Number(latestScoreFor(weakest)?.lenses?.[key]?.score);
          return {
            key,
            label: mockLensLabel(key),
            learner: Number.isFinite(learnerScore) ? Math.round(learnerScore * 100) : 0,
            cohort: cohortScores.length
              ? Math.round((cohortScores.reduce((sum, score) => sum + score, 0) / cohortScores.length) * 100)
              : 0,
          };
        })
        : buildRubricAxes(
          cohortRows,
          understandingField,
          UNDERSTANDING_REVIEW_LENSES.show_and_tell || [],
          weakest
        );
      const weakestAxis = [...rubricAxes].sort((a, b) => a.learner - b.learner)[0];

      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label={isMockInterview ? 'Completed cycles' : 'Reviewed'}
              value={completedCycleRows.length}
              denominator={`of ${cohortMetrics.total}`}
              status={completedCycleRows.length === cohortMetrics.total ? 'good' : completedCycleRows.length ? 'warning' : 'critical'}
              statusLabel={isMockInterview ? `${notCompleted.length} without a completed cycle` : `${notReviewed.length} still unscored`}
              caption={isMockInterview
                ? 'Completed or no-show assigned mock interview participation. Panel scores feed the score view.'
                : `${title} notes with a graded rubric score.`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Median score"
              value={reviewedRows.length ? `${medianScore}%` : '—'}
              caption="Median rather than mean, so one very low score does not drag the cohort read."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Needs follow-up"
              value={needsFollowUp}
              status={needsFollowUp ? 'serious' : 'good'}
              statusLabel={needsFollowUp ? 'Scored under 60%' : 'None under 60%'}
              caption="Graded reflections weak enough to rerun before the next session."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Lowest scored"
              value={weakest ? `${Math.round(Number(latestScoreFor(weakest)?.percent || 0))}%` : '—'}
              caption={weakest ? `${learnerName(weakest)} — ${latestScoreFor(weakest)?.topic || 'untitled topic'}` : 'Nothing graded yet.'}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <ChartFrame
              question={`Who needs another run at their ${title.toLowerCase()}?`}
              insight={
                weakest
                  ? `${learnerName(weakest)} scored lowest at ${Math.round(Number(latestScoreFor(weakest)?.percent || 0))}%; cohort median is ${medianScore}%`
                  : `No ${title.toLowerCase()} notes graded yet`
              }
              footnote={isMockInterview
                ? 'Learners with no panel score are drawn grey and labelled, not as a zero — an unscored interview and a weak one need different responses.'
                : 'Learners with no graded note are drawn grey and labelled, not as a zero — an ungraded reflection and a bad one need different responses.'}
              tableHead={['Learner', 'Score', 'Topic']}
              tableRows={cohortRows.map((row) => {
                const latest = row[understandingField]?.latest;
                const mockAssignment = row.mock_interview_assignments || {};
                return [
                  learnerName(row),
                  latest ? `${Math.round(Number(latest.percent || 0))}%` : 'not reviewed',
                  latest?.topic || (isMockInterview && mockAssignment.latest_title
                    ? `${mockAssignment.latest_status || 'scheduled'} - ${mockAssignment.latest_title}`
                    : '—'),
                ];
              })}
              emptyMessage={
                reviewedRows.length
                  ? ''
                  : isMockInterview
                    ? `No ${title.toLowerCase()} panel scores saved yet. Completed cycles still count as participation; a score appears once an interviewer saves their scorecard.`
                    : `No ${title.toLowerCase()} notes graded yet. A score appears automatically once a learner saves a ${title} journal entry.`
              }
            >
              <GapBarChart
                rows={scoreRows}
                referenceValue={medianScore}
                referenceLabel={`median ${medianScore}%`}
              />
            </ChartFrame>
          </Grid>
          <Grid item xs={12} md={5}>
            <ChartFrame
              question={weakest ? `Which part of ${learnerName(weakest)}'s explanation is weak?` : 'Which part of the rubric is weak?'}
              insight={
                weakestAxis
                  ? `${weakestAxis.label} is ${learnerName(weakest)}'s lowest lens at ${weakestAxis.learner}%`
                  : `No per-lens ${title.toLowerCase()} breakdown yet`
              }
              footnote={`The lowest-scoring learner's rubric profile against the cohort average. A single ${title.toLowerCase()} percentage says someone needs help; the shape says which part of the explanation to rehearse.`}
              tableHead={['Rubric lens', learnerName(weakest) || 'Learner', 'Cohort avg']}
              tableRows={rubricAxes.map((axis) => [axis.label, `${axis.learner}%`, `${axis.cohort}%`])}
              emptyMessage={
                rubricAxes.length >= 3
                  ? ''
                  : isMockInterview
                    ? `No per-lens breakdown saved yet. It appears once an interviewer saves rubric scores.`
                    : `No per-lens breakdown saved yet. It appears once a ${title} journal entry has been graded against the rubric.`
              }
            >
              <RubricRadar axes={rubricAxes} learnerName={learnerName(weakest)} />
            </ChartFrame>
          </Grid>
          <Grid item xs={12}>
            <ReadThis
              status={needsFollowUp ? 'serious' : notReviewed.length ? 'warning' : reviewedRows.length ? 'good' : 'unknown'}
              headline={
                needsFollowUp
                  ? `${needsFollowUp} ${title.toLowerCase()} reflection${needsFollowUp === 1 ? '' : 's'} scored below 60%`
                  : reviewedRows.length
                    ? `${title} reflections are holding above the follow-up line`
                    : `No ${title.toLowerCase()} evidence to read yet`
              }
              points={[
                notReviewed.length
                  ? isMockInterview
                    ? `${notReviewed.map((row) => learnerName(row)).join(', ')} ${notReviewed.length === 1 ? 'has' : 'have'} no saved panel score. Coverage first — a median over ${reviewedRows.length} of ${cohortMetrics.total} learners is not a cohort read.`
                    : `${notReviewed.map((row) => learnerName(row)).join(', ')} ${notReviewed.length === 1 ? 'has' : 'have'} no graded reflection note. Coverage first — a median over ${reviewedRows.length} of ${cohortMetrics.total} learners is not a cohort read.`
                  : isMockInterview ? 'Every learner has a panel score, so the median is representative.' : 'Every learner has a graded note, so the median is representative.',
                weakest
                  ? `Start with ${learnerName(weakest)} on "${latestScoreFor(weakest)?.topic || 'their last topic'}".`
                  : `Schedule the first ${title.toLowerCase()} slot to start generating evidence.`,
                isMockInterview
                  ? `Completed assigned cycles count as participation automatically. Interviewer scorecards now feed the cohort score view; journal entries remain useful as learner evidence.`
                  : `Scoring runs against the ${title.toLowerCase()} rubric automatically when the learner saves the journal entry — a missing score usually means the entry was never saved, not that it failed.`,
              ]}
            />
          </Grid>
        </Grid>
      );
    }

    // Overview
    const atRisk = learnerMetrics.filter((m) => m.status === 'at_risk');
    const drop = biggestFunnelDrop(funnelStages);
    const noEvidence = learnerMetrics.filter((m) => m.evidence === 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LearnerReadout rows={cohortRows} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Need intervention"
            value={atRisk.length}
            denominator={`of ${cohortMetrics.total}`}
            status={atRisk.length ? 'critical' : 'good'}
            statusLabel={atRisk.length ? atRisk.map((m) => m.name).join(', ') : 'Nobody flagged'}
            caption="Flagged by the signals engine on activity, evidence, and confidence rules."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Strong engagement"
            value={cohortMetrics.strong}
            denominator={`of ${cohortMetrics.total}`}
            status={cohortMetrics.strong >= cohortMetrics.total / 2 ? 'good' : 'warning'}
            statusLabel={`${cohortMetrics.total ? Math.round((cohortMetrics.strong / cohortMetrics.total) * 100) : 0}% of the cohort`}
            caption="Consistent activity paired with saved evidence."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Median completion"
            value={`${reference.medianCompletion}%`}
            caption="Share of opened lessons finished, at the middle of the cohort."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="No saved evidence"
            value={noEvidence.length}
            status={noEvidence.length ? 'serious' : 'good'}
            statusLabel={noEvidence.length ? 'Nothing to review' : 'Everyone has evidence'}
            caption="No journal entries and no clinic records — invisible at portfolio review."
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <ChartFrame
            question="Where is the cohort dropping off?"
            insight={
              drop
                ? `${drop.lost} learner${drop.lost === 1 ? '' : 's'} lost between "${drop.from.label}" and "${drop.to.label}"`
                : 'Every learner has reached the final stage'
            }
            footnote="Each stage is a subset of the one above it, so the funnel can only narrow. Hover a stage to see exactly who is in it."
            tableHead={['Stage', 'Learners', 'Lost from previous']}
            tableRows={funnelStages.map((stage, index) => [
              stage.label,
              stage.value,
              index === 0 ? '—' : funnelStages[index - 1].value - stage.value,
            ])}
            emptyMessage={cohortMetrics.total ? '' : 'No learners in this cohort yet.'}
          >
            <ProgressFunnel stages={funnelStages} />
          </ChartFrame>
        </Grid>

        <Grid item xs={12} md={7}>
          <ActionQueue
            items={actionQueue}
            emptyMessage="No learner is at risk, awaiting setup, or rating themselves above their graded work."
          />
        </Grid>

        <Grid item xs={12}>
          <ReadThis
            status={atRisk.length ? 'critical' : noEvidence.length ? 'warning' : 'good'}
            headline={
              drop
                ? `The cohort loses most people between "${drop.from.label}" and "${drop.to.label}"`
                : 'The cohort is moving through every stage'
            }
            points={[
              drop
                ? `${drop.lost} of ${drop.from.value} learners who reached "${drop.from.label}" have not reached "${drop.to.label}". That transition is where an intervention buys the most.`
                : 'No stage is losing learners.',
              noEvidence.length
                ? `${noEvidence.map((m) => m.name).join(', ')} ${noEvidence.length === 1 ? 'has' : 'have'} saved no journal or clinic evidence. Whatever they have learned is currently unreviewable — fix the habit before adding content.`
                : 'Every learner has saved at least one piece of evidence.',
              `Cohort medians: ${reference.medianCompletion}% completion, ${reference.medianConfidence}% confidence, ${reference.medianEvidence} saved evidence items. The per-activity tabs break each of these down by learner.`,
            ]}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <Box>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              {isAdmin ? 'Cohort Progress Analytics' : 'Learner Progress'}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
              {isAdmin
                ? 'Admin view for the selected cohort: participation, task momentum, hackathon flow, and evidence signals by activity.'
                : "This view is scoped to the signed-in learner. It reads that learner's saved practice records and does not mix answers across cohort members."}
            </Typography>
            {isAdmin && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Cohort filter: {cohortKey || 'all cohorts visible to this admin'}.
              </Typography>
            )}
          </Box>
          {summary && (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Chip
                label={`${summary.percent}%`}
                color={summary.percent >= 75 ? 'success' : summary.percent >= 50 ? 'warning' : 'default'}
                sx={{ fontWeight: 900, fontSize: '0.95rem', height: 36 }}
              />
              <Typography variant="caption" color="text.secondary">
                {summary.score}/{summary.max}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={progressTab}
          onChange={(_, value) => setProgressTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 800 },
          }}
        >
          {tabs.map(([key, label]) => <Tab key={key} value={key} label={label} />)}
        </Tabs>
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {isAdmin && renderAdminTab()}
        </Box>
      </Paper>

      {isAdmin && null}

      {!isAdmin && loading && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading your saved progress...</Typography>
          </Stack>
        </Paper>
      )}

      {!isAdmin && !loading && error && <Alert severity="error">{error}</Alert>}

      {!isAdmin && !loading && !error && !learnerDuplicateTabActive && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>{selectedLearnerTabLabel}</Typography>
          <Typography variant="body2" color="text.secondary">
            This learner tab is separated from the DCC take-home practice now. Its own scored activity feed will appear here once that event has a saved rubric record.
          </Typography>
        </Paper>
      )}

      {!isAdmin && !loading && !error && learnerDuplicateTabActive && !learnerTakeHomeStates.length && (
        <Alert severity="info">
          No DCC take-home grading record yet. Open a Daily Clinic take-home, answer the savable practice tables, then click Grade answers.
        </Alert>
      )}

      {!isAdmin && !loading && !error && learnerDuplicateTabActive && learnerTakeHomeStates.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <Tabs
              value={selectedDccTakeHome}
              onChange={(_, value) => setSelectedDccTakeHome(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 36 },
              }}
            >
              {DCC_TAKE_HOME_TABS.map((tab) => (
                <Tab
                  key={`learner-dcc-takehome-${tab.key}`}
                  value={tab.key}
                  label={tab.label}
                  disabled={!state?.[tab.key]}
                />
              ))}
            </Tabs>
          </Paper>
          {!selectedLearnerState && (
            <Alert severity="info">
              No saved {selectedLearnerTakeHomeConfig.label.toLowerCase()} grading record yet.
            </Alert>
          )}
          {selectedLearnerState && (
            <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="overline" color="text.secondary">Practice completion</Typography>
                <Typography variant="h4" fontWeight={900}>
                  {selectedLearnerState.completed_tables || 0}/{selectedLearnerTakeHomeConfig.completedTarget}
                </Typography>
                <Typography variant="body2" color="text.secondary">{selectedLearnerTakeHomeConfig.label} diagnosis tables completed.</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="overline" color="text.secondary">Current score</Typography>
                <Typography variant="h4" fontWeight={900}>
                  {summary ? `${summary.percent}%` : 'Not graded'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary ? `${summary.score}/${summary.max} rubric checks passed.` : 'Click Grade answers in the practice bank.'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="overline" color="text.secondary">Last updated</Typography>
                <Typography variant="h6" fontWeight={800}>
                  {selectedLearnerState.updated_at ? new Date(selectedLearnerState.updated_at).toLocaleString('en-GB') : 'Not saved'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Saved against this learner's progress record.</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={800} gutterBottom>Strengths</Typography>
                {topStrengths.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {topStrengths.map((lens) => <Chip key={lens} label={lens} color="success" variant="outlined" />)}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">Grade answers to identify strength areas.</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={800} gutterBottom>Development Areas</Typography>
                {summary?.weaknesses?.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {summary.weaknesses.map((lens) => <Chip key={lens} label={lens} color="warning" variant="outlined" />)}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No major weak lens detected yet.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {gradeRows.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800} gutterBottom>{selectedLearnerTakeHomeConfig.label} Practice Table Scores</Typography>
              <Stack spacing={1}>
                {gradeRows.map(([tableId, grade]) => (
                  <Box key={tableId}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={800}>Table {tableId}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {grade.score}/{grade.max} ({grade.percent}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, grade.percent || 0))}
                      color={grade.percent >= 75 ? 'success' : grade.percent >= 50 ? 'warning' : 'inherit'}
                      sx={{ height: 7, borderRadius: 999 }}
                    />
                    {grade.feedback && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {grade.feedback}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
            </>
          )}
        </>
      )}
      <Dialog
        open={Boolean(dccLineDetail)}
        onClose={() => setDccLineDetail(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dccLineDetail ? `${dccLineDetail.learner} - ${dccLineDetail.takeHome || 'DCC take-home'} Table ${dccLineDetail.tableId}` : 'DCC take-home detail'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={900}>
                {dccLineDetail?.lensLabel}
              </Typography>
              {dccLineDetail?.status && (
                <Chip
                  size="small"
                  label={dccLineDetail.status}
                  color={dccLineDetail.status === 'correct' ? 'success' : dccLineDetail.status === 'partial' ? 'warning' : dccLineDetail.status === 'missed' ? 'error' : 'default'}
                  sx={{ fontWeight: 800 }}
                />
              )}
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={900}>User answer</Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {dccLineDetail?.userAnswer || 'No answer captured'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={900}>Expected answer</Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {dccLineDetail?.expectedAnswer || 'No expected answer saved'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDccLineDetail(null)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

const CohortPilotPage = () => {
  const { user } = useAuth();
  const [cohortKey, setCohortKey] = useState('');
  const [userRole, setUserRole] = useState('learner');
  const [weeks, setWeeks] = useState([]);
  const [allCohorts, setAllCohorts] = useState([]);
  const [myCohorts, setMyCohorts] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [cohortSwitching, setCohortSwitching] = useState(false);
  const [activeActivityTab, setActiveActivityTab] = useState('daily_clinic');
  const [activeClinicTab, setActiveClinicTab] = useState('dq_math');
  // The mock interview capture form needs the real posted cycles — their job
  // title, advertised topics, and (for whoever is allowed to see them) the
  // actual questions. Fetched only on that tab, and re-fetched on entry so a
  // cycle an admin just assigned shows up without a page reload.
  const [mockInterviewSessions, setMockInterviewSessions] = useState([]);

  // Slide visibility — keyed by slide id, loaded from backend
  const [slideVisibility, setSlideVisibility] = useState({});

  // Slide modal
  const [selectedSlideId, setSelectedSlideId] = useState(null);

  // Week modal
  const [selectedWeekNum, setSelectedWeekNum] = useState(null);

  // Week 1 draft team size (configurable — 4 or 5 learners)
  const [teamSize, setTeamSize] = useState(5);

  // Add week dialog
  const [addWeekOpen, setAddWeekOpen] = useState(false);
  const [newWeekNumber, setNewWeekNumber] = useState(1);
  const [addWeekTitle, setAddWeekTitle] = useState('');
  const [addWeekSaving, setAddWeekSaving] = useState(false);
  const [addWeekError, setAddWeekError] = useState('');

  const isAdmin = userRole === 'admin' || userRole === 'platform_owner';

  // Derive the track for this cohort from the learner's selected cohort membership first,
  // then fall back to week metadata for admin-created weeks.
  const selectedCohortMembership = myCohorts.find((membership) => membership.cohort_key === cohortKey) || null;
  const cohortTrack = weeks.find((w) => w.track_slug)?.track_slug || null;
  const journalTrackerId = resolveCohortJournalTracker(selectedCohortMembership?.track_slug, cohortTrack);
  const journalTrackerLabel = trackLabelFor(journalTrackerId);
  const latestCohortWeek = weeks.length
    ? [...weeks].sort((a, b) => Number(b.week_number || 0) - Number(a.week_number || 0))[0]
    : null;
  const visibleHackathonWeeks = isAdmin ? weeks : weeks.filter((week) => week.status !== 'locked');
  // "Week heading" should default to the week actually being worked on right
  // now, not just whichever week has the highest number — an admin can
  // pre-create future weeks, and the highest-numbered one may not have
  // started yet. Prefer in_progress, then the earliest still-open week, then
  // the most recently closed one, only falling back to "highest number" if
  // none of those exist.
  const activeCohortWeek =
    weeks.find((week) => week.status === 'in_progress') ||
    [...weeks].filter((week) => week.status === 'open').sort((a, b) => Number(a.week_number || 0) - Number(b.week_number || 0))[0] ||
    [...weeks].filter((week) => week.status === 'closed').sort((a, b) => Number(b.week_number || 0) - Number(a.week_number || 0))[0] ||
    latestCohortWeek;
  // Plain "Week N" only — activeCohortWeek.problem_title is the hackathon's
  // scenario name (e.g. "The Friday 4pm Emergency"), which made every
  // activity type's default heading look hackathon-themed even when logging
  // a Daily Concept Clinic or Show & Tell entry that had nothing to do with
  // it. The specific topic is already captured by each form's own topic
  // field; this heading only needs to group entries by week.
  const defaultJournalWeekLabel = activeCohortWeek
    ? `Week ${activeCohortWeek.week_number}`
    : 'Week 1';

  // The dropdown behind every journal form's "Cohort week" field. The stored
  // VALUE stays a plain "Week N" so journal_render's week parsing and the
  // week-XX.md filenames stay stable; the problem title is shown only as a
  // label, to help a learner recognise which week they mean.
  const journalWeekOptions = useMemo(() => {
    const numbered = weeks
      .map((week) => Number(week.week_number))
      .filter((number) => Number.isFinite(number) && number > 0);
    const highest = Math.max(8, ...numbered, Number(activeCohortWeek?.week_number) || 0);
    const titleFor = new Map(
      weeks.map((week) => [Number(week.week_number), week.problem_title || week.title || ''])
    );
    return Array.from({ length: highest }, (_, index) => index + 1).map((number) => {
      const title = titleFor.get(number);
      const isCurrent = Number(activeCohortWeek?.week_number) === number;
      return {
        value: `Week ${number}`,
        label: [`Week ${number}`, title ? `— ${title}` : '', isCurrent ? '(current)' : '']
          .filter(Boolean).join(' '),
      };
    });
  }, [weeks, activeCohortWeek]);

  useEffect(() => {
    recordAuditEvent('page_view', { path: '/cohort' }).catch(() => { });
    fetchSlideSettings().then(setSlideVisibility).catch(() => { });
  }, []);

  useEffect(() => {
    if (!cohortKey || activeActivityTab !== 'mock_interview') return undefined;
    let cancelled = false;
    fetchMockInterviews(cohortKey)
      .then((rows) => { if (!cancelled) setMockInterviewSessions(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setMockInterviewSessions([]); });
    return () => { cancelled = true; };
  }, [cohortKey, activeActivityTab]);

  const loadMyCohort = useCallback(async () => {
    setPageLoading(true);
    try {
      const data = await fetchMyCohort();
      const myRole = data.role || 'learner';
      let myKey = data.cohort_key || '';
      setUserRole(myRole);
      setWeeks(data.weeks || []);
      setMyCohorts(data.memberships || (myKey ? [{ cohort_key: myKey }] : []));

      if (myRole === 'admin' || myRole === 'platform_owner') {
        const keys = await fetchAllCohortKeys().catch(() => []);
        setAllCohorts(keys);
        // Platform owners may not be assigned to a cohort — auto-select first
        if (!myKey && keys.length > 0) {
          myKey = keys[0];
          const w = await fetchCohortWeeks(myKey).catch(() => []);
          setWeeks(w);
        }
      }

      setCohortKey(myKey);
    } catch {
      setCohortKey('');
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => { loadMyCohort(); }, [loadMyCohort]);

  // Helpers to read the extended {visible, scope} shape (backwards-compat with plain booleans)
  const getSlideEntry = useCallback((id) => {
    const v = slideVisibility[id];
    if (!v || typeof v === 'boolean') return { visible: Boolean(v), scope: 'all', unlockedResources: {} };
    return {
      visible: Boolean(v.visible),
      scope: v.scope || 'all',
      unlockedResources: v.unlockedResources || {},
    };
  }, [slideVisibility]);

  const patchSlide = useCallback(async (id, patch) => {
    const current = getSlideEntry(id);
    const next = { ...current, ...patch };
    setSlideVisibility((prev) => ({ ...prev, [id]: next }));
    try {
      await patchSlideSettings({ [id]: next });
    } catch {
      setSlideVisibility((prev) => ({ ...prev, [id]: current }));
    }
  }, [getSlideEntry]);

  const handleSlideToggle = useCallback((slideId, visible) => {
    patchSlide(slideId, { visible });
  }, [patchSlide]);

  const handleSlideScope = useCallback((slideId, scope) => {
    patchSlide(slideId, { scope });
  }, [patchSlide]);

  const handleSlideResourceUnlock = useCallback((slideId, href, unlocked) => {
    const current = getSlideEntry(slideId);
    const unlockedResources = { ...(current.unlockedResources || {}) };
    if (unlocked) {
      unlockedResources[href] = true;
    } else {
      delete unlockedResources[href];
    }
    patchSlide(slideId, { unlockedResources });
  }, [getSlideEntry, patchSlide]);

  const handleCohortChange = useCallback(async (newKey) => {
    if (!newKey || newKey === cohortKey) return;
    setCohortSwitching(true);
    setSelectedWeekNum(null);
    try {
      const w = await fetchCohortWeeks(newKey);
      setCohortKey(newKey);
      setWeeks(w);
    } catch {
      setWeeks([]);
      setCohortKey(newKey);
    } finally {
      setCohortSwitching(false);
    }
  }, [cohortKey]);

  const nextWeekNumber = weeks.length
    ? Math.max(...weeks.map((w) => w.week_number)) + 1
    : 1;

  const handleWeekSaved = useCallback((savedWeek) => {
    setWeeks((prev) => {
      const exists = prev.some((w) => w.week_number === savedWeek.week_number);
      if (exists) {
        return prev.map((w) =>
          w.week_number === savedWeek.week_number ? { ...w, ...savedWeek } : w,
        );
      }
      return [...prev, savedWeek].sort((a, b) => a.week_number - b.week_number);
    });
  }, []);

  const handleAddWeek = async () => {
    setAddWeekSaving(true);
    setAddWeekError('');
    try {
      const saved = await saveCohortWeek(cohortKey, newWeekNumber, {
        problem_title: addWeekTitle || `Week ${newWeekNumber} Hackathon`,
        problem_brief: '',
        scenario_context: '',
        shared_repo_url: '',
        roles: [],
        availability_slots: DEFAULT_AVAILABILITY_SLOTS,
        reflection: '',
        status: 'locked',
        scheduled_at: null,
      });
      handleWeekSaved(weekToSummary(saved));
      setAddWeekOpen(false);
      setAddWeekTitle('');
      setSelectedWeekNum(newWeekNumber);
    } catch (err) {
      setAddWeekError(err.message || 'Could not create week.');
    } finally {
      setAddWeekSaving(false);
    }
  };

  const handleAddScheduleWeek = async () => {
    const weekNumber = nextWeekNumber;
    const saved = await saveCohortWeek(cohortKey, weekNumber, {
      problem_title: `Week ${weekNumber} Schedule`,
      problem_brief: '',
      scenario_context: '',
      shared_repo_url: '',
      roles: [],
      availability_slots: DEFAULT_AVAILABILITY_SLOTS,
      schedule_polls: {
        hackathon: { slots: DEFAULT_HACKATHON_SLOTS, responses: {}, selected_slot: '' },
        show_tell: { slots: DEFAULT_SHOW_TELL_SLOTS, responses: {}, selected_slot: '' },
        mock_interviews: { slots: DEFAULT_MOCK_INTERVIEW_SLOTS, responses: {}, selected_slot: '' },
      },
      reflection: '',
      status: 'locked',
      scheduled_at: null,
    });
    const summary = weekToSummary(saved);
    handleWeekSaved(summary);
    return saved;
  };

  const handleGenerateNewWeek = async () => {
    setAddWeekSaving(true);
    setAddWeekError('');
    try {
      const saved = await generateCohortWeek(
        cohortKey,
        newWeekNumber,
        cohortTrack || 'data-analyst-job-ready',
        newWeekNumber,
        teamSize,
      );
      handleWeekSaved(weekToSummary(saved));
      setAddWeekOpen(false);
      setAddWeekTitle('');
      setSelectedWeekNum(newWeekNumber);
    } catch (err) {
      setAddWeekError(err.message || 'Could not generate week.');
    } finally {
      setAddWeekSaving(false);
    }
  };

  const handleCreateWeekOneDraft = async () => {
    setAddWeekSaving(true);
    setAddWeekError('');
    try {
      const saved = await saveCohortWeek(cohortKey, 1, buildWeekOneHackathonDraft(teamSize));
      handleWeekSaved(weekToSummary(saved));
      setSelectedWeekNum(1);
    } catch (err) {
      setAddWeekError(err.message || 'Could not create Week 1 draft.');
    } finally {
      setAddWeekSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cohortKey) {
    return (
      <Box sx={{ bgcolor: 'background.default' }}>
        <Box sx={{ bgcolor: '#102033', color: '#fff', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <Typography variant="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}>
              Cohort workspace
            </Typography>
            <Typography sx={{ color: alpha('#fff', 0.78) }}>
              Weekly hackathons, SOLVE-grounded roles, and shared cohort progress.
            </Typography>
          </Container>
        </Box>
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" fontWeight={800} gutterBottom>
            You're not in a cohort yet
          </Typography>
          <Typography color="text.secondary">
            Ask your cohort admin to assign you to a cohort. Once assigned, this workspace will
            show your team's weekly hackathons.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* Hero */}
      <Box sx={{ bgcolor: '#102033', color: '#fff', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.75rem' }, mb: 1 }}>
                Cohort workspace
              </Typography>
              <Typography sx={{ color: alpha('#fff', 0.78), mb: 1.5 }}>
                Weekly hackathons, SOLVE-grounded roles, and shared cohort progress.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} alignItems="center">
                {/* Cohort selector: admins see all cohorts; learners see their memberships. */}
                {(isAdmin ? allCohorts.length > 0 : myCohorts.length > 1) ? (
                  <TextField
                    select
                    value={cohortKey}
                    onChange={(e) => handleCohortChange(e.target.value)}
                    size="small"
                    disabled={cohortSwitching}
                    sx={{
                      minWidth: 210,
                      '& .MuiInputBase-root': { color: '#fff', fontSize: '0.8rem' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', 0.35) },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', 0.65) },
                      '& .MuiSvgIcon-root': { color: `${alpha('#fff', 0.8)} !important` },
                      '& .MuiInputBase-root.Mui-disabled': { color: alpha('#fff', 0.5) },
                    }}
                  >
                    {(isAdmin ? allCohorts : myCohorts.map((membership) => membership.cohort_key)).map((key) => (
                      <MenuItem key={key} value={key}>{displayCohortName(key)}</MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Chip
                    label={displayCohortName(cohortKey)}
                    sx={{ bgcolor: alpha('#fff', 0.12), color: '#fff', fontWeight: 600 }}
                    size="small"
                  />
                )}
                <Chip
                  icon={<BoltIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                  label="Weekly Hackathons"
                  sx={{ bgcolor: alpha('#fff', 0.08), color: '#fff' }}
                  size="small"
                />
                <Chip
                  icon={<AccountTreeIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                  label={<GlossaryTerm term="solve">SOLVE Framework</GlossaryTerm>}
                  sx={{ bgcolor: alpha('#fff', 0.08), color: '#fff' }}
                  size="small"
                />
              </Stack>
            </Box>
            {isAdmin && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => {
                    setNewWeekNumber(nextWeekNumber);
                    setAddWeekTitle('');
                    setAddWeekOpen(true);
                  }}
                  sx={{ color: '#fff', borderColor: alpha('#fff', 0.4), '&:hover': { borderColor: '#fff' } }}
                >
                  Add week
                </Button>
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Tabs
            value={activeActivityTab}
            onChange={(_, value) => setActiveActivityTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Cohort activity workspace tabs"
            sx={{
              minHeight: 56,
              '& .MuiTab-root': {
                minHeight: 56,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.9rem',
                px: { xs: 1.5, sm: 2.25 },
              },
            }}
          >
            {COHORT_ACTIVITY_TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* Daily clinic — notes, slides, resources, and flashcards */}
      {activeActivityTab === 'daily_clinic' && (() => {
        const TRACK_GROUPS = [
          { key: 'dq_math',                 label: 'Data Quality Math', predicate: (s) => s.id?.startsWith('dq-math-') },
          { key: 'cross_track',             label: 'Cross-Track', predicate: (s) => s.track === null && !s.id?.startsWith('dq-math-') },
          { key: 'sql',                     label: 'SQL', predicate: (s) => s.track === 'sql-job-ready' },
          { key: 'python',                  label: 'Python', predicate: (s) => s.track === 'python-job-ready' },
          { key: 'full_stack',              label: 'Full-Stack / APIs', predicate: (s) => s.track === 'full-stack-developer-job-ready' },
          { key: 'azure',                   label: 'Azure Data Engineer', predicate: (s) => s.track === 'azure-data-engineer' },
          { key: 'data_analyst',            label: 'Data Analyst', predicate: (s) => s.track === 'data-analyst-job-ready' },
        ];

        const activeSlides = SESSION_SLIDES.filter((s) => s.active).map((s) => {
          const entry = getSlideEntry(s.id);
          const scopeMatch = entry.scope === 'all' || entry.scope === cohortTrack || !cohortTrack;
          return {
            ...s,
            visible: entry.visible,
            scope: entry.scope,
            unlockedResources: entry.unlockedResources,
            _scopeMatch: scopeMatch,
          };
        });
        const visibleToLearner = activeSlides.filter((s) => s.visible && s._scopeMatch);
        const displaySlides = isAdmin ? activeSlides : visibleToLearner;
        const selectedSlide = activeSlides.find((s) => s.id === selectedSlideId) ?? null;
        const visibleGroups = TRACK_GROUPS.map((group) => ({
          ...group,
          slides: displaySlides.filter(group.predicate),
        })).filter((group) => group.slides.length > 0);
        const selectedClinicTab = activeClinicTab === 'all' || activeClinicTab === 'flashcards'
          || visibleGroups.some((group) => group.key === activeClinicTab)
          ? activeClinicTab
          : (visibleGroups[0]?.key || 'flashcards');
        const displayGroups = selectedClinicTab === 'all'
          ? visibleGroups
          : visibleGroups.filter((group) => group.key === selectedClinicTab);
        const liveClinicTopicOptions = clinicTopicOptionsFromSlides(displayGroups.flatMap((group) => group.slides));

        return (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', py: { xs: 3, md: 4 } }}>
            <Container maxWidth="lg">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                  Daily Clinic Library
                </Typography>
                {isAdmin && selectedClinicTab !== 'flashcards' && (
                  <Typography variant="caption" color="text.secondary">
                    Click a card to view &amp; configure — toggle to make live
                  </Typography>
                )}
              </Stack>

              <Tabs
                value={selectedClinicTab}
                onChange={(_, value) => setActiveClinicTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Daily clinic library categories"
                sx={{
                  mb: 3,
                  minHeight: 38,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minHeight: 38,
                    px: 1.5,
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 800,
                  },
                }}
              >
                {CLINIC_LIBRARY_TABS.map((tab) => (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={tab.label}
                    disabled={tab.key !== 'all' && tab.key !== 'flashcards' && !visibleGroups.some((group) => group.key === tab.key)}
                  />
                ))}
              </Tabs>

              {selectedClinicTab === 'flashcards' ? (
                <SqlWindowFlashcards />
              ) : (
                <Grid container spacing={2.5} alignItems="flex-start">
                  <Grid item xs={12} md={8}>
                    {displayGroups.length === 0 ? (
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No clinic resources in this category yet.
                        </Typography>
                      </Paper>
                    ) : displayGroups.map(({ key, label, slides }) => {
                      return (
                        <Box key={String(key)} sx={{ mb: 3 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1.25 }}
                          >
                            {label}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                              gap: 1.5,
                            }}
                          >
                            {slides.map((slide) => (
                              <SlideCard
                                key={slide.id}
                                slide={slide}
                                isAdmin={isAdmin}
                                onOpen={() => setSelectedSlideId(slide.id)}
                                onToggleVisibility={isAdmin ? handleSlideToggle : undefined}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CohortJournalCapture
                      type="daily_clinic"
                      cohortKey={cohortKey}
                      trackerId={journalTrackerId}
                      trackerLabel={journalTrackerLabel}
                      defaultWeekLabel={defaultJournalWeekLabel}
                      weekOptions={journalWeekOptions}
                      topicOptions={liveClinicTopicOptions}
                    />
                  </Grid>
                </Grid>
              )}
            </Container>

            {/* Slide detail modal */}
            <Dialog
              open={Boolean(selectedSlide)}
              onClose={() => setSelectedSlideId(null)}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
            >
              {selectedSlide && (
                <SlideViewer
                  slide={selectedSlide}
                  isAdmin={isAdmin}
                  onToggleVisibility={isAdmin ? handleSlideToggle : undefined}
                  onScopeChange={isAdmin ? handleSlideScope : undefined}
                  onResourceUnlockChange={isAdmin ? handleSlideResourceUnlock : undefined}
                  onClose={() => setSelectedSlideId(null)}
                />
              )}
            </Dialog>
          </Box>
        );
      })()}

      {activeActivityTab === 'schedule' && (
        cohortSwitching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ScheduleAvailabilityView
            cohortKey={cohortKey}
            weeks={weeks}
            isAdmin={isAdmin}
            currentUser={user}
            nextWeekNumber={nextWeekNumber}
            onWeekSaved={handleWeekSaved}
            onAddWeek={handleAddScheduleWeek}
          />
        )
      )}

      {/* Week grid or empty states */}
      {activeActivityTab === 'hackathon' && (cohortSwitching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : visibleHackathonWeeks.length === 0 ? (
        isAdmin ? (
          <>
            <Container maxWidth="md" sx={{ pt: 6, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} gutterBottom>No active week created yet</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                The designed Week 1 ticket is shown below for admins only. Pick a team size, create
                the week, configure the event details, then unlock it when learners should see it.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                <TextField
                  select
                  label="Team size"
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e.target.value))}
                  size="small"
                  sx={{ width: 160 }}
                >
                  {TEAM_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n} members</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Button
                variant="contained" size="large" startIcon={<AddIcon />}
                onClick={handleCreateWeekOneDraft}
                disabled={addWeekSaving}
              >
                {addWeekSaving ? 'Creating...' : 'Create Week 1 draft'}
              </Button>
              {addWeekError && <Alert severity="error" sx={{ mt: 2 }}>{addWeekError}</Alert>}
            </Container>
            <HackathonCTA teamSize={teamSize} />
          </>
        ) : (
          <NoActiveHackathon />
        )
      ) : (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              {/* Section header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    {visibleHackathonWeeks.length} {visibleHackathonWeeks.length === 1 ? 'week' : 'weeks'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click a week to open, assign roles, and track PRs.
                  </Typography>
                </Box>
                {isAdmin && (
                  <Button
                    variant="outlined" size="small" startIcon={<AddIcon />}
                    onClick={() => {
                      setNewWeekNumber(nextWeekNumber);
                      setAddWeekTitle('');
                      setAddWeekOpen(true);
                    }}
                  >
                    Add week {nextWeekNumber}
                  </Button>
                )}
              </Stack>

              {/* Cards */}
              <Grid container spacing={2.5}>
                {visibleHackathonWeeks.map((week) => (
                  <Grid item xs={12} sm={6} key={week.week_number}>
                    <WeekCard
                      week={week}
                      onClick={() => setSelectedWeekNum(week.week_number)}
                    />
                  </Grid>
                ))}

                {/* Add week tile (admin) */}
                {isAdmin && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      onClick={() => {
                        setNewWeekNumber(nextWeekNumber);
                        setAddWeekTitle('');
                        setAddWeekOpen(true);
                      }}
                      sx={{
                        p: 2.5, border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                        cursor: 'pointer', height: '100%', minHeight: 140,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: 1, color: 'text.disabled',
                        transition: 'border-color 0.15s, color 0.15s',
                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700}>
                        Add Week {nextWeekNumber}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <CohortJournalCapture
                type="hackathon"
                cohortKey={cohortKey}
                trackerId={journalTrackerId}
                trackerLabel={journalTrackerLabel}
                defaultWeekLabel={defaultJournalWeekLabel}
                weekOptions={journalWeekOptions}
                topicOptions={captureTopicOptionsFor('hackathon', { weeks })}
              />
            </Grid>
          </Grid>
        </Container>
      ))}

      {activeActivityTab === 'journal_evidence' && (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Journal & Evidence
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
                  Use this area for reflection habits, evidence notes, repo proof, screenshots, and learning summaries.
                  Daily lessons and concept clinics should produce short notes here so feedback can cite actual learner work.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <CohortJournalCapture
                type="journal_evidence"
                cohortKey={cohortKey}
                trackerId={journalTrackerId}
                trackerLabel={journalTrackerLabel}
                defaultWeekLabel={defaultJournalWeekLabel}
                weekOptions={journalWeekOptions}
                topicOptions={captureTopicOptionsFor('journal_evidence', { weeks })}
              />
            </Grid>
          </Grid>
        </Container>
      )}

      {activeActivityTab === 'show_and_tell' && (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, mb: 2.5 }}>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Show & Tell
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
                  A 30-45 minute live presentation to the whole cohort on a simple, insightful topic. Prep briefs
                  are auto-graded for readiness before the session (advisory, never blocks it); live delivery is
                  scored by the admin in the room.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <ShowTellBoard
                cohortKey={cohortKey}
                isAdmin={isAdmin}
                currentUserId={user?.id}
                defaultWeekNumber={activeCohortWeek?.week_number || 1}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CohortJournalCapture
                type="show_and_tell"
                cohortKey={cohortKey}
                trackerId={journalTrackerId}
                trackerLabel={journalTrackerLabel}
                defaultWeekLabel={defaultJournalWeekLabel}
                weekOptions={journalWeekOptions}
                topicOptions={captureTopicOptionsFor('show_and_tell', { weeks })}
              />
            </Grid>
          </Grid>
        </Container>
      )}

      {activeActivityTab === 'mock_interview' && (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, mb: 2.5 }}>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Mock Interviews
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
                  Every 2 weeks InsiTech International "posts" a fake job advert covering what the cohort has been
                  taught so far. One learner is assigned as the candidate; others are assigned as the interviewer(s)
                  who see the questions and grading scheme. To keep it a fair test, the candidate never sees those
                  until the interview is marked complete.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <MockInterviewBoard
                cohortKey={cohortKey}
                isAdmin={isAdmin}
                currentUserId={user?.id}
                defaultCycleNumber={activeCohortWeek?.week_number ? Math.max(1, Math.ceil(activeCohortWeek.week_number / 2)) : 1}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CohortJournalCapture
                type="mock_interview"
                cohortKey={cohortKey}
                trackerId={journalTrackerId}
                trackerLabel={journalTrackerLabel}
                defaultWeekLabel={defaultJournalWeekLabel}
                weekOptions={journalWeekOptions}
                topicOptions={captureTopicOptionsFor('mock_interview', { weeks })}
                interviewSessions={mockInterviewSessions}
              />
            </Grid>
          </Grid>
        </Container>
      )}

      {activeActivityTab === 'progress' && (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <LearnerProgressSignals isAdmin={isAdmin} cohortKey={cohortKey} weeks={weeks} />
        </Container>
      )}

      {/* Week modal */}
      <WeekModal
        open={selectedWeekNum !== null}
        cohortKey={cohortKey}
        weekNumber={selectedWeekNum}
        isAdmin={isAdmin}
        onClose={() => setSelectedWeekNum(null)}
        onSaved={handleWeekSaved}
      />

      {/* Add week dialog */}
      <Dialog open={addWeekOpen} onClose={() => setAddWeekOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Week {newWeekNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Generate the next hackathon from the curriculum, or create a blank week
              if you want to write the challenge manually.
            </Typography>
            <TextField
              label="Week number"
              type="number"
              value={newWeekNumber}
              onChange={(e) => setNewWeekNumber(Number(e.target.value))}
              inputProps={{ min: 1 }}
              size="small" fullWidth
            />
            <TextField
              label="Hackathon title (optional)"
              value={addWeekTitle}
              onChange={(e) => setAddWeekTitle(e.target.value)}
              size="small" fullWidth
              placeholder="e.g. The Friday 4pm Emergency"
            />
            {addWeekError && <Alert severity="error">{addWeekError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWeekOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            startIcon={addWeekSaving ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleGenerateNewWeek}
            disabled={addWeekSaving}
          >
            Generate from curriculum
          </Button>
          <Button
            variant="contained"
            startIcon={addWeekSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleAddWeek}
            disabled={addWeekSaving}
          >
            {addWeekSaving ? 'Creating…' : 'Create blank'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CohortPilotPage;
